import { useState, useRef, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Play, Send, Check, X, Plus, Trash2, Edit2, Upload, FileText, ExternalLink, Banknote, RefreshCw, Fingerprint, Download } from 'lucide-react';
import { startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { employeesApi, accountantApi, categoriesApi, settingsApi, companyApi, projectsApi, statementsApi, partnerAliasApi, partnersApi, documentsApi, portalFetchApi, revolutApi, paymentsApi, raiffeisenApi, fxStatsApi, ownTrendsApi, previewUrl, passkeyApi, passkeyErrorMessage, authApi, companyAssetsApi, exportApi, isStepUpRequired, freshRequest, sendFresh } from '../services/api';
import { useStepUp } from '../contexts/StepUpContext';
import {
  getChatQuota,
  deleteChatHistory,
  issueTelegramNonce,
  getTelegramPending,
  confirmTelegramPending,
  disownTelegramPending,
  getTelegramBindings,
  revokeTelegramBinding,
  updateTelegramBinding,
  sendTelegramTestMessage,
  getChatCost,
} from '../services/chat';
// NAV adófolyószámla-panel — külön fájlban, mert ez a modul már 4300+ sor.
import NavUpoPanel from './NavUpoSettings';
import AssetStatusBlock from '../components/AssetStatusBlock';
import BillingoAccountsPanel from '../components/BillingoCredentials';
import AssetCredentialsPanel from '../components/AssetCredentials';
import RaiffeisenCredentials from '../components/RaiffeisenCredentials';
import { Btn, Card, Alert, Amount, Input, Select, Badge, Modal, Checkbox, Spinner, DropButton, ConfirmDialog, QueryState, filesFromDataTransfer, Pill, SearchField, AssetRequiredNotice } from '../components/UI';
import { openAuthedFile, useAuthedObjectUrl } from '../components/AuthedFile';
import { useBulkUpload, BulkResults } from '../components/BulkUpload';
import { useToast } from '../components/Toast';
import { useAuth } from '../contexts/AuthContext';
import { huFmt, fmtIban, fmtMonthHu, fmtDateTimeHu } from '../utils';
import { bankColor, BankDot, currencyColor, CurrencyBadge } from './Statements';
import {
  ShareBarChart, CategoryTrendChart, CategoryYearlyChart, AgingBarChart, AGING_BUCKETS,
  PnlGroupedChart, PnlProfitChart, truncName,
} from '../components/charts/StatsCharts';
import {
  PeriodFilter, periodToRange, STANDARD_PRESETS, MONTHLY_TREND_PRESETS,
  PNL_PRESETS, presetToMonths,
} from '../components/PeriodFilter';
import {
  StackedMonthlyChart, FeeTypeTotalsChart, FxEffChart, FxHistChart,
  FxBreakEvenChart, PlanDeltaChart,
} from '../components/charts/FeeFxCharts';

// UTC ISO ('…Z') → budapesti helyi idő (YYYY-MM-DD HH:mm). DST-biztos.
function fmtSentAt(s) {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return String(s).slice(0, 16);
  // 'sv-SE' → 'YYYY-MM-DD HH:mm' formátum (a felhasználó által preferált)
  return d.toLocaleString('sv-SE', {
    timeZone: 'Europe/Budapest',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

const DOC_KIND_OPTIONS = [
  { value: 'MUNKASZERZODES', label: 'Munkaszerződés' },
  { value: 'MODOSITAS',      label: 'Módosítás / Függelék' },
  { value: 'FELMONDAS',      label: 'Felmondás' },
  { value: 'EGYEB',          label: 'Egyéb' },
];
const DOC_KIND_LABEL = Object.fromEntries(DOC_KIND_OPTIONS.map(o => [o.value, o.label]));

// ── Payroll komponens TÖRÖLVE (2026-07-20, review 7.1): bekötetlen volt (App.js
//    nem importálta, route sincs). A payroll BACKEND (/api/payroll/*) és az e-mail
//    automatizmus érintetlen. Szükség esetén git-history-ból visszaállítható.
// ── Employees ──────────────────────────────────────────────────────────────
// K8/b (2026-09-23): a munkavállalói bankszámla változása a backendben friss passkey
// step-upot kér (`routers.py` `_bank_account_change_gate`, 403 + X-Step-Up-Required).
// A backend a tagolást nem tekinti változásnak — itt ugyanígy normalizálunk
// (`_norm_acct`: csak betű/szám, nagybetűsen), hogy név- vagy e-mail-javításnál ne kérdezzünk.
const normAcct = (v) => String(v ?? '').replace(/[^0-9a-z]/gi, '').toUpperCase();
const EMP_STEP_UP_WHY = 'A munkavállaló bankszámlaszámának módosítása előtt';
// A step-up ablak bezárása nem hiba — ezt a jelölőt a mutáció `onError`-ja csendben elnyeli.
const STEP_UP_CANCELLED = 'step_up_cancelled';

export function Employees() {
  // ⚠ D26/D39/K-5 (2026-09-27, U18): a bruttó bér, a bankszámla és a munkavállalói
  // dokumentumok bér-adat — a backend nem-ADMIN-nak ki sem adja (`pay_fields_hidden`),
  // és az írásukat 403-mal tiltja. A felület a mezőket nem rendereli (tudatos eltérés a
  // „letiltva-de-látható” mintától: ez ADAT, nem kapcsoló); a bér-jelzők letiltva látszanak.
  const { isAdmin } = useAuth();
  const ADMIN_ONLY = 'Csak cég-adminisztrátor módosíthatja.';
  const qc = useQueryClient();
  const toast = useToast();
  const { requireFreshStepUp } = useStepUp();
  const [confirm, setConfirm] = useState(null); // {title, message, danger, confirmLabel, action}
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [ibanResult, setIbanResult] = useState(null);
  const [docKind, setDocKind] = useState('MUNKASZERZODES');
  const [pendingDoc, setPendingDoc] = useState(null);   // {file, kind} - új munkavállalónál mentésig
  const [dragOver, setDragOver] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState(null);
  const fileRef = useRef(null);

  const employeesQ = useQuery({
    queryKey: ['employees'], queryFn: employeesApi.list
  });
  const employees = employeesQ.data ?? [];

  // Meglévő munkavállaló dokumentumai
  const { data: empDocs=[], refetch: refetchDocs } = useQuery({
    queryKey: ['employee-docs', editing?.id],
    queryFn: () => employeesApi.listDocuments(editing.id),
    enabled: !!editing?.id && isAdmin,
  });

  const saveMut = useMutation({
    mutationFn: async (d) => {
      // 1. Employee mentés (create vagy update)
      // K8/b: ha a bankszámla változik, a passkeyt a mentés ELŐTT kérjük (a
      // StepUpContext elve: előzetesen kérdezünk, nem a 403-ra reagálva) …
      // ⚠⚠ 2026-09-24, élő hiba: MINDIG friss, erre a kérésre (ez a munkavállaló + ez a
      // törzs) kötött passkey — soha nem a 30 perces jegy / `stepUpStatus`. A leíró és a
      // küldött kérés ugyanabból a `freshRequest`-ből jön (`services/api.js`).
      const req = editing?.id
        ? freshRequest('PATCH /employees/{eid}', { path: { eid: editing.id }, body: d })
        : freshRequest('POST /employees', { body: d });
      const acctChanged = normAcct(d.bank_account) !== normAcct(editing?.bank_account);
      if (acctChanged && !await requireFreshStepUp(EMP_STEP_UP_WHY, [req])) {
        throw new Error(STEP_UP_CANCELLED);
      }
      let saved;
      try {
        saved = await sendFresh(req);
      } catch (e) {
        // … és ha a jegy közben lejárt (vagy a kliens-oldali összevetés tévedett), a
        // backend 403-ára egyszer új passkeyt kérünk, majd automatikusan újrapróbáljuk. Az
        // új munkavállaló 403-nál NEM jön létre (a kapu az INSERT előtt fut), így az
        // újrapróba nem duplikál.
        if (!isStepUpRequired(e)) throw e;
        if (!await requireFreshStepUp(EMP_STEP_UP_WHY, [req])) throw new Error(STEP_UP_CANCELLED);
        saved = await sendFresh(req);
      }
      // 2. Ha új munkavállaló és van pending dokumentum, töltsük fel most
      if (!editing?.id && pendingDoc) {
        try {
          await employeesApi.uploadDocument(saved.id, pendingDoc.file, pendingDoc.kind);
        } catch (e) {
          // ha az upload elbukik, az employee már mentve van – csak figyelmeztetés
          console.error('Doc upload after employee create failed:', e);
        }
      }
      return saved;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['employee-docs'] });
      setEditing(null); setForm({}); setPendingDoc(null); setAiError(null);
      toast.success('Munkavállaló mentve');
    },
    onError: (err) => {
      if (err?.message === STEP_UP_CANCELLED) {
        toast.info('A mentés megszakítva — a bankszámlaszám módosításához passkey-megerősítés kell.');
        return;
      }
      if (isStepUpRequired(err)) {
        toast.error('Mentés sikertelen: a bankszámlaszám módosításához passkey-megerősítés kell.');
        return;
      }
      const det = err?.response?.data?.detail;
      const msg = det && typeof det === 'object' ? (det.message || det.error) : det;
      toast.error('Mentés sikertelen: ' + (msg || err.message));
    },
  });

  const F = k => form[k] !== undefined ? form[k] : (editing?.[k] ?? '');
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  // ── Dokumentum feltöltés / parse ──
  const handleFile = async (file) => {
    if (!file) return;
    setAiError(null);
    setAiBusy(true);
    try {
      if (editing?.id) {
        // Meglévő employee: azonnal feltöltjük + AI parse + DB
        const r = await employeesApi.uploadDocument(editing.id, file, docKind);
        await refetchDocs();
        if (r.ai_extracted && !r.ai_extracted._error) {
          applyAiData(r.ai_extracted);
        } else if (r.ai_extracted?._error) {
          setAiError(r.ai_extracted._error);
        }
      } else {
        // Új munkavállaló: csak parse, file pending state-be
        const ai = await employeesApi.parseDocument(file, docKind);
        if (ai && !ai._error) {
          applyAiData(ai);
        } else if (ai?._error) {
          setAiError(ai._error);
        }
        setPendingDoc({ file, kind: docKind });
      }
    } catch (e) {
      setAiError(e?.response?.data?.detail || e.message || 'Ismeretlen hiba');
    } finally {
      setAiBusy(false);
    }
  };

  const applyAiData = (ai) => {
    // Csak azokat a mezőket töltsük, amik még üresek a űrlapban, vagy a user explicit OK
    // Itt egyszerűen felülírjuk – a user a következő screen-en módosíthatja
    setForm(prev => ({
      ...prev,
      full_name:        ai.full_name        ?? prev.full_name        ?? editing?.full_name        ?? '',
      address:          ai.address          ?? prev.address          ?? editing?.address          ?? '',
      bank_account:     ai.bank_account     ?? prev.bank_account     ?? editing?.bank_account     ?? '',
      gross_salary:     ai.gross_salary     ?? prev.gross_salary     ?? editing?.gross_salary     ?? '',
      employment_start: ai.employment_start ?? prev.employment_start ?? editing?.employment_start ?? '',
      employment_end:   ai.employment_end === 'HATÁROZATLAN' ? null
                          : (ai.employment_end ?? prev.employment_end ?? editing?.employment_end ?? ''),
    }));
  };

  const deleteDocMut = useMutation({
    mutationFn: ({eid, did}) => employeesApi.deleteDocument(eid, did),
    onSuccess: () => refetchDocs(),
  });

  const tdS = { padding:'0.5rem 0.75rem', fontSize:'0.8rem', color:'var(--text)',
    borderBottom:'1px solid var(--border-dim)', verticalAlign:'middle' };
  const thS = { padding:'0.55rem 0.75rem', fontSize:'0.68rem', color:'var(--text-muted)',
    fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em',
    borderBottom:'1px solid var(--border)' };

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'1.1rem'}}>
        <h2 style={{fontSize:'0.95rem',fontWeight:600}}>Munkavállalók</h2>
        <Btn onClick={()=>{setEditing({});setForm({});setIbanResult(null);}}
          disabled={!isAdmin} title={isAdmin ? undefined : 'Munkavállalót csak cég-adminisztrátor vehet fel.'}>+ Új</Btn>
      </div>

      {editing !== null && (
        <Card title={editing?.id?'Szerkesztés':'Új munkavállaló'} style={{marginBottom:'1rem'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
            <Input label="Teljes név" value={F('full_name')} onChange={e=>set('full_name',e.target.value)}
              disabled={!isAdmin} title={isAdmin ? undefined : ADMIN_ONLY}/>
            <Input label="Monogram" value={F('monogram')} onChange={e=>set('monogram',e.target.value.toUpperCase())}/>
            {/* review P1: a bérjegyzék erre az e-mailre megy — csak ADMIN írja */}
            <Input label="E-mail" value={F('email')} onChange={e=>set('email',e.target.value)}
              disabled={!isAdmin} title={isAdmin ? undefined : ADMIN_ONLY}/>
            <Input label="Lakcím (Revolut utaláshoz)" value={F('address')}
              placeholder="pl. 1100 Budapest, Példa utca 12."
              onChange={e=>set('address',e.target.value)}/>
            {isAdmin ? (<>
            <div>
              <Input label="Bankszámlaszám" value={F('bank_account')} onChange={e=>set('bank_account',e.target.value)}/>
              <Btn size="sm" variant="ghost" style={{marginTop:4}}
                onClick={async()=>{const r=await employeesApi.iban(F('bank_account'));setIbanResult(r);}}>
                IBAN számítás
              </Btn>
              {ibanResult && <p style={{fontSize:'0.7rem',marginTop:4,
                color:ibanResult.error?'var(--danger)':'var(--accent)'}}>
                {ibanResult.error||ibanResult.iban}
              </p>}
            </div>
            <Input label="Bruttó alapbér (Ft/hó)" type="number" value={F('gross_salary')||''}
              onChange={e=>set('gross_salary',e.target.value ? Number(e.target.value) : null)}/>
            </>) : (
              <p style={{gridColumn:'1 / -1',fontSize:'0.78rem',color:'var(--text-muted)'}}>
                Bér- és járulékadatot, a bankszámlaszámot és a munkavállalói dokumentumokat csak cég-adminisztrátor látja.
              </p>
            )}
            <Input label="Bérjegyzék mappa (OneDrive)" value={F('payslip_folder')}
              placeholder="pl. KOLONICS ANNA/BERJEGYZEK" disabled={!isAdmin}
              title={isAdmin ? undefined : ADMIN_ONLY}
              onChange={e=>set('payslip_folder',e.target.value)}/>
            <Input label="Munkaviszony kezdete" type="date" value={F('employment_start')||''}
              onChange={e=>set('employment_start',e.target.value || null)}/>
            <div>
              <Input label="Munkaviszony vége (üres = határozatlan)" type="date"
                value={F('employment_end')||''}
                onChange={e=>set('employment_end',e.target.value || null)}/>
              {(F('employment_end') == null || F('employment_end') === '') && (
                <p style={{fontSize:'0.7rem',marginTop:4,color:'var(--text-muted)'}}>
                  Határozatlan idejű
                </p>
              )}
            </div>
          </div>

          <div style={{display:'flex',gap:20,flexWrap:'wrap',marginBottom:12}}>
            {[['save_payslip','Bérjegyzék mentés'],['send_payslip_email','E-mail küldés'],['transfer_salary','Utalás']].map(([k,l])=>(
              <Checkbox key={k} label={l} checked={!!F(k)} onChange={e=>set(k,e.target.checked)}
                disabled={!isAdmin} title={isAdmin ? undefined : ADMIN_ONLY}/>
            ))}
          </div>

          {/* Drop zone + dokumentum lista — csak ADMIN (K-4: AI-nak küldött munkaszerződés) */}
          {isAdmin && <div style={{marginBottom:12,padding:12,background:'rgba(255,255,255,0.03)',
            borderRadius:'var(--radius)',border:'1px solid var(--border-dim)'}}>
            <div style={{display:'flex',gap:10,alignItems:'flex-end',marginBottom:10}}>
              <Select label="Dokumentum típusa" value={docKind}
                onChange={e=>setDocKind(e.target.value)}
                options={DOC_KIND_OPTIONS}
                style={{minWidth:180}}/>
              <span style={{fontSize:'0.72rem',color:'var(--text-muted)',marginBottom:8}}>
                AI kinyeri a nevét, címét, számlaszámát, fizetését és munkaviszony idejét.
              </span>
            </div>
            <div
              onDragOver={e=>{e.preventDefault();setDragOver(true);}}
              onDragLeave={()=>setDragOver(false)}
              onDrop={e=>{
                e.preventDefault();setDragOver(false);
                const f = filesFromDataTransfer(e.dataTransfer)[0];
                if (f) handleFile(f);
              }}
              onClick={()=>fileRef.current?.click()}
              style={{
                border: `2px dashed ${dragOver?'var(--primary)':'var(--border)'}`,
                borderRadius:'var(--radius)', padding:'1rem',
                background: dragOver?'rgba(59,130,246,0.08)':'transparent',
                cursor:'pointer', textAlign:'center',
                color: dragOver?'var(--primary)':'var(--text-muted)',
                fontSize:'0.82rem', transition:'all 0.15s',
              }}>
              <Upload size={20} style={{marginBottom:4,opacity:0.7}}/>
              <div>{aiBusy ? 'AI feldolgozás…' : 'Húzd ide a PDF-et, vagy kattints a tallózáshoz'}</div>
              <input ref={fileRef} type="file" accept="application/pdf,image/*"
                style={{display:'none'}}
                onChange={e=>{const f=e.target.files?.[0]; if(f) handleFile(f); e.target.value='';}}/>
            </div>
            {aiError && <Alert type="danger" style={{marginTop:8}}>{aiError}</Alert>}
            {pendingDoc && !editing?.id && (
              <p style={{fontSize:'0.72rem',marginTop:6,color:'var(--accent)'}}>
                ⏳ Feltöltésre vár: <strong>{pendingDoc.file.name}</strong> ({DOC_KIND_LABEL[pendingDoc.kind]})
                – mentéskor kerül a OneDrive-ra
              </p>
            )}

            {/* Korábbi dokumentumok listája (csak meglévő munkavállalónál) */}
            {editing?.id && empDocs.length > 0 && (
              <div style={{marginTop:12}}>
                <p style={{fontSize:'0.72rem',color:'var(--text-muted)',marginBottom:6,
                  textTransform:'uppercase',letterSpacing:'0.06em',fontWeight:600}}>
                  Feltöltött dokumentumok ({empDocs.length})
                </p>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.78rem'}}>
                  <tbody>
                    {empDocs.map(d => (
                      <tr key={d.id} style={{borderBottom:'1px solid var(--border-dim)'}}>
                        <td style={{padding:'0.4rem 0.5rem'}}>
                          <FileText size={13} style={{verticalAlign:'middle',marginRight:4,opacity:0.6}}/>
                          {DOC_KIND_LABEL[d.kind] || d.kind}
                        </td>
                        <td style={{padding:'0.4rem 0.5rem',fontSize:'0.72rem',color:'var(--text-muted)'}}>
                          {d.filename}
                        </td>
                        <td style={{padding:'0.4rem 0.5rem',fontFamily:'var(--mono)',fontSize:'0.7rem',color:'var(--text-muted)'}}>
                          {String(d.uploaded_at).slice(0,10)}
                        </td>
                        <td style={{padding:'0.4rem 0.5rem',textAlign:'right'}}>
                          {d.url && (
                            <a href={d.url} target="_blank" rel="noreferrer"
                              style={{color:'var(--primary)',marginRight:8,display:'inline-flex',alignItems:'center',gap:2}}>
                              <ExternalLink size={12}/> Megnyit
                            </a>
                          )}
                          <Btn size="sm" variant="ghost" icon={Trash2}
                            onClick={()=>setConfirm({
                              title:'Dokumentum törlése', danger:true, confirmLabel:'Törlés',
                              message:`Törlöd a(z) "${d.filename}" dokumentumot?`,
                              action:()=>deleteDocMut.mutate({eid: editing.id, did: d.id}),
                            })}/>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>}

          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
            <Btn variant="ghost" onClick={()=>{setEditing(null);setForm({});setPendingDoc(null);setAiError(null);}}>Mégsem</Btn>
            <Btn loading={saveMut.isPending} onClick={()=>saveMut.mutate({...editing,...form})}>Mentés</Btn>
          </div>
        </Card>
      )}

      <Card noPad>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.8rem'}}>
            <thead>
              <tr>
                <th style={thS}>Név</th>
                <th style={thS}>E-mail</th>
                {isAdmin && <th style={thS}>Bankszámlaszám</th>}
                <th style={thS}>Mentés</th>
                <th style={thS}>Küldés</th>
                <th style={thS}>Utalás</th>
                <th style={thS}></th>
              </tr>
            </thead>
            <tbody>
              <QueryState query={employeesQ} colSpan={isAdmin ? 7 : 6} empty={employees.length === 0}
                emptyText="Nincs munkavállaló rögzítve."
                errorText="A munkavállalók betöltése sikertelen.">
              {employees.map(e => (
                <tr key={e.id} style={{cursor:'pointer'}}
                  onClick={()=>{setEditing(e);setForm({});setIbanResult(null);}}>
                  <td style={tdS}><strong>{e.full_name}</strong> <span style={{color:'var(--text-muted)',fontSize:'0.72rem'}}>{e.monogram}</span></td>
                  <td style={tdS}>{e.email||'–'}</td>
                  {isAdmin && <td style={tdS}><span style={{fontFamily:'var(--mono)',fontSize:'0.72rem'}}>{e.bank_account||'–'}</span></td>}
                  <td style={tdS}>{e.save_payslip?<Check size={13} color="var(--accent)"/>:<X size={13} color="var(--danger)"/>}</td>
                  <td style={tdS}>{e.send_payslip_email?<Check size={13} color="var(--accent)"/>:<X size={13} color="var(--danger)"/>}</td>
                  <td style={tdS}>{e.transfer_salary?<Check size={13} color="var(--accent)"/>:<X size={13} color="var(--danger)"/>}</td>
                  <td style={tdS}><Btn size="sm" variant="ghost" onClick={e2=>{e2.stopPropagation();setEditing(e);setForm({});}}>✎</Btn></td>
                </tr>
              ))}
              </QueryState>
            </tbody>
          </table>
        </div>
      </Card>

      <ConfirmDialog open={!!confirm} title={confirm?.title} message={confirm?.message}
        danger={confirm?.danger} confirmLabel={confirm?.confirmLabel}
        onCancel={()=>setConfirm(null)}
        onConfirm={()=>{ const a=confirm.action; setConfirm(null); a(); }}/>
    </div>
  );
}

// ── Stats ──────────────────────────────────────────────────────────────────
export function Stats() {
  // A fül URL-hash-ből is nyitható (mélylink: /stats#fx, /stats#bankfees, …),
  // és váltáskor a hash frissül – a Dashboard/Teendők linkjei így fülre célozhatnak.
  // K-9 (2026-09-27): a 'trends' hiányzott → újratöltéskor a Kategóriák fülre esett vissza.
  const validTabs = ['categories','partners','balance','projects','bankfees','fx','trends'];
  // F0-8: a hash `/` előtti tagja a fül (`#projects/eredmeny` → `projects`), a többi a
  // fülön belüli nézet — különben a projekt-nézet mélylinkje a Kategóriákra esne vissza.
  const initialTab = () => {
    const h = (window.location.hash || '').replace('#','').split('/')[0];
    return validTabs.includes(h) ? h : 'categories';
  };
  const [tab, setTabState] = useState(initialTab);
  // 2026-10-02 (Gábor élesben): a címsorban átírt `#trends` nem váltott fület — a hash-váltás
  // nem tölti újra az oldalt, a fül csak betöltéskor olvasta. Most a `hashchange`-et is követi.
  useEffect(() => {
    const onHash = () => setTabState(initialTab());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const setTab = (t) => {
    setTabState(t);
    window.history.replaceState(null, '', t === 'projects' ? '#projects/eredmeny' : `#${t}`);
  };
  const tabBtnS = active => ({
    // K-11: token, nem beégetett fehér (Raymondban a sárgán 1,27:1 volt)
    background: active ? 'var(--tab-active-bg)' : 'transparent',
    color: active ? 'var(--tab-active-fg)' : 'var(--text-muted)',
    border: '1px solid ' + (active ? 'var(--tab-active-bg)' : 'var(--border)'),
    borderRadius: 'var(--radius)', padding: '0.4rem 0.9rem',
    fontSize: '0.8rem', cursor: 'pointer', fontWeight: active ? 600 : 400,
  });
  return (
    <div>
      <h1 style={{fontSize:'1.2rem',fontWeight:700,marginBottom:'1rem'}}>Statisztikák</h1>
      <div style={{display:'flex',gap:8,marginBottom:'1rem'}}>
        <button style={tabBtnS(tab==='categories')} onClick={()=>setTab('categories')}>Kategóriák</button>
        <button style={tabBtnS(tab==='partners')}   onClick={()=>setTab('partners')}>Partnerek</button>
        <button style={tabBtnS(tab==='balance')}    onClick={()=>setTab('balance')}>Tartozások</button>
        <button style={tabBtnS(tab==='projects')}   onClick={()=>setTab('projects')}>Projektek</button>
        <button style={tabBtnS(tab==='bankfees')}   onClick={()=>setTab('bankfees')}>Bankköltség</button>
        <button style={tabBtnS(tab==='fx')}         onClick={()=>setTab('fx')}>Deviza (FX)</button>
        <button style={tabBtnS(tab==='trends')}     onClick={()=>setTab('trends')}>Saját trendek</button>
      </div>
      {tab === 'categories' && <CategoryStats/>}
      {tab === 'partners'   && <PartnerStats/>}
      {tab === 'balance'    && <PartnerBalance/>}
      {tab === 'projects'   && <ProjectPnl/>}
      {tab === 'bankfees'   && <BankFeesStats/>}
      {tab === 'fx'         && <FxStats/>}
      {tab === 'trends'     && <OwnTrends/>}
    </div>
  );
}

// ── Saját trendek (B4/F1 ár-idősor + B5 fizetési viselkedés, 2026-09-06) ─────
// A bérlő SAJÁT múltja mint benchmark — nem iparági átlag, nem cégek közti adat.
function OwnTrends() {
  const [years, setYears] = useState(6);
  const [days, setDays] = useState(365);
  const psQ = useQuery({ queryKey: ['price-series', years], queryFn: () => ownTrendsApi.priceSeries({ years }) });
  const pbQ = useQuery({ queryKey: ['payment-behaviour', days], queryFn: () => ownTrendsApi.paymentBehaviour({ days }) });
  const ps = psQ.data, pb = pbQ.data;
  const fmt = (v, d = 0) => (v == null ? '–' : huFmt(v, d));
  const chg = (v) => v == null ? '–' : <span style={{color: v > 0 ? 'var(--danger)' : v < 0 ? 'var(--success)' : 'var(--text-muted)', fontWeight:600}}>{v > 0 ? '+' : ''}{v}%</span>;
  const yearsCols = ps ? [...new Set(ps.series.flatMap(s => Object.keys(s.years)))].sort() : [];
  const th = { textAlign:'left', padding:'4px 6px', color:'var(--text-muted)', fontSize:'0.64rem', textTransform:'uppercase', borderBottom:'1px solid var(--border)', whiteSpace:'nowrap' };
  const td = { padding:'4px 6px', fontSize:'0.78rem', borderBottom:'1px solid var(--border-dim)', verticalAlign:'top' };
  return (
    <div style={{display:'flex', flexDirection:'column', gap:'0.75rem'}}>
      <Card title="Saját ár-idősor tételszinten"
        subtitle="Ugyanazért a tételért mennyit fizettél évente (súlyozott átlag-egységár, Σ nettó HUF / Σ mennyiség) — a bejövő számlák tételsoraiból, tétel-megnevezés + mértékegység szerint. A saját múltad, nem iparági átlag: nincs jogi előfeltétel."
        actions={<Select value={years} onChange={e => setYears(Number(e.target.value))}
          options={[3,4,5,6,8,10].map(y => ({ value: y, label: `${y} év` }))}/>}>
        <QueryState query={psQ} empty={!ps?.series?.length}
          emptyText="Nincs legalább 2 évben ismétlődő tétel a tételsorok között (a NAV-tételsorok 2026-08 óta gyűlnek; a korábbi évek csak újraszinkronnal kerülnek be)."
          errorText="Az ár-idősor betöltése sikertelen.">
          {() => (
            <>
              <div style={{fontSize:'0.72rem', color:'var(--text-muted)', marginBottom:8}}>
                {ps.lines_considered.toLocaleString('hu-HU')} tételsor · {ps.series_count} sorozat (≥2 év, ≥3 sor) · a változás az első→utolsó év
              </div>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%', borderCollapse:'collapse'}}>
                  <thead><tr>
                    <th style={th}>Tétel</th><th style={th}>Egys.</th>
                    {yearsCols.map(y => <th key={y} style={{...th, textAlign:'right'}}>{y}</th>)}
                    <th style={{...th, textAlign:'right'}}>Változás</th><th style={{...th, textAlign:'right'}}>Sorok</th>
                  </tr></thead>
                  <tbody>
                    {ps.series.map(sr => (
                      <tr key={sr.key + '|' + (sr.unit || '')}>
                        <td style={{...td, maxWidth:280}} title={(sr.sample_names || []).join(' · ')}>{sr.key}</td>
                        <td style={{...td, color:'var(--text-muted)'}}>{sr.unit || '–'}</td>
                        {yearsCols.map(y => (
                          <td key={y} style={{...td, fontFamily:'var(--mono)', textAlign:'right'}}
                            title={sr.years[y] ? `${sr.years[y].lines} sor · ${fmt(sr.years[y].quantity, 2)} ${sr.unit || ''}` : ''}>
                            {sr.years[y] ? fmt(sr.years[y].unit_price) : <span style={{color:'var(--text-dim)'}}>·</span>}
                          </td>
                        ))}
                        <td style={{...td, textAlign:'right', fontFamily:'var(--mono)'}}>{chg(sr.change_pct)}</td>
                        <td style={{...td, textAlign:'right', fontFamily:'var(--mono)', color:'var(--text-muted)'}}>{sr.lines}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </QueryState>
      </Card>

      <Card title="Fizetési viselkedés (bejövő számlák)"
        subtitle="Határidő = esedékesség − kiállítás; késés = kifizetés − esedékesség (negatív: a határidő ELŐTT fizettél). Az n mellett olvasd: a paid_date lefedettsége határozza meg a minta erejét."
        actions={<Select value={days} onChange={e => setDays(Number(e.target.value))}
          options={[[180,'180 nap'],[365,'1 év'],[730,'2 év'],[1825,'5 év']].map(([v,l]) => ({ value: v, label: l }))}/>}>
        <QueryState query={pbQ} empty={!pb || !pb.n_documents} emptyText="Nincs bejövő számla az időszakban." errorText="A fizetési viselkedés betöltése sikertelen.">
          {() => (
            <>
              <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(170px, 1fr))', gap:8, marginBottom:10}}>
                <FxKpi label="Átl. fizetési határidő" unit="nap" value={fmt(pb.avg_term_days, 1)} sub={`medián ${fmt(pb.median_term_days, 1)} · n=${pb.n_with_term}`}/>
                <FxKpi label="Átl. fizetési késés" unit="nap" value={fmt(pb.avg_delay_days, 1)}
                  sub={pb.n_paid < 30 ? `n=${pb.n_paid} — vékony minta` : `medián ${fmt(pb.median_delay_days, 1)} · n=${pb.n_paid}`}
                  accent={pb.avg_delay_days != null && pb.avg_delay_days > 0 ? 'var(--danger)' : 'var(--success)'} big/>
                <FxKpi label="Késve fizetett" value={pb.late_share == null ? '–' : `${Math.round(pb.late_share * 100)}%`} sub={`${pb.paid_late} késve · ${pb.paid_on_time} időben`}/>
                <FxKpi label="Nyitott, lejárt" value={pb.unpaid_overdue} sub="esedékesség elmúlt, nincs kifizetés"/>
              </div>
              {pb.partners?.length > 0 && (
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%', borderCollapse:'collapse'}}>
                    <thead><tr>{['Partner','Számla','Átl. határidő','Átl. késés','Kifizetett','Késve'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {pb.partners.map(p => (
                        <tr key={p.partner}>
                          <td style={td}>{p.partner || '–'}</td>
                          <td style={{...td, fontFamily:'var(--mono)'}}>{p.n}</td>
                          <td style={{...td, fontFamily:'var(--mono)'}}>{fmt(p.avg_term_days, 1)} nap</td>
                          <td style={{...td, fontFamily:'var(--mono)'}}>{p.avg_delay_days == null ? '–' : `${fmt(p.avg_delay_days, 1)} nap`}</td>
                          <td style={{...td, fontFamily:'var(--mono)'}}>{p.paid_n}</td>
                          <td style={{...td, fontFamily:'var(--mono)'}}>{p.late_share == null ? '–' : `${Math.round(p.late_share * 100)}%`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </QueryState>
      </Card>
    </div>
  );
}

// ── FX költség- és árfolyam-analitika ───────────────────────────────────────

const FX_FEE = '#f59e0b', FX_SPREAD = '#ea580c', FX_CONV = '#0ea5e9', FX_PAY = '#9085e9';
const fxFt  = (v, d = 0) => (v == null ? '–' : `${huFmt(v, d)} Ft`);
const fxPct = (v, d = 2) => (v == null ? '–' : `${Number(v).toFixed(d)}%`);
const fxBps = (v) => (v == null ? '–' : `${v >= 0 ? '+' : ''}${Number(v).toFixed(0)} bps`);
const FX_KIND_LBL = { conversion: 'Átváltás', payment: 'Fizetés' };
// Deviza-pár szín (a vonaldiagram + legend közös forrása): a bázisdeviza
// entitás-stabil színe, ismeretlen devizánál a fix kategorikus paletta.
const fxPairColor = (p, i) => currencyColor((p || '').split('/')[0]) || colorAt(i);
// Csomag-stabil színek (break-even vonalak, delta-chart, legend – egy forrás).
const FX_PLAN_COLOR = (p) => (p.plan === 'scale' ? '#5b8def' : p.plan === 'grow' ? '#22c55e' : '#f59e0b');

// KPI kártya (a Bankköltség total-kártya mintájára)
function FxKpi({ label, unit, value, sub, accent, big }) {
  return (
    <Card style={{ borderLeft: `4px solid ${accent || 'var(--border)'}` }}>
      <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase',
        letterSpacing: '0.05em', fontWeight: 600 }}>
        {label}{unit && <span style={{ color: 'var(--text-dim)', fontWeight: 500 }}> {unit}</span>}
      </div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: big ? '1.4rem' : '1.25rem', fontWeight: 700,
        marginTop: 4, color: accent && big ? accent : 'var(--text)' }}>{value}</div>
      {sub && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4,
        fontFamily: 'var(--mono)' }}>{sub}</div>}
    </Card>
  );
}

function FxStats() {
  const btns = periodButtons();
  const [active, setActive] = useState(0);
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [useCustom, setUseCustom] = useState(false);
  const [kind, setKind] = useState('all');            // all | conversion | payment
  const [sub, setSub] = useState('overview');
  const [provOpts, setProvOpts] = useState([]);
  const [pairOpts, setPairOpts] = useState([]);
  const [selProv, setSelProv] = useState(null);       // null = mind
  const [selPair, setSelPair] = useState(null);

  const params = {
    ...(useCustom
      ? { from: custom.from || undefined, to: custom.to || undefined }
      : { from: btns[active].from, to: btns[active].to }),
    ...(kind !== 'all' ? { kinds: kind } : {}),
    ...(selProv ? { providers: selProv } : {}),
    ...(selPair ? { pairs: selPair } : {}),
  };
  const fxQ = useQuery({
    queryKey: ['fx-stats', params],
    queryFn: () => fxStatsApi.analytics(params),
  });
  const { data } = fxQ;

  useEffect(() => {
    const pp = data?.overview?.provider_pair;
    if (pp?.length) {
      setProvOpts(o => o.length ? o : [...new Set(pp.map(r => r.provider))]);
      setPairOpts(o => o.length ? o : [...new Set(pp.map(r => r.pair))]);
    }
  }, [data]);

  const pillS = (on) => ({
    padding: '0.3rem 0.7rem', fontSize: '0.74rem', cursor: 'pointer', borderRadius: 'var(--radius)',
    border: '1px solid ' + (on ? 'var(--primary)' : 'var(--border)'),
    background: on ? 'rgba(59,130,246,0.15)' : 'transparent', color: on ? '#93c0ff' : 'var(--text-muted)',
  });
  const subS = (on) => ({
    padding: '0.5rem 1rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
    borderRadius: 8, border: '1px solid transparent',
    background: on ? 'var(--accent)' : 'transparent', color: on ? '#04160e' : 'var(--text-muted)',
    boxShadow: on ? '0 1px 8px rgba(16,185,129,0.35)' : 'none',
  });

  const subTabs = [['overview', 'Áttekintés'], ['ooh', 'Munkaidőn kívüli felár'],
    ['prefunding', 'Egyenleg-előrejelzés'], ['plans', 'Csomag-szimuláció']];

  return (
    <div>
      {/* Al-fülek (zöld szegmens-vezérlő) – a szűrő FÖLÖTT */}
      <div style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap', background: 'var(--bg-input)',
        border: '1px solid var(--border)', borderRadius: 11, padding: 4, marginBottom: '0.85rem' }}>
        {subTabs.map(([v, l]) => <button key={v} onClick={() => setSub(v)} style={subS(sub === v)}>{l}</button>)}
      </div>

      {/* Szűrősor */}
      <Card style={{ marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {btns.map((b, i) => (
            <button key={i} onClick={() => { setActive(i); setUseCustom(false); }} style={pillS(!useCustom && active === i)}>{b.label}</button>
          ))}
          <button onClick={() => setUseCustom(true)} style={pillS(useCustom)}>Egyéni</button>
        </div>
        {useCustom && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <Input label="Tól" type="date" value={custom.from} onChange={e => setCustom(c => ({ ...c, from: e.target.value }))} style={{ width: 160 }} />
            <Input label="Ig" type="date" value={custom.to} onChange={e => setCustom(c => ({ ...c, to: e.target.value }))} style={{ width: 160 }} />
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {provOpts.length > 1 && <>
            {provOpts.map(p => <button key={p} onClick={() => setSelProv(selProv === p ? null : p)} style={pillS(selProv === p)}>{p}</button>)}
            <span style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 4px' }} />
          </>}
          {pairOpts.length > 1 && <>
            {pairOpts.map(p => <button key={p} onClick={() => setSelPair(selPair === p ? null : p)} style={pillS(selPair === p)}>{p}</button>)}
            <span style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 4px' }} />
          </>}
          {[['all', 'Mind'], ['conversion', 'Átváltás'], ['payment', 'Fizetés']].map(([v, l]) => (
            <button key={v} onClick={() => setKind(v)} style={pillS(kind === v)}>{l}</button>
          ))}
        </div>
      </Card>

      <QueryState query={fxQ}
        empty={!data || data.meta.event_count === 0}
        emptyText="Nincs FX-esemény a kijelölt szűréssel."
        errorText="Az FX-analitika betöltése sikertelen.">
        {() => (
        <>
          {data.meta.warnings?.includes('few_months') && (
            <div style={{ marginBottom: '0.75rem' }}><Alert type="warning">
              ⚠ Csak {data.meta.months_analyzed} hónap FX-adat — az évesített becslések (cél-egyenleg, csomagdíj) bizonytalanok.
            </Alert></div>
          )}
          {sub === 'overview'   && <FxOverview data={data.overview} />}
          {sub === 'ooh'        && <FxOoh data={data.ooh} />}
          {sub === 'prefunding' && <FxPrefunding data={data.prefunding} />}
          {sub === 'plans'      && <FxPlanSim data={data.plan_simulation} />}
        </>
        )}
      </QueryState>
    </div>
  );
}

function FxOverview({ data }) {
  const thS = { padding: '0.5rem 0.75rem', fontSize: '0.68rem', fontWeight: 600, color: 'var(--text-muted)',
    textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)', textAlign: 'left' };
  const thN = { ...thS, textAlign: 'right' };
  const tdS = { padding: '0.5rem 0.75rem', fontSize: '0.82rem', borderBottom: '1px solid var(--border-dim)' };
  const tdN = { ...tdS, textAlign: 'right', fontFamily: 'var(--mono)' };
  const bandColor = k => (k === 'conversion' ? FX_CONV : FX_PAY);

  return (
    <div>
      {/* KPI sávok */}
      {data.kpis.map(k => k.event_count > 0 && (
        <div key={k.band} style={{ marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.72rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.05em', color: bandColor(k.band), margin: '0 0 6px 2px' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: bandColor(k.band) }} />
            {FX_KIND_LBL[k.band]} · {k.band === 'conversion' ? 'saját treasury (átváltás)' : 'deviza költés (kártyás/szállítói)'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '0.75rem' }}>
            <FxKpi label="Teljes FX-költség" unit="HUF" value={fxFt(k.total_cost_huf)} accent={bandColor(k.band)}
              sub={`díj ${fxFt(k.fee_huf)} · rés ${fxFt(k.spread_huf)}`} />
            <FxKpi label="Költséghányad" unit="%" value={fxPct(k.cost_ratio_pct, 3)} accent={bandColor(k.band)}
              sub={`${k.event_count} esemény`} />
            <FxKpi label="Effektív vs MNB" unit="bps" value={fxBps(k.eff_vs_mnb_bps)} accent={bandColor(k.band)}
              sub="eltérés az MNB-középtől" />
            <FxKpi label="Volumen" unit="HUF" value={fxFt(k.volume_huf)} accent={bandColor(k.band)} />
          </div>
        </div>
      ))}

      {/* Insight-lista */}
      {data.insights?.length > 0 && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          {data.insights.map((ins, i) => (
            <div key={i} style={{ flex: '1 1 240px', background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderLeft: `3px solid ${ins.severity === 'warn' ? 'var(--warning)' : 'var(--primary)'}`,
              borderRadius: 8, padding: '9px 12px', fontSize: '0.8rem' }}>{ins.text}</div>
          ))}
        </div>
      )}

      {/* Havi FX-költség (stacked: díj + rés) */}
      <Card title="Havi FX-költség (HUF)" style={{ marginBottom: '0.75rem' }}>
        <StackedMonthlyChart rows={data.monthly_cost} ytitle="HUF" showLegend
          series={[
            { key: 'fee_huf',    name: 'Látható díj',        color: FX_FEE },
            { key: 'spread_huf', name: 'Rejtett árfolyamrés', color: FX_SPREAD },
          ]} />
      </Card>

      {/* Effektív vs MNB idősor */}
      <Card title="Effektív árfolyam vs MNB-közép" style={{ marginBottom: '0.75rem' }}>
        <FxEffChart rows={data.eff_vs_mnb} pairColor={fxPairColor} />
        {data.eff_vs_mnb.length > 0 && (
          <div style={{ display: 'flex', gap: 18, fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 8, flexWrap: 'wrap' }}>
            {[...new Set(data.eff_vs_mnb.map(r => r.pair))].map((p, i) => <span key={p} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <i style={{ width: 22, height: 5, borderRadius: 3, background: fxPairColor(p, i) }} />{p} effektív</span>)}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><i style={{ width: 22, height: 3, borderRadius: 3, background: 'var(--text-muted)' }} />MNB-közép (szaggatott)</span>
          </div>
        )}
      </Card>

      {/* Provider × pár tábla */}
      <Card title="Szolgáltató × pár – legolcsóbb kiemelve" noPad>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={thS}>Szolgáltató</th><th style={thS}>Pár</th>
              <th style={thN}>Volumen (HUF)</th><th style={thN}>Költséghányad</th>
              <th style={thN}>Eff. vs MNB</th><th style={thN}>Esemény</th>
            </tr></thead>
            <tbody>
              {data.provider_pair.map((r, i) => (
                <tr key={i} style={{ borderLeft: r.cheapest ? '3px solid var(--accent)' : '3px solid transparent',
                  background: r.cheapest ? 'rgba(16,185,129,0.07)' : 'transparent' }}>
                  <td style={tdS}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <BankDot name={r.provider} size={10} />{r.provider}</span></td>
                  <td style={tdS}>{r.pair}{r.cheapest && <span style={{ marginLeft: 6 }}><Badge variant="success">✓ legolcsóbb</Badge></span>}</td>
                  <td style={tdN}>{fxFt(r.volume_huf)}</td>
                  <td style={tdN}>{fxPct(r.cost_ratio_pct, 3)}</td>
                  <td style={{ ...tdN, color: 'var(--text-muted)' }}>{fxBps(r.eff_vs_mnb_bps)}</td>
                  <td style={tdN}>{r.event_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function FxOoh({ data }) {
  const thS = { padding: '0.5rem 0.75rem', fontSize: '0.68rem', fontWeight: 600, color: 'var(--text-muted)',
    textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)', textAlign: 'left' };
  const thN = { ...thS, textAlign: 'right' };
  const tdS = { padding: '0.5rem 0.75rem', fontSize: '0.82rem', borderBottom: '1px solid var(--border-dim)' };
  const tdN = { ...tdS, textAlign: 'right', fontFamily: 'var(--mono)' };
  const basisLbl = b => b === 'DETECTED_WEEKEND' ? '🗓 hétvége' : b === 'DETECTED_HOLIDAY' ? '🎌 ünnep' : '~ becslés';

  return (
    <div>
      <div style={{ marginBottom: '0.75rem' }}><Alert type="warning">
        ⚠ Munkaidőn kívüli felár: <b>{fxFt(data.total_surcharge_huf)}</b> a kijelölt időszakban
        ({data.detected_count} detektált + {data.inferred_count} becsült).
        {data.usd_card.event_count > 0 && <> Ebből USD-kártyás: {fxFt(data.usd_card.surcharge_huf)}.</>}
        {' '}Pre-fundinggal ~{fxFt(data.avoidable_annual_huf)}/év elkerülhető.
      </Alert></div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <FxKpi label="Felár összesen" unit="HUF" value={fxFt(data.total_surcharge_huf)} accent="var(--warning)"
          sub={`${data.event_count} esemény`} />
        <FxKpi label="Detektált / becsült" unit="db" value={`${data.detected_count} / ${data.inferred_count}`} accent="var(--warning)"
          sub={`ebből ${data.usd_card.event_count} USD-kártyás`} />
        <FxKpi label="Elkerülhető" unit="HUF/év" value={fxFt(data.avoidable_annual_huf)} accent="var(--accent)"
          sub={`a felár ~93%-a`} />
        <FxKpi label="Felár aránya" unit="%" value={fxPct(data.share_of_total_pct, 1)} accent="var(--warning)"
          sub="az össz. FX-költségből" />
      </div>

      <Card title="Havi felár-trend (HUF) – detektált (tömör) + becsült (sraffozott)" style={{ marginBottom: '0.75rem' }}>
        <StackedMonthlyChart rows={data.monthly} ytitle="HUF"
          series={[
            { key: 'detected_huf', name: 'Detektált (biztos)',        color: FX_FEE },
            { key: 'inferred_huf', name: 'Következtetett (becslés)',  color: FX_SPREAD, hatch: true },
          ]} />
        <div style={{ display: 'flex', gap: 18, fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 8 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><i style={{ width: 15, height: 15, borderRadius: 3, background: FX_FEE }} />Detektált (biztos)</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><i style={{ width: 15, height: 15, borderRadius: 3, background: FX_SPREAD, opacity: 0.6 }} />Következtetett (becslés)</span>
        </div>
      </Card>

      <Card title="Feláras események – a fő szám: felár = 1,0% × volumen" noPad>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={thS}>Dátum</th><th style={thS}>Szolg.</th><th style={thS}>Pár</th>
              <th style={thN}>Volumen (HUF)</th><th style={thN}>Spread %</th><th style={thN}>Bench. %</th>
              <th style={thN}>Felár (HUF)</th><th style={thS}>Bizonyosság</th>
            </tr></thead>
            <tbody>
              {data.events.map((e, i) => (
                <tr key={i} style={{ borderLeft: e.is_usd_card ? '3px solid var(--danger)' : '3px solid transparent' }}>
                  <td style={{ ...tdS, fontFamily: 'var(--mono)' }}>{e.tx_date}</td>
                  <td style={tdS}>{e.provider}</td>
                  <td style={tdS}>{e.pair}{e.is_usd_card && <span style={{ marginLeft: 6 }}><Badge variant="danger">USD-kártya</Badge></span>}</td>
                  <td style={tdN}>{fxFt(e.volume_huf)}</td>
                  <td style={{ ...tdN, color: FX_SPREAD }}>{fxPct(e.spread_pct, 3)}</td>
                  <td style={{ ...tdN, color: 'var(--text-muted)' }}>{fxPct(e.benchmark_pct, 3)}</td>
                  <td style={{ ...tdN, fontWeight: 700 }}>{fxFt(e.surcharge_huf)}</td>
                  <td style={tdS}><Badge variant={e.basis.startsWith('DETECTED') ? 'success' : 'warning'}>{basisLbl(e.basis)}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function FxPrefunding({ data }) {
  const [curTab, setCurTab] = useState(data.by_ccy[0]?.currency);
  if (!data.by_ccy.length) return <Card><div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>Nincs deviza-kiadás az időszakban.</div></Card>;
  const active = data.by_ccy.some(c => c.currency === curTab) ? curTab : data.by_ccy[0].currency;
  const hist = data.histogram.filter(h => h.currency === active);
  const nf = (v) => huFmt(v, 2);
  return (
    <div>
      <div style={{ marginBottom: '0.75rem' }}><Alert type="info">
        ⓘ Cél-egyenleg = max( P90 havi igény × 1,5 ; max havi kiadás × 1,2 ). Diagnosztikus + ajánló – nem indít utalást.
      </Alert></div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
        {data.by_ccy.map(c => {
          const cc = currencyColor(c.currency);
          const ok = c.gap <= 0;
          return (
            <Card key={c.currency} style={{ borderLeft: `4px solid ${cc}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <CurrencyBadge cur={c.currency} />
                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>egyenleg vs cél</span>
              </div>
              <div style={{ fontSize: '0.78rem', display: 'grid', gap: 3 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text-muted)' }}>Aktuális</span><span style={{ fontFamily: 'var(--mono)' }}>{nf(c.current_balance)} {c.currency}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text-muted)' }}>Cél (P90×1,5)</span><span style={{ fontFamily: 'var(--mono)', fontWeight: 700 }}>{nf(c.target)} {c.currency}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text-muted)' }}>Hiány</span>
                  <span style={{ fontFamily: 'var(--mono)', color: ok ? 'var(--accent)' : 'var(--danger)' }}>{ok ? '✓ fedezve' : `▼ ${nf(c.gap)} ${c.currency}`}</span></div>
                {!ok && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text-muted)' }}>Javasolt havi</span>
                  <span style={{ fontFamily: 'var(--mono)', color: FX_CONV }}>+{nf(c.suggested_monthly)} {c.currency}</span></div>}
              </div>
              {/* Bullet-sáv: aktuális egyenleg (kitöltés) a cél-jelölőhöz képest */}
              {(() => {
                const scaleMax = Math.max(c.current_balance || 0, c.target || 0, 1) * 1.05;
                const pct  = Math.max(0, Math.min(100, ((c.current_balance || 0) / scaleMax) * 100));
                const tpct = Math.max(0, Math.min(100, ((c.target || 0) / scaleMax) * 100));
                return (
                  <div title={`Aktuális ${nf(c.current_balance)} / cél ${nf(c.target)} ${c.currency}`}
                    style={{ position: 'relative', height: 6, background: 'rgba(139,155,180,0.15)', borderRadius: 3, marginTop: 10 }}>
                    <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`,
                      background: cc, borderRadius: 3, opacity: 0.85 }} />
                    <div style={{ position: 'absolute', left: `calc(${tpct}% - 1px)`, top: -3, bottom: -3,
                      width: 2, background: 'var(--text)', borderRadius: 1 }} />
                  </div>
                );
              })()}
              {!ok && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.4 }}>
                Minden hó első munkanapján válts ~{nf(c.suggested_monthly)} {c.currency}-t (munkaidőben).</div>}
            </Card>
          );
        })}
      </div>

      {data.by_ccy.length > 1 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: '0.5rem' }}>
          {data.by_ccy.map(c => {
            const on = active === c.currency, cc = currencyColor(c.currency);
            return <button key={c.currency} onClick={() => setCurTab(c.currency)} style={{
              background: on ? cc + '22' : 'transparent', color: on ? cc : 'var(--text-muted)',
              border: '1px solid ' + (on ? cc : 'var(--border)'), borderRadius: 'var(--radius)',
              padding: '0.3rem 0.75rem', fontSize: '0.74rem', cursor: 'pointer', fontFamily: 'var(--mono)',
              fontWeight: on ? 700 : 400,
            }}>{c.currency}</button>;
          })}
        </div>
      )}
      <Card title={`Havi kiadás – ${active} (medián / P90 / max)`} style={{ borderTop: `3px solid ${currencyColor(active)}` }}>
        <FxHistChart rows={hist} ccy={active} />
      </Card>
    </div>
  );
}

function FxPlanSim({ data }) {
  const [scen, setScen] = useState('current');
  if (!data.available) return <Card><div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>
    A díjkonfig nem elérhető (plan_config).</div></Card>;
  const sd = data.scenarios[scen];
  const byPlan = Object.fromEntries(sd.plans.map(p => [p.plan, p]));
  const plansOrder = sd.plans.map(p => p.plan);
  const cheaper = sd.recommended;
  const otherAnnual = plansOrder.filter(p => p !== cheaper).map(p => byPlan[p].annual_total_huf)[0] || 0;
  const save = Math.abs((byPlan[cheaper]?.annual_total_huf || 0) - otherAnnual);
  const months = byPlan[plansOrder[0]]?.months || [];

  const thS = { padding: '0.5rem 0.75rem', fontSize: '0.68rem', fontWeight: 600, color: 'var(--text-muted)',
    textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)', textAlign: 'left' };
  const thN = { ...thS, textAlign: 'right' };
  const tdS = { padding: '0.5rem 0.75rem', fontSize: '0.82rem', borderBottom: '1px solid var(--border-dim)' };
  const tdN = { ...tdS, textAlign: 'right', fontFamily: 'var(--mono)' };
  const scenPill = (v, l) => (
    <button onClick={() => setScen(v)} style={{
      padding: '0.3rem 0.8rem', fontSize: '0.75rem', cursor: 'pointer', borderRadius: 'var(--radius)',
      border: '1px solid ' + (scen === v ? 'var(--primary)' : 'var(--border)'),
      background: scen === v ? 'rgba(59,130,246,0.15)' : 'transparent', color: scen === v ? '#93c0ff' : 'var(--text-muted)',
    }}>{l}</button>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginRight: 4 }}>Szcenárió:</span>
        {scenPill('current', 'Jelenlegi viselkedés')}
        {scenPill('ooh_zero', 'Fegyelmezett pre-funding')}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>ⓘ Scale 35 000 Ft/hó · 20M keret · Grow 10 000 Ft/hó · 5,8M keret · felette 0,6%</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
        {plansOrder.map(p => (
          <FxKpi key={p} label={`Éves díj – ${byPlan[p].label.replace('Revolut Business – ', '')}`} unit="Ft/év"
            value={fxFt(byPlan[p].annual_total_huf)} accent="#5b8def"
            sub={`⌀ ${fxFt(Math.round(byPlan[p].annual_total_huf / 12))}/hó`} />
        ))}
        <FxKpi label="Ajánlott csomag" value={(cheaper || '').toUpperCase()} accent="var(--accent)" big
          sub={`−${fxFt(save)}/év a másikhoz képest`} />
        <FxKpi label="Break-even" unit="Ft/hó FX" value={fxFt(data.break_even_fx_volume_huf)} accent="#5b8def"
          sub="alatta Grow · felette Scale" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <Card title="Havi becsült díj – Scale vs Grow" noPad>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={thS}>Hónap</th><th style={thN}>FX-volumen</th>
                <th style={thN}>Scale</th><th style={thN}>Grow</th><th style={thS}>Billenés</th>
              </tr></thead>
              <tbody>
                {months.map((m, i) => {
                  const sc = byPlan.scale?.months[i]?.fee_huf ?? 0;
                  const gr = byPlan.grow?.months[i]?.fee_huf ?? 0;
                  const growCheaper = gr < sc;
                  const tip = byPlan.grow?.months[i]?.over_allowance;
                  return (
                    <tr key={m.month}>
                      <td style={{ ...tdS, fontFamily: 'var(--mono)' }}>{m.month.slice(2)}</td>
                      <td style={tdN}>{(m.fx_volume_huf / 1e6).toFixed(1)}M</td>
                      <td style={{ ...tdN, background: !growCheaper ? 'rgba(16,185,129,0.10)' : 'transparent' }}>{fxFt(sc)}{!growCheaper && ' ✓'}</td>
                      <td style={{ ...tdN, background: growCheaper ? 'rgba(16,185,129,0.10)' : 'transparent' }}>{fxFt(gr)}{growCheaper && ' ✓'}</td>
                      <td style={tdS}>{tip && <Badge variant="warning">⚠ Grow keret-túllépés</Badge>}</td>
                    </tr>
                  );
                })}
                <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 700 }}>
                  <td style={tdS}>ÉVES*</td><td style={tdN}>–</td>
                  <td style={tdN}>{fxFt(byPlan.scale?.annual_total_huf)}</td>
                  <td style={tdN}>{fxFt(byPlan.grow?.annual_total_huf)}</td>
                  <td style={tdS}>{(cheaper || '').toUpperCase()} olcsóbb</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: '0.68rem', color: 'var(--text-dim)', padding: '0.5rem 0.75rem' }}>
            *ÉVES = a {data.months_analyzed} hónap 12 hónapra vetítve.
          </div>
        </Card>
        <Card title="Break-even (havi díj Ft vs FX-volumen Ft/hó)">
          <FxBreakEvenChart months={months} plans={sd.plans}
            breakEven={data.break_even_fx_volume_huf} planColor={FX_PLAN_COLOR} />
          <div style={{ display: 'flex', gap: 18, fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 8 }}>
            {sd.plans.map(p => (
              <span key={p.plan} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <i style={{ width: 22, height: 5, borderRadius: 3, background: FX_PLAN_COLOR(p) }} />
                {p.label.replace('Revolut Business – ', '')}</span>
            ))}
          </div>
        </Card>
      </div>

      {/* Havi díjkülönbség (Grow − Scale) – nulla körüli diverging oszlopok */}
      {byPlan.scale && byPlan.grow && months.length > 0 && (
        <Card title="Havi díjkülönbség – Grow − Scale (Ft/hó)"
          subtitle="Negatív (zöld) = a Grow olcsóbb az adott hónapban · pozitív (kék) = a Scale"
          style={{ marginTop: '0.75rem' }}>
          <PlanDeltaChart rows={months.map((m, i) => ({
            month: m.month,
            delta: (byPlan.grow?.months[i]?.fee_huf ?? 0) - (byPlan.scale?.months[i]?.fee_huf ?? 0),
          }))} />
        </Card>
      )}
    </div>
  );
}

// Nézet-váltó pill (Részesedés ⟷ Donut) – localStorage-ben perzisztált.
function useStoredView(key, def) {
  const [v, setV] = useState(() => {
    try { return localStorage.getItem(key) || def; } catch { return def; }
  });
  const set = (nv) => {
    setV(nv);
    try { localStorage.setItem(key, nv); } catch { /* privát mód */ }
  };
  return [v, set];
}

function ViewSwitch({ value, onChange, options }) {
  return (
    <div style={{ display: 'inline-flex', border: '1px solid var(--border)',
      borderRadius: 999, overflow: 'hidden' }}>
      {options.map(o => (
        <button key={o.value} onClick={() => onChange(o.value)} style={{
          padding: '0.28rem 0.75rem', fontSize: '0.72rem', cursor: 'pointer', border: 'none',
          background: value === o.value ? 'var(--tab-active-bg)' : 'transparent',
          color: value === o.value ? 'var(--tab-active-fg)' : 'var(--text-muted)',
          fontWeight: value === o.value ? 600 : 400,
        }}>{o.label}</button>
      ))}
    </div>
  );
}

const VIEW_OPTIONS = [
  { value: 'bars',  label: 'Részesedés' },
  { value: 'donut', label: 'Donut' },
];

// A havi trend fix, validált palettája (dataviz skill) – sorrend = összeg szerinti
// kategória-sorrend, SOHA nem ciklizálódik újra; az „Egyéb" mindig szürke.
const TREND_COLORS = ['#3b82f6', '#0d9488', '#9333ea', '#ea580c'];
const TREND_OTHER = '#64748b';

function CategoryStats() {
  const [view, setView] = useStoredView('stats_cat_view', 'bars');
  // D26 (2026-09-27): nem-ADMIN-nak a backend a BÉR / TAGI JÖVEDELEM kategóriát kihagyja —
  // a végösszeg ezt kimondja (U2, INV-35).
  const { isAdmin } = useAuth();

  // ── Fő blokk: kategóriánkénti fizetendő egy időszakra ──────────────────────
  // ⚠⚠ 2026-08-24 (wiki/proposals/pagination.md "Problem"): a RÉGI verzió a
  // `/api/documents`-ből TÖLTÖTT NYERS SOROKAT aggregálta kliens-oldalon —
  // 1000 dokumentum fölött csendben alulszámolt, és nem volt időszűrője (minden
  // bizonylatot próbált egyszerre látni). A `category-stats` végpont a
  // SZERVEREN aggregál, limit nélkül, és most már a Gábor kérte 5 időszak-
  // preset szerint szűr (alap: aktuális év).
  const [topPreset, setTopPreset] = useState('this-year');
  const [topCustom, setTopCustom] = useState({});
  const topRange = periodToRange(topPreset, topCustom);
  const topQ = useQuery({
    queryKey: ['category-stats', topRange],
    queryFn: () => documentsApi.categoryStats(topRange),
  });
  const topRows = topQ.data ?? [];   // [{category, total_huf, count}], összeg szerint csökkenő

  // Részesedés-oszlopok: a sáv hossza = részesedés a teljes költésből.
  const shareData = topRows.map(r => ({
    name: truncName(r.category), fullName: r.category, value: r.total_huf, count: r.count,
    color: r.category === 'Egyéb' ? TREND_OTHER : 'var(--chart-out)',
  }));

  // Donut-nézet: top-6 + „Egyéb" (a sok szelet olvashatatlan).
  const top6 = topRows.slice(0, 6);
  const rest6 = topRows.slice(6);
  const restVal = rest6.reduce((s, r) => s + r.total_huf, 0);
  const restCnt = rest6.reduce((s, r) => s + r.count, 0);
  const donutSlices = top6.map((r, i) => ({
    label: r.category, value: r.total_huf, count: r.count,
    color: r.category === 'Egyéb' ? OTHER_COLOR : colorAt(i),
  }));
  if (restVal > 0) {
    const eg = donutSlices.find(s => s.label === 'Egyéb');
    if (eg) { eg.value += restVal; eg.count += restCnt; }
    else donutSlices.push({ label: 'Egyéb', value: restVal, count: restCnt, color: OTHER_COLOR });
  }

  // ── Havi bontás — SAJÁT időszűrő (alap: utolsó 12 hónap) ───────────────────
  const [trendPreset, setTrendPreset] = useState('last-12-months');
  const [trendCustom, setTrendCustom] = useState({});
  const trendRange = periodToRange(trendPreset, trendCustom);
  const trendQ = useQuery({
    queryKey: ['category-stats-monthly', trendRange],
    queryFn: () => documentsApi.categoryStats({ ...trendRange, group_by: 'month' }),
  });
  const monthlyRows = trendQ.data ?? [];   // [{category, month, total_huf, count}]

  const monthCatTotals = {};
  monthlyRows.forEach(r => { monthCatTotals[r.category] = (monthCatTotals[r.category] || 0) + r.total_huf; });
  // Top-4 (az „Egyéb" kategória sosem top – mindig a maradékba olvad).
  const topCats = Object.entries(monthCatTotals)
    .filter(([c]) => c !== 'Egyéb').sort((a, b) => b[1] - a[1]).slice(0, 4).map(([c]) => c);
  const hasOther = Object.keys(monthCatTotals).some(c => !topCats.includes(c));
  // Szanitizált dataKey-ek (a kategórianévben pont lehet → Recharts path-nak venné).
  const trendSeries = [
    ...topCats.map((c, i) => ({ key: `c${i}`, name: c, color: TREND_COLORS[i] })),
    ...(hasOther ? [{ key: 'other', name: 'Egyéb', color: TREND_OTHER }] : []),
  ];
  const otherCatsM = Object.keys(monthCatTotals).filter(c => !topCats.includes(c));
  const byCatMonth = {};
  monthlyRows.forEach(r => { (byCatMonth[r.category] = byCatMonth[r.category] || {})[r.month] = r.total_huf; });
  // A kiválasztott tartomány hónap-kulcsai (a chartnak akkor is kell üres
  // oszlop egy hónapra, ha abban a hónapban nem volt tétel).
  const monthKeys = [];
  if (trendRange.date_from && trendRange.date_to) {
    let cur = new Date(`${trendRange.date_from}T00:00:00`);
    const end = new Date(`${trendRange.date_to}T00:00:00`);
    while (cur <= end) {
      monthKeys.push(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`);
      cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    }
  }
  const trendRows = monthKeys.map(m => {
    const row = { month: m };
    topCats.forEach((c, i) => { row[`c${i}`] = Math.round(byCatMonth[c]?.[m] || 0); });
    if (hasOther) row.other = Math.round(otherCatsM.reduce((s, c) => s + (byCatMonth[c]?.[m] || 0), 0));
    return row;
  });
  const hasTrend = trendSeries.length > 0 && trendRows.some(r => trendSeries.some(s => r[s.key]));

  // ── Éves összehasonlító — nincs saját időszűrő, mindig minden elérhető év ──
  const yearlyQ = useQuery({
    queryKey: ['category-stats-yearly'],
    queryFn: () => documentsApi.categoryStats({ group_by: 'year' }),
  });
  const yearlyRows = yearlyQ.data ?? [];   // [{category, year, total_huf, count}]
  const yearCatTotals = {};
  yearlyRows.forEach(r => { yearCatTotals[r.category] = (yearCatTotals[r.category] || 0) + r.total_huf; });
  const yearTopCats = Object.entries(yearCatTotals)
    .filter(([c]) => c !== 'Egyéb').sort((a, b) => b[1] - a[1]).slice(0, 4).map(([c]) => c);
  const yearHasOther = Object.keys(yearCatTotals).some(c => !yearTopCats.includes(c));
  const yearSeries = [
    ...yearTopCats.map((c, i) => ({ key: `c${i}`, name: c, color: TREND_COLORS[i] })),
    ...(yearHasOther ? [{ key: 'other', name: 'Egyéb', color: TREND_OTHER }] : []),
  ];
  const years = [...new Set(yearlyRows.map(r => r.year))].sort();
  const yearOtherCats = Object.keys(yearCatTotals).filter(c => !yearTopCats.includes(c));
  const byCatYear = {};
  yearlyRows.forEach(r => { (byCatYear[r.category] = byCatYear[r.category] || {})[r.year] = r.total_huf; });
  const yearlyChartRows = years.map(y => {
    const row = { year: y };
    yearTopCats.forEach((c, i) => { row[`c${i}`] = Math.round(byCatYear[c]?.[y] || 0); });
    if (yearHasOther) row.other = Math.round(yearOtherCats.reduce((s, c) => s + (byCatYear[c]?.[y] || 0), 0));
    return row;
  });

  return (
    <>
      <Card title={isAdmin ? 'Kategóriák (fizetendő, HUF-közelítő)'
                           : 'Kategóriák (fizetendő, HUF-közelítő, bér nélkül)'}
        actions={<ViewSwitch value={view} onChange={setView} options={VIEW_OPTIONS} />}>
        <div style={{ marginBottom: 12 }}>
          <PeriodFilter preset={topPreset} onPresetChange={setTopPreset}
            custom={topCustom} onCustomChange={setTopCustom} presets={STANDARD_PRESETS} />
        </div>
        <QueryState query={topQ} empty={topRows.length === 0}
          emptyText="Nincs bizonylat ebben az időszakban."
          errorText="A kategória-statisztika betöltése sikertelen.">
          {view === 'donut'
            ? <DonutChart slices={donutSlices}
                centerLabel={isAdmin ? 'Összesen' : 'Összesen (bér nélkül)'} />
            : <ShareBarChart data={shareData} />}
        </QueryState>
      </Card>

      <Card title="Havi bontás kategóriánként"
        subtitle="Top 4 kategória + Egyéb – a teljesítés (vagy kiállítás) hónapja szerint">
        <div style={{ marginBottom: 12 }}>
          <PeriodFilter preset={trendPreset} onPresetChange={setTrendPreset}
            custom={trendCustom} onCustomChange={setTrendCustom} presets={MONTHLY_TREND_PRESETS} />
        </div>
        <QueryState query={trendQ} empty={!hasTrend}
          emptyText="Nincs adat erre az időszakra."
          errorText="A havi bontás betöltése sikertelen.">
          <CategoryTrendChart data={trendRows} series={trendSeries} />
        </QueryState>
      </Card>

      <Card title="Éves összehasonlító kategóriánként"
        subtitle="Top 4 kategória + Egyéb – minden elérhető év">
        <QueryState query={yearlyQ} empty={yearlyChartRows.length === 0}
          emptyText="Nincs elég adat évek közti összehasonlításhoz."
          errorText="Az éves összehasonlító betöltése sikertelen.">
          <CategoryYearlyChart data={yearlyChartRows} series={yearSeries} />
        </QueryState>
      </Card>
    </>
  );
}

// Validált kategorikus paletta (sötét téma, dataviz skill) – fix sorrend.
const PARTNER_COLORS = ['#3987e5', '#199e70', '#c98500', '#008300',
                        '#9085e9', '#e66767', '#d55181', '#d95926'];
const OTHER_COLOR = '#898781';   // "Egyéb" / semleges szürke
const colorAt = (i) => PARTNER_COLORS[i % PARTNER_COLORS.length];

// Újrahasználható lyukas pie (donut) + legenda. slices: [{label, value, count?, color}]
function DonutChart({ slices, centerLabel = 'Összesen' }) {
  const total = slices.reduce((s, x) => s + (x.value || 0), 0) || 1;
  const SZ = 210, R = 82, TH = 26, C = 2 * Math.PI * R;
  let acc = 0;
  if (!slices.length) {
    return <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Nincs adat.</p>;
  }
  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
      <div style={{ position: 'relative', width: SZ, height: SZ, flexShrink: 0 }}>
        <svg viewBox={`0 0 ${SZ} ${SZ}`} width={SZ} height={SZ}>
          <g transform={`rotate(-90 ${SZ / 2} ${SZ / 2})`}>
            {slices.map(s => {
              const seg = (s.value / total) * C;
              const el = (
                <circle key={s.label} cx={SZ / 2} cy={SZ / 2} r={R} fill="none"
                  stroke={s.color} strokeWidth={TH}
                  strokeDasharray={`${Math.max(seg - 2, 0.5)} ${C}`}
                  strokeDashoffset={-acc}>
                  <title>{`${s.label}: ${huFmt(s.value, 0)} Ft (${(s.value / total * 100).toFixed(1)}%)`}</title>
                </circle>
              );
              acc += seg;
              return el;
            })}
          </g>
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{centerLabel}</span>
          <span style={{ fontSize: '1.05rem', fontWeight: 700 }}>{huFmt(total, 0)} Ft</span>
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 240, display: 'flex', flexDirection: 'column', gap: 6,
        maxHeight: 320, overflowY: 'auto' }}>
        {slices.map(s => (
          <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem' }}>
            <span style={{ width: 11, height: 11, borderRadius: 3, background: s.color, flexShrink: 0 }} />
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {s.label}{s.count != null && <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}> ({s.count})</span>}
            </span>
            <span style={{ fontFamily: 'var(--mono)', whiteSpace: 'nowrap' }}>{huFmt(s.value, 0)} Ft</span>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem', width: 38, textAlign: 'right' }}>
              {(s.value / total * 100).toFixed(0)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// 2026-09-24 — tulajdonosi döntés: a statisztika alapból partner-CSOPORTRA összesít
// (a Google két jogi személye egy sor), kapcsolóval jogi személyre bontható. A
// csoportot a Partnerek oldalon lehet kezelni; az alias a statisztikát nem érinti.
function GroupByToggle({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Összesítés:</span>
      <Pill active={value === 'group'} onClick={() => onChange('group')}
        title="A közös csoportba sorolt partnerek (pl. egy márka több cége) egy sorban">
        Csoport
      </Pill>
      <Pill active={value === 'legal_entity'} onClick={() => onChange('legal_entity')}
        title="Minden jogi személy (adószám) külön sorban">
        Jogi személy
      </Pill>
    </div>
  );
}

// D26 (2026-09-27, U2): nem-ADMIN-nak a backend a `include_baj`-t figyelmen kívül hagyja
// (bér nélkül válaszol) — ezért nála a kapcsoló nem látszik kattinthatónak, fix felirat áll.
function BajToggle({ includeBaj, setIncludeBaj, btnS }) {
  const { isAdmin } = useAuth();
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Bérek/Adók/Járulékok:</span>
      {isAdmin ? (<>
        <button style={btnS(includeBaj)} onClick={() => setIncludeBaj(true)}>B.A.J.-vel együtt</button>
        <button style={btnS(!includeBaj)} onClick={() => setIncludeBaj(false)}>B.A.J. nélkül</button>
      </>) : (
        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
          B.A.J. nélkül — a bér-tételeket csak cég-adminisztrátor látja.
        </span>
      )}
    </div>
  );
}

function PartnerStats() {
  const [dir, setDir] = useState('incoming');
  const [includeBaj, setIncludeBaj] = useState(true);
  // Időszűrő (2026-08-24, Gábor kérése — "a szokásos"). Alap: aktuális év.
  const [preset, setPreset] = useState('this-year');
  const [custom, setCustom] = useState({});
  const range = periodToRange(preset, custom);
  const [groupBy, setGroupBy] = useStoredView('stats_partner_group_by', 'group');
  const statsQ = useQuery({
    queryKey: ['partner-stats', dir, includeBaj, range, groupBy],
    queryFn: () => documentsApi.partnerStats({ direction: dir, limit: 40,
      include_baj: dir === 'incoming' ? includeBaj : true, group_by: groupBy, ...range }),
  });
  const rows = statsQ.data ?? [];
  const isLoading = statsQ.isLoading;
  const btnS = (on) => ({
    padding: '0.3rem 0.7rem', borderRadius: 'var(--radius)', fontSize: '0.78rem',
    border: `1px solid ${on ? 'var(--primary)' : 'var(--border)'}`,
    background: on ? 'rgba(59,130,246,0.15)' : 'transparent',
    color: on ? 'var(--primary)' : 'var(--text-muted)', cursor: 'pointer',
  });

  const [view, setView] = useStoredView('stats_partner_view', 'bars');

  // Donut-nézet: top 7 + „Egyéb" (a sok szelet olvashatatlan; a 8. a maradék).
  const TOP = 7;
  const top = rows.slice(0, TOP);
  const rest = rows.slice(TOP);
  const restTotal = rest.reduce((s, r) => s + r.total_huf, 0);
  const restCount = rest.reduce((s, r) => s + r.count, 0);
  const slices = [
    ...top.map((r, i) => ({ label: r.partner, value: r.total_huf, count: r.count, color: colorAt(i) })),
    ...(restTotal > 0 ? [{ label: 'Egyéb', value: restTotal, count: restCount, color: OTHER_COLOR }] : []),
  ];

  // Részesedés-oszlopok: top 8 + „Egyéb". Szín az irányt követi:
  // szállítók = költség-kék, vevők = bevétel-zöld; „Egyéb" semleges szürke.
  const dirColor = dir === 'outgoing' ? 'var(--chart-in)' : 'var(--chart-out)';
  const TOPB = 8;
  const btop = rows.slice(0, TOPB);
  const brest = rows.slice(TOPB);
  const brestTotal = brest.reduce((s, r) => s + r.total_huf, 0);
  const brestCount = brest.reduce((s, r) => s + r.count, 0);
  const barData = [
    ...btop.map(r => ({ name: truncName(r.partner, 20), fullName: r.partner,
      value: r.total_huf, count: r.count, color: dirColor })),
    ...(brestTotal > 0 ? [{ name: 'Egyéb', fullName: `Egyéb (${brest.length} partner)`,
      value: brestTotal, count: brestCount, color: TREND_OTHER }] : []),
  ];

  // Koncentráció: a top 3 partner részesedése a teljes összegből.
  const grandTotal = rows.reduce((s, r) => s + r.total_huf, 0);
  const top3 = rows.slice(0, 3).reduce((s, r) => s + r.total_huf, 0);
  const top3Pct = grandTotal > 0 ? Math.round((top3 / grandTotal) * 100) : 0;

  return (
    <Card title={`Top partnerek (fizetendő, HUF-közelítő – ${groupBy === 'group' ? 'csoportonként' : 'jogi személyenként'})`}
      actions={<ViewSwitch value={view} onChange={setView} options={VIEW_OPTIONS} />}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button style={btnS(dir === 'incoming')} onClick={() => setDir('incoming')}>Szállítók (bejövő)</button>
        <button style={btnS(dir === 'outgoing')} onClick={() => setDir('outgoing')}>Vevők (kimenő)</button>
      </div>
      {dir === 'incoming' && (
        <BajToggle includeBaj={includeBaj} setIncludeBaj={setIncludeBaj} btnS={btnS} />
      )}
      <GroupByToggle value={groupBy} onChange={setGroupBy} />
      <div style={{ marginBottom: 12 }}>
        <PeriodFilter preset={preset} onPresetChange={setPreset}
          custom={custom} onCustomChange={setCustom} presets={STANDARD_PRESETS} />
      </div>
      {!isLoading && rows.length > 0 && (
        <div style={{ display: 'flex', gap: 18, marginBottom: 14, flexWrap: 'wrap',
          fontSize: '0.78rem', color: 'var(--text-muted)' }}>
          <span>Összesen: <b style={{ color: 'var(--text)', fontFamily: 'var(--mono)',
            fontVariantNumeric: 'tabular-nums' }}>{huFmt(grandTotal, 0)} Ft</b></span>
          <span><b style={{ color: 'var(--text)' }}>{rows.length}</b> partner</span>
          <span>Top 3 partner = <b style={{ color: 'var(--text)' }}>{top3Pct}%</b></span>
        </div>
      )}
      <QueryState query={statsQ} empty={rows.length === 0}
        emptyText="Nincs partner-adat ehhez a szűréshez."
        errorText="A partner-statisztika betöltése sikertelen.">
        {view === 'donut'
          ? <DonutChart slices={slices} />
          : <ShareBarChart data={barData} />}
      </QueryState>
    </Card>
  );
}

// ⚠ 2026-08-24: a Tartozások fül a NYITOTT (ma is fennálló) tartozást mutatja —
// itt a "Mind" ALAPÉRTELMEZETT, nem "Aktuális év" (mint Partnereken/
// Kategóriákon): egy 2 évvel korábbi bizonylat máig kifizetetlen tartozása
// az "Aktuális év" preset alatt eltűnne a listáról, mert a bizonylat DÁTUMA
// nem esik az idei évre — miközben a tartozás MA IS fennáll. Gábor döntése:
// a szűrő legyen elérhető, de ne rejtsen el valós, nyitott tartozást alapból.
const BALANCE_PRESETS = [{ value: 'all', label: 'Mind' }, ...STANDARD_PRESETS];

function PartnerBalance() {
  const [dir, setDir] = useState('incoming');
  const [includeBaj, setIncludeBaj] = useState(true);
  const [preset, setPreset] = useState('all');
  const [custom, setCustom] = useState({});
  const range = periodToRange(preset, custom);
  const [groupBy, setGroupBy] = useStoredView('stats_partner_group_by', 'group');
  const balanceQ = useQuery({
    queryKey: ['partner-balance', dir, includeBaj, range, groupBy],
    queryFn: () => documentsApi.partnerBalance({ direction: dir,
      include_baj: dir === 'incoming' ? includeBaj : true, group_by: groupBy, ...range }),
  });
  const data = balanceQ.data ?? [];
  // Defenzív: a backend válasza lehet sima lista VAGY {items, totals} (aging-gel).
  const rows = Array.isArray(data) ? data : (data.items || []);
  const totalsApi = Array.isArray(data) ? null : (data.totals || null);
  const btnS = (on) => ({
    padding: '0.3rem 0.7rem', borderRadius: 'var(--radius)', fontSize: '0.78rem',
    border: `1px solid ${on ? 'var(--primary)' : 'var(--border)'}`,
    background: on ? 'rgba(59,130,246,0.15)' : 'transparent',
    color: on ? 'var(--primary)' : 'var(--text-muted)', cursor: 'pointer',
  });
  const totalOwed = rows.reduce((s, r) => s + r.owed_huf, 0);
  const today = new Date().toISOString().slice(0, 10);
  const fmtDate = (s) => (s ? String(s).slice(0, 10).replace(/-/g, '.') : '–');

  // ── Öregedés (aging) – csak akkor jelenik meg, ha a backend már adja ──
  const hasAging = rows.some(r => r.aging && typeof r.aging === 'object');
  const totals = totalsApi || (hasAging
    ? AGING_BUCKETS.reduce((acc, b) => {
        acc[b.key] = rows.reduce((s, r) => s + (r.aging?.[b.key] || 0), 0);
        return acc;
      }, {})
    : null);
  const agingRows = hasAging
    ? [...rows].filter(r => r.aging)
        .sort((a, b) => b.owed_huf - a.owed_huf)
        .slice(0, 8)
        .map(r => ({
          name: truncName(r.partner, 20), fullName: r.partner,
          not_due: r.aging?.not_due || 0,
          d0_30: r.aging?.d0_30 || 0, d31_60: r.aging?.d31_60 || 0,
          d61_90: r.aging?.d61_90 || 0, d90_plus: r.aging?.d90_plus || 0,
        }))
    : [];
  const title = dir === 'incoming' ? 'Amivel tartozunk (kifizetetlen)'
                                   : 'Amivel tartoznak (kiegyenlítetlen)';
  return (
    <Card title={`${title} – összesen ${huFmt(totalOwed, 0)} Ft`}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button style={btnS(dir === 'incoming')} onClick={() => setDir('incoming')}>Szállítók felé</button>
        <button style={btnS(dir === 'outgoing')} onClick={() => setDir('outgoing')}>Vevők felénk</button>
      </div>
      {dir === 'incoming' && (
        <BajToggle includeBaj={includeBaj} setIncludeBaj={setIncludeBaj} btnS={btnS} />
      )}
      <GroupByToggle value={groupBy} onChange={setGroupBy} />
      <div style={{ marginBottom: 12 }}>
        <PeriodFilter preset={preset} onPresetChange={setPreset}
          custom={custom} onCustomChange={setCustom} presets={BALANCE_PRESETS} />
      </div>
      <QueryState query={balanceQ} empty={rows.length === 0}
        emptyText="Nincs nyitott tartozás. ✓"
        errorText="A partner-egyenlegek betöltése sikertelen.">
        <>
        {/* Öregedés-összesítő tile-sor (kockázati skála) */}
        {totals && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
            gap: 10, marginBottom: 14 }}>
            {AGING_BUCKETS.map(b => (
              <div key={b.key} style={{ background: 'var(--bg-surface)',
                border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                padding: '0.6rem 0.75rem' }}>
                <div style={{ fontSize: '0.66rem', color: 'var(--text-muted)',
                  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                  {b.label}
                </div>
                <div style={{ fontFamily: 'var(--mono)', fontVariantNumeric: 'tabular-nums',
                  fontWeight: 700, fontSize: '0.95rem', color: b.text }}>
                  {huFmt(totals[b.key] || 0, 0)} Ft
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Partnerenkénti öregedés (stacked) – top 8 tartozás szerint */}
        {agingRows.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <AgingBarChart data={agingRows} />
          </div>
        )}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead><tr>
            <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase' }}>Partner</th>
            <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase' }}>Tartozás</th>
            <th style={{ textAlign: 'center', padding: '0.4rem 0.5rem', color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase' }}>Tételek</th>
            <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase' }}>Legrégebbi határidő</th>
          </tr></thead>
          <tbody>
            {rows.map(r => {
              const overdue = r.oldest_due && r.oldest_due < today;
              return (
                <tr key={r.key ?? r.partner} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '0.4rem 0.5rem' }}>{r.partner}</td>
                  <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontFamily: 'var(--mono)', fontWeight: 600 }}>{huFmt(r.owed_huf, 0)} Ft</td>
                  <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>{r.count}</td>
                  <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontFamily: 'var(--mono)',
                    fontVariantNumeric: 'tabular-nums',
                    color: overdue ? 'var(--danger)' : 'var(--text-muted)' }}>
                    {fmtDate(r.oldest_due)}{overdue ? ' ⚠' : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </>
      </QueryState>
    </Card>
  );
}

function periodButtons() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const pad = n => String(n).padStart(2,'0');
  const lastDay = (yr, mo) => new Date(yr, mo+1, 0).getDate();
  const prev = new Date(y, m - 1, 1);
  const py = prev.getFullYear(), pm = prev.getMonth();
  return [
    { label: `${y}`, from:`${y}-01-01`, to:`${y}-12-31` },
    { label: `${y-1}`, from:`${y-1}-01-01`, to:`${y-1}-12-31` },
    { label: 'Q1', from:`${y}-01-01`, to:`${y}-03-31` },
    { label: 'Q2', from:`${y}-04-01`, to:`${y}-06-30` },
    { label: 'Q3', from:`${y}-07-01`, to:`${y}-09-30` },
    { label: 'Q4', from:`${y}-10-01`, to:`${y}-12-31` },
    { label: 'Aktuális hó', from:`${y}-${pad(m+1)}-01`, to:`${y}-${pad(m+1)}-${lastDay(y,m)}` },
    // K-8 (2026-09-27): januárban az előző hó a TAVALYI december — eddig `YYYY-00-01` ment ki,
    // és a backend szűrő nélkül a teljes időszakot adta.
    { label: 'Előző hó',    from:`${py}-${pad(pm+1)}-01`, to:`${py}-${pad(pm+1)}-${lastDay(py,pm)}` },
  ];
}

// ── Projekt-eredmény (F0-8) ────────────────────────────────────────────────
// A szerver `services/project_pnl.compute` az EGYETLEN számító (INV-12): a lábsor a szerver
// `totals`-a, a kliens NEM összegez (a régi float-összegzés ellenmintája). A tengely a
// teljesítési hónap (INV-11); a devizás tétel a bizonylat saját árfolyamán (INV-9).
const PNL_COLS = [
  { key: 'revenue',      label: 'Bevétel' },
  { key: 'direct_cost',  label: 'Közvetlen költség' },
  { key: 'payroll_cost', label: 'Bérköltség' },
  { key: 'profit',       label: 'Eredmény' },
];
const PNL_PAYROLL_TIP = 'A bérköltség a bérpapírok feldolgozásából kerül a projektekre — ez még nincs bekapcsolva.';

// „a 12-ből”, „a 20-ból”: a -ból/-ből a szám kiejtett UTOLSÓ tagjának hangrendjét követi
// (egy, kettő, négy, öt, hét, kilenc, tíz, negyven, ötven, hetven, kilencven, ezer → -ből;
// három, hat, nyolc, húsz, harminc, hatvan, nyolcvan, száz, millió → -ból).
const HU_EGYES = ['', 'ből', 'ből', 'ból', 'ből', 'ből', 'ból', 'ből', 'ból', 'ből'];
const HU_TIZES = ['', 'ből', 'ból', 'ból', 'ből', 'ből', 'ból', 'ből', 'ból', 'ből'];
function huElativus(n) {
  const k = Math.abs(Math.trunc(n));
  if (k === 0) return 'ból';                                   // nulla
  if (k % 10) return HU_EGYES[k % 10];
  if (k % 100) return HU_TIZES[(k % 100) / 10];
  if (k % 1000) return 'ból';                                  // …száz
  if (k % 1000000) return 'ből';                               // …ezer
  return 'ból';                                                // …millió
}

function pnlMoney(v) {
  if (v == null) return '–';
  const n = Number(v);
  return `${n < 0 ? '−' : ''}${huFmt(Math.abs(n), 0)} Ft`;
}

function ProjectPnl() {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const [preset, setPreset] = useState('this-year');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const params = presetToMonths(preset, custom);
  const ready = !!(params.period_from && params.period_to);
  const badCustom = preset === 'custom' && ready && params.period_from > params.period_to;

  const pnlQ = useQuery({
    queryKey: ['pnl', params],
    queryFn: () => projectsApi.pnl(params),
    enabled: ready && !badCustom,
    placeholderData: (prev) => prev,          // váltáskor a régi adat halványítva marad
  });
  const projQ = useQuery({ queryKey: ['projects'], queryFn: projectsApi.list });
  // INV-37: a pótlás HIBÁJA inline és tartós (`role="alert"` az Adatminőség-sávban), toast
  // csak sikerre — és a siker-szöveg a szerver eredményét mondja (`updated` / `failed`).
  const [backfillError, setBackfillError] = useState(null);
  const backfill = useMutation({
    mutationFn: documentsApi.backfillAmountHuf,
    onMutate: () => setBackfillError(null),
    onSuccess: (res) => {
      const updated = res?.updated ?? 0;
      const failed = (res?.failed ?? []).length;
      if (updated === 0 && failed > 0) {
        setBackfillError(`A forint-érték egyik bizonylatnál sem pótolható (${failed} sikertelen) — az árfolyam nem érhető el.`);
      } else {
        toast.success(failed > 0
          ? `${updated} bizonylat forint-értéke pótolva, ${failed} nem sikerült.`
          : `${updated} bizonylat forint-értéke pótolva.`);
      }
      pnlQ.refetch();
    },
    onError: (e) => setBackfillError(e?.response?.status === 403
      ? (e.response.data?.detail?.message || 'Csak cég-adminisztrátor módosíthatja.')
      : 'A forint-érték pótlása sikertelen. Próbáld újra később.'),
  });

  const d = pnlQ.data;
  const projects = d?.projects ?? [];
  const partial = d?.partial ?? {};
  const periodLbl = ready
    ? (params.period_from === params.period_to ? fmtMonthHu(params.period_from)
      : `${fmtMonthHu(params.period_from)} – ${fmtMonthHu(params.period_to)}`)
    : '';
  const noProjects = projQ.isSuccess && (projQ.data ?? []).length === 0;
  const nothingAllocated = !!d && projects.length > 0 && projects.every(p => !p.doc_count);

  // ── diagram: az Általános NÉLKÜL, a legnagyobb bevételű 8 projekt ──
  const MAX_BARS = 8;
  const revCostData = [...projects].sort((x, y) => (y.revenue || 0) - (x.revenue || 0))
    .slice(0, MAX_BARS).map(r => ({ name: truncName(r.name, 18), fullName: r.name,
      revenue: r.revenue || 0, cost: r.direct_cost || 0 }));
  const profitData = [...projects].sort((x, y) => (y.profit || 0) - (x.profit || 0))
    .slice(0, MAX_BARS).map(r => ({ name: truncName(r.name, 18), fullName: r.name, profit: r.profit || 0 }));
  const chartTitleS = { fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)',
    textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px' };

  const thS = { padding:'0.45rem 0.75rem', fontSize:'0.68rem', fontWeight:600,
    color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em',
    borderBottom:'1px solid var(--border)', textAlign:'left' };
  const tdS = { padding:'0.55rem 0.75rem', fontSize:'0.82rem',
    borderBottom:'1px solid var(--border-dim)', verticalAlign:'middle' };
  const numS = { ...tdS, textAlign:'right', fontFamily:'var(--mono)', fontVariantNumeric:'tabular-nums' };
  const chipS = { display:'inline-flex', alignItems:'center', gap:8, flexWrap:'wrap',
    background:'var(--warning-subtle)', border:'1px solid var(--border)', borderRadius:'var(--radius)',
    padding:'0.4rem 0.7rem', fontSize:'0.78rem', color:'var(--text)' };

  const cell = (row, key, strong) => {
    if (key === 'payroll_cost') {
      return (
        <td key={key} title={PNL_PAYROLL_TIP}
          style={{...numS, color:'var(--text-muted)', fontFamily:'inherit', fontSize:'0.74rem'}}>
          {row.payroll_cost == null ? 'nincs bérpapír-feldolgozás' : pnlMoney(row.payroll_cost)}
        </td>
      );
    }
    const v = row[key];
    const neg = Number(v) < 0;
    return (
      <td key={key} style={{...numS, fontWeight: strong || key === 'profit' ? 700 : 400,
        // a szín csak kiegészítés — az előjel (−) hordozza az információt (U4)
        color: neg ? 'var(--danger)' : 'var(--text)'}}>
        {pnlMoney(v)}
      </td>
    );
  };

  const caption = `Projekt-eredmény, ${periodLbl}, teljesítési hónap szerint`;

  return (
    <Card title="Projektek">
      {/* Belső nézet-váltó (az F0-ban egyetlen nézet; a „Munkabér allokáció” az F4) */}
      <div aria-label="Projekt-nézet" style={{display:'flex',gap:6,marginBottom:12}}>
        <Pill active>Eredmény</Pill>
      </div>

      <div style={{marginBottom:12}}>
        <PeriodFilter preset={preset} onPresetChange={setPreset} custom={custom}
          onCustomChange={setCustom} presets={PNL_PRESETS} monthPicker />
        {badCustom && (
          <div style={{marginTop:6,fontSize:'0.76rem',color:'var(--danger)'}}>
            A kezdő hónap nem lehet a záró hónap után.
          </div>
        )}
      </div>

      {noProjects ? (
        <div style={{textAlign:'center',color:'var(--text-muted)',fontSize:'0.84rem',padding:'2rem 1rem'}}>
          Még nincs projekt. Projektet a Beállítások › Projektek oldalon vehetsz fel.
          <div style={{marginTop:10}}>
            <a href="/settings/projects" style={{color:'var(--primary)'}}>Projektek kezelése →</a>
          </div>
        </div>
      ) : (!ready || badCustom) ? (
        <div style={{color:'var(--text-muted)',fontSize:'0.82rem',padding:'1rem'}}>
          Válaszd ki a kezdő és a záró hónapot.
        </div>
      ) : (
        <QueryState query={pnlQ} errorText="A projekt-eredmény betöltése sikertelen.">
          {() => d && (
            <div style={{opacity: pnlQ.isPlaceholderData ? 0.55 : 1, transition:'opacity 0.15s'}}>
              {/* Adatminőség — figyelmeztetés, nem hiba (nem piros) */}
              {(partial.missing_huf > 0 || partial.missing_period > 0 || partial.missing_net > 0 || d.payroll_status === 'not_processed') && (
                <div aria-label="Adatminőség" style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:12}}>
                  {partial.missing_huf > 0 && (
                    <span style={chipS}>
                      <span aria-hidden="true">⚠</span>
                      {partial.missing_huf} bizonylat nincs az összegben — hiányzik a forint-értéke
                      {isAdmin && (
                        <Btn size="sm" variant="secondary" disabled={backfill.isPending}
                          onClick={() => backfill.mutate()}>
                          {backfill.isPending ? 'Pótlás…' : 'Forint-érték pótlása'}
                        </Btn>
                      )}
                    </span>
                  )}
                  {partial.missing_net > 0 && (
                    <span style={chipS}><span aria-hidden="true">⚠</span>
                      {partial.missing_net} bizonylatnak nincs nettó összege — nincs az összegben
                    </span>
                  )}
                  {partial.missing_period > 0 && (
                    <span style={chipS}><span aria-hidden="true">⚠</span>
                      {partial.missing_period} bizonylatnak nincs teljesítési hónapja — nincs az összegben
                    </span>
                  )}
                  {d.payroll_status === 'not_processed' && (
                    <span style={chipS}><span aria-hidden="true">⚠</span>
                      A bérköltség üres — a bérpapírok feldolgozása nincs bekapcsolva.
                    </span>
                  )}
                </div>
              )}
              {backfillError && (
                <div role="alert" style={{marginBottom:12,padding:'0.5rem 0.75rem',fontSize:'0.8rem',
                  border:'1px solid var(--danger)',borderRadius:6,color:'var(--danger)'}}>
                  {backfillError}
                </div>
              )}

              {nothingAllocated && (
                <div style={{color:'var(--text-muted)',fontSize:'0.8rem',marginBottom:10}}>
                  Ebben az időszakban nincs projekthez sorolt tétel. Minden bevétel és költség az Általános sorban van.
                </div>
              )}

              {projects.length > 0 && !nothingAllocated && (
                <div aria-hidden="true" style={{ display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 20, marginBottom: 16 }}>
                  <div>
                    <h4 style={chartTitleS}>Bevétel vs közvetlen költség projektenként</h4>
                    <PnlGroupedChart data={revCostData} />
                    {projects.length > MAX_BARS && (
                      <div style={{fontSize:'0.72rem',color:'var(--text-muted)',marginTop:4}}>
                        A {MAX_BARS} legnagyobb bevételű projekt a {projects.length}-{huElativus(projects.length)} — a teljes lista a táblázatban.
                      </div>
                    )}
                  </div>
                  <div>
                    <h4 style={chartTitleS}>Eredmény</h4>
                    <PnlProfitChart data={profitData} />
                  </div>
                </div>
              )}

              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse'}}>
                  <caption style={{textAlign:'left',captionSide:'top',padding:'0 0 0.5rem',
                    color:'var(--text-muted)',fontSize:'0.74rem'}}>{caption}</caption>
                  <thead>
                    <tr>
                      <th scope="col" style={thS}>Projekt</th>
                      <th scope="col" style={thS}>Ügyfél</th>
                      {PNL_COLS.map(c => (
                        <th key={c.key} scope="col" style={{...thS,textAlign:'right'}}>{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map(r => (
                      <tr key={r.id}>
                        <th scope="row" style={{...tdS,fontWeight:600,textAlign:'left'}}>
                          {r.name}
                          {r.is_active === false && (
                            <span style={{marginLeft:6}}><Badge>archivált</Badge></span>
                          )}
                        </th>
                        <td style={{...tdS,color:'var(--text-muted)'}}>{r.client_name||'–'}</td>
                        {PNL_COLS.map(c => cell(r, c.key))}
                      </tr>
                    ))}
                    <tr style={{borderTop:'2px solid var(--border)'}}>
                      <th scope="row" colSpan={2} style={{...tdS,fontWeight:600,textAlign:'left',
                        color:'var(--text-muted)'}}>Általános (projekthez nem sorolt)</th>
                      {PNL_COLS.map(c => cell(d.general || {}, c.key))}
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr style={{borderTop:'2px solid var(--border)'}}>
                      <th scope="row" colSpan={2} style={{...tdS,fontWeight:700,textAlign:'left'}}>
                        Összesen — a cég egésze</th>
                      {PNL_COLS.map(c => cell(d.totals || {}, c.key, true))}
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div style={{marginTop:10,fontSize:'0.74rem',color:'var(--text-muted)',lineHeight:1.6}}>
                <div>
                  Teljesítési hónap szerint · {periodLbl} · {d.totals?.doc_count ?? 0} bizonylat ·
                  Devizás tételek a bizonylat saját árfolyamán
                </div>
                {d.last_modified?.at && (
                  <div>Az időszak számai utoljára {fmtDateTimeHu(d.last_modified.at, { dateOnly: true })} módosultak.</div>
                )}
              </div>
            </div>
          )}
        </QueryState>
      )}
    </Card>
  );
}


// ── Bankköltség statisztika ───────────────────────────────────────────────

const FEE_TYPE_LBL = {
  monthly: 'Havi díj',     transaction: 'Tranzakciós',  giro: 'GIRO jutalék',
  fx: 'FX díj',            custody: 'Letétkezelés',      card: 'Kártya díj',
  other: 'Egyéb',
};
// Díjtípus-stabil színek (a validált kategorikus palettából, fix hozzárendelés
// – sosem rang szerint; az „Egyéb" semleges szürke).
const FEE_TYPE_COLORS = {
  monthly: '#3987e5', transaction: '#199e70', giro: '#c98500', fx: '#d95926',
  custody: '#9085e9', card: '#d55181', other: OTHER_COLOR,
};

function BankFeesStats() {
  const btns = periodButtons();
  const [active, setActive] = useState(0);
  const [custom, setCustom] = useState({ from:'', to:'' });
  const [useCustom, setUseCustom] = useState(false);
  const [accountId, setAccountId] = useState('');

  const { data: accounts = [] } = useQuery({
    queryKey: ['statement-accounts'],
    queryFn: statementsApi.accounts,
  });

  const params = {
    ...(useCustom
      ? { date_from: custom.from || undefined, date_to: custom.to || undefined }
      : { date_from: btns[active].from, date_to: btns[active].to }),
    ...(accountId ? { account_id: accountId } : {}),
  };
  const feesQ = useQuery({
    queryKey: ['bank-fees-summary', params],
    queryFn: () => statementsApi.bankFeesSummary(params),
  });
  const { data } = feesQ;

  // Periódus címke
  const periodLabel = useCustom
    ? `${custom.from || '…'} … ${custom.to || '…'}`
    : `${btns[active].label} (${btns[active].from} … ${btns[active].to})`;

  const currencies = (data?.total || []).map(t => t.currency);
  const [curTab, setCurTab] = useState(null);
  const activeCur = curTab || currencies[0] || 'HUF';

  // ── Havi grafikon adat-előkészítés (stack-nézet: bank / díjtípus) ──
  const [feeView, setFeeView] = useStoredView('stats_fee_stack', 'bank');
  // Bank szerinti stack: a monthly sorok {month, currency, bank_name, amount}
  // alakúak – az aktív devizára szűrve hónap×bank pivot. A sorrend a teljes
  // válasz `banks` listáját követi (legend-konzisztencia deviza-szűrésnél is).
  const feeMonthly = (data?.monthly || []).filter(m => m.currency === activeCur);
  const feeMonths = [...new Set(feeMonthly.map(d => d.month))].sort();
  const feeBanks = [...new Set(feeMonthly.map(d => d.bank_name))]
    .sort((a, b) => ((data?.banks || []).indexOf(a)) - ((data?.banks || []).indexOf(b)));
  // Szanitizált dataKey-ek (a banknévben pont/speciális karakter lehet).
  const feeSeries = feeBanks.map((b, i) => ({ key: `b${i}`, name: b, color: bankColor(b) }));
  const feeLookup = {};
  feeMonthly.forEach(d => {
    const k = `${d.month}|${d.bank_name}`;
    feeLookup[k] = (feeLookup[k] || 0) + (d.amount || 0);
  });
  const feeRows = feeMonths.map(m => {
    const row = { month: m };
    feeBanks.forEach((b, i) => { row[`b${i}`] = feeLookup[`${m}|${b}`] || 0; });
    return row;
  });
  // Díjtípus-nézet: a monthly sorok NEM hordoznak fee_type-ot, ezért ez az
  // időszaki by_fee_type összesítés rendezett sávdiagramja (nem havi bontás).
  const feeTypeRows = (data?.by_fee_type || []).filter(r => r.currency === activeCur)
    .map(r => ({ name: FEE_TYPE_LBL[r.fee_type] || r.fee_type, value: r.amount,
      count: r.count, color: FEE_TYPE_COLORS[r.fee_type] || OTHER_COLOR }))
    .sort((a, b) => b.value - a.value);

  return (
    <div>
      {/* ── Filter ─────────────────────────────────── */}
      <Card style={{marginBottom:'0.75rem'}}>
        <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:8}}>
          {btns.map((b, i) => (
            <button key={i}
              onClick={()=>{setActive(i);setUseCustom(false);}}
              style={{
                background: (!useCustom && active===i) ? 'var(--tab-active-bg)' : 'transparent',
                color: (!useCustom && active===i) ? 'var(--tab-active-fg)' : 'var(--text-muted)',
                border:'1px solid '+ ((!useCustom && active===i) ? 'var(--tab-active-bg)' : 'var(--border)'),
                borderRadius:'var(--radius)',padding:'0.3rem 0.65rem',
                fontSize:'0.74rem',cursor:'pointer',
                fontWeight: (!useCustom && active===i) ? 600 : 400,
              }}>{b.label}</button>
          ))}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr auto',gap:8,alignItems:'end'}}>
          <Input label="Egyedi időszak – tól" type="date" value={custom.from}
            onChange={e=>{setCustom(c=>({...c,from:e.target.value}));setUseCustom(true);}}/>
          <Input label="Ig" type="date" value={custom.to}
            onChange={e=>{setCustom(c=>({...c,to:e.target.value}));setUseCustom(true);}}/>
          <Select label="Bank" value={accountId}
            onChange={e=>setAccountId(e.target.value)}
            options={[{value:'',label:'– Minden bank –'},
              ...accounts.map(a=>({value:a.id,label:`${a.bank_name || '?'} · ${a.currency} · …${(a.iban||'').slice(-6)}`}))]}/>
          <div style={{fontSize:'0.7rem',color:'var(--text-muted)',paddingBottom:8}}>
            {periodLabel}
          </div>
        </div>
      </Card>

      <QueryState query={feesQ} empty={!data?.total?.length}
        emptyText="Nincs bankköltség a kijelölt időszakban."
        errorText="A bankköltség-összesítő betöltése sikertelen.">
        {() => (
        <>
          {/* ── Total kártyák ──────────────────────────── */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))',gap:'0.75rem',marginBottom:'0.75rem'}}>
            {data.total.map(t => {
              const c = currencyColor(t.currency);
              return (
                <Card key={t.currency} style={{borderLeft:`4px solid ${c}`}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                    <Banknote size={14} color={c}/>
                    <span style={{fontSize:'0.7rem',color:'var(--text-muted)',
                      textTransform:'uppercase',letterSpacing:'0.06em',fontWeight:600}}>
                      Összes bankköltség
                    </span>
                    <CurrencyBadge cur={t.currency}/>
                  </div>
                  <div style={{fontFamily:'var(--mono)',fontSize:'1.5rem',fontWeight:700,color:c}}>
                    {huFmt(t.amount, t.currency==='HUF'?0:2)} {t.currency}
                  </div>
                  <div style={{fontSize:'0.72rem',color:'var(--text-muted)',marginTop:2}}>
                    {t.count} tétel
                  </div>
                </Card>
              );
            })}
          </div>

          {/* ── B8 (2026-09-06): implikált árfolyamrés — a bank nem díjként mutatja, de költség ── */}
          {data.fx_spread?.count > 0 && (
            <Card style={{borderLeft:'4px solid var(--warning, #d97706)',marginBottom:'0.75rem'}}>
              <div style={{display:'flex',flexWrap:'wrap',alignItems:'baseline',gap:'0.75rem 1.5rem'}}>
                <div>
                  <div style={{fontSize:'0.7rem',color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.06em',fontWeight:600,marginBottom:4}}>
                    Implikált árfolyamrés (rejtett költség)
                  </div>
                  <div style={{fontFamily:'var(--mono)',fontSize:'1.5rem',fontWeight:700,color:'var(--warning, #d97706)'}}>
                    {huFmt(data.fx_spread.total_huf, 0)} HUF
                  </div>
                  <div style={{fontSize:'0.72rem',color:'var(--text-muted)',marginTop:2}}>
                    {data.fx_spread.count} devizaváltás · a bank árfolyama és az MNB-közép különbsége — a kivonaton nem díjként szerepel
                  </div>
                </div>
                <div style={{fontSize:'0.74rem',color:'var(--text-muted)'}}>
                  Összes banki költség a résre is számítva:{' '}
                  <b style={{fontFamily:'var(--mono)'}}>
                    {huFmt((data.total.find(t => t.currency === 'HUF')?.amount || 0) + data.fx_spread.total_huf, 0)} HUF
                  </b>
                </div>
              </div>
            </Card>
          )}

          {/* ── Deviza tab + havi grafikon ─────────────── */}
          {currencies.length > 1 && (
            <div style={{display:'flex',gap:6,marginBottom:'0.5rem'}}>
              {currencies.map(c => {
                const cc = currencyColor(c);
                const active = activeCur === c;
                return (
                  <button key={c}
                    onClick={()=>setCurTab(c)}
                    style={{
                      background: active ? cc + '22' : 'transparent',
                      color: active ? cc : 'var(--text-muted)',
                      border:'1px solid '+ (active ? cc : 'var(--border)'),
                      borderRadius:'var(--radius)',padding:'0.3rem 0.75rem',
                      fontSize:'0.74rem',cursor:'pointer',fontWeight: active ? 700 : 400,
                      letterSpacing:'0.04em',fontFamily:'var(--mono)',
                    }}>{c}</button>
                );
              })}
            </div>
          )}
          <Card title={feeView === 'feetype' ? 'Bankköltség díjtípus szerint' : 'Havi bankköltség'}
            subtitle={feeView === 'feetype'
              ? 'Időszaki összesítés – a kivonat-összesítő díjtípusonként nem ad havi bontást'
              : undefined}
            actions={<ViewSwitch value={feeView} onChange={setFeeView}
              options={[{ value: 'bank', label: 'Bank szerint' },
                        { value: 'feetype', label: 'Díjtípus szerint' }]} />}
            style={{marginBottom:'0.75rem',
            borderTop:`3px solid ${currencyColor(activeCur)}`}}>
            {feeView === 'feetype' ? (
              <FeeTypeTotalsChart data={feeTypeRows} currency={activeCur} />
            ) : (
              <>
                <StackedMonthlyChart rows={feeRows} series={feeSeries}
                  currency={activeCur} height={240}
                  emptyText="Nincs adat erre az időszakra." />
                {/* Bank-legend (entitás-stabil BankDot színekkel) */}
                {feeBanks.length > 0 && (
                  <div style={{display:'flex',flexWrap:'wrap',gap:'0.75rem',
                    marginTop:'0.5rem',paddingLeft:'0.5rem'}}>
                    {feeBanks.map(b => (
                      <div key={b} style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:'0.74rem'}}>
                        <BankDot name={b} size={10}/>
                        <span>{b}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </Card>

          {/* ── Bank szerint + típus szerint táblázatok ───── */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem',marginBottom:'0.75rem'}}>
            <Card title="Bank szerinti bontás" noPad>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.8rem'}}>
                <thead>
                  <tr style={{borderBottom:'1px solid var(--border)'}}>
                    <th style={{padding:'0.5rem 0.75rem',textAlign:'left',fontSize:'0.7rem',
                      color:'var(--text-muted)',textTransform:'uppercase',fontWeight:600}}>Bank</th>
                    <th style={{padding:'0.5rem 0.5rem',textAlign:'center',fontSize:'0.7rem',
                      color:'var(--text-muted)',textTransform:'uppercase',fontWeight:600}}>Deviza</th>
                    <th style={{padding:'0.5rem 0.5rem',textAlign:'right',fontSize:'0.7rem',
                      color:'var(--text-muted)',textTransform:'uppercase',fontWeight:600}}>Tétel</th>
                    <th style={{padding:'0.5rem 0.75rem',textAlign:'right',fontSize:'0.7rem',
                      color:'var(--text-muted)',textTransform:'uppercase',fontWeight:600}}>Összeg</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.by_bank || []).map((r, i) => (
                    <tr key={i} style={{borderBottom:'1px solid var(--border-dim)',
                      borderLeft:`3px solid ${bankColor(r.bank_name)}`}}>
                      <td style={{padding:'0.45rem 0.75rem'}}>
                        <span style={{display:'inline-flex',alignItems:'center',gap:8}}>
                          <BankDot name={r.bank_name} size={10}/>
                          <span>{r.bank_name}</span>
                        </span>
                      </td>
                      <td style={{padding:'0.45rem 0.5rem',textAlign:'center'}}>
                        <CurrencyBadge cur={r.currency}/>
                      </td>
                      <td style={{padding:'0.45rem 0.5rem',textAlign:'right',fontFamily:'var(--mono)'}}>{r.count}</td>
                      <td style={{padding:'0.45rem 0.75rem',textAlign:'right',fontFamily:'var(--mono)',
                        fontWeight:600,color:currencyColor(r.currency)}}>
                        {huFmt(r.amount, r.currency==='HUF'?0:2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
            <Card title="Típus szerinti bontás" noPad>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.8rem'}}>
                <thead>
                  <tr style={{borderBottom:'1px solid var(--border)'}}>
                    <th style={{padding:'0.5rem 0.75rem',textAlign:'left',fontSize:'0.7rem',
                      color:'var(--text-muted)',textTransform:'uppercase',fontWeight:600}}>Típus</th>
                    <th style={{padding:'0.5rem 0.5rem',textAlign:'center',fontSize:'0.7rem',
                      color:'var(--text-muted)',textTransform:'uppercase',fontWeight:600}}>Deviza</th>
                    <th style={{padding:'0.5rem 0.5rem',textAlign:'right',fontSize:'0.7rem',
                      color:'var(--text-muted)',textTransform:'uppercase',fontWeight:600}}>Tétel</th>
                    <th style={{padding:'0.5rem 0.75rem',textAlign:'right',fontSize:'0.7rem',
                      color:'var(--text-muted)',textTransform:'uppercase',fontWeight:600}}>Összeg</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.by_fee_type || []).map((r, i) => (
                    <tr key={i} style={{borderBottom:'1px solid var(--border-dim)'}}>
                      <td style={{padding:'0.45rem 0.75rem'}}>
                        {FEE_TYPE_LBL[r.fee_type] || r.fee_type}
                      </td>
                      <td style={{padding:'0.45rem 0.5rem',textAlign:'center'}}>
                        <CurrencyBadge cur={r.currency}/>
                      </td>
                      <td style={{padding:'0.45rem 0.5rem',textAlign:'right',fontFamily:'var(--mono)'}}>{r.count}</td>
                      <td style={{padding:'0.45rem 0.75rem',textAlign:'right',fontFamily:'var(--mono)',
                        fontWeight:600,color:currencyColor(r.currency)}}>
                        {huFmt(r.amount, r.currency==='HUF'?0:2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>

          {/* ── Top tételek ──────────────────────────── */}
          <Card title={`Legutóbbi tételek (${(data.recent || []).length})`} noPad>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.78rem'}}>
              <thead>
                <tr style={{borderBottom:'1px solid var(--border)'}}>
                  <th style={{padding:'0.5rem 0.65rem',textAlign:'left',fontSize:'0.68rem',
                    color:'var(--text-muted)',textTransform:'uppercase',fontWeight:600}}>Dátum</th>
                  <th style={{padding:'0.5rem 0.65rem',textAlign:'left',fontSize:'0.68rem',
                    color:'var(--text-muted)',textTransform:'uppercase',fontWeight:600}}>Bank</th>
                  <th style={{padding:'0.5rem 0.65rem',textAlign:'left',fontSize:'0.68rem',
                    color:'var(--text-muted)',textTransform:'uppercase',fontWeight:600}}>Típus</th>
                  <th style={{padding:'0.5rem 0.65rem',textAlign:'left',fontSize:'0.68rem',
                    color:'var(--text-muted)',textTransform:'uppercase',fontWeight:600}}>Közlemény</th>
                  <th style={{padding:'0.5rem 0.65rem',textAlign:'right',fontSize:'0.68rem',
                    color:'var(--text-muted)',textTransform:'uppercase',fontWeight:600}}>Összeg</th>
                </tr>
              </thead>
              <tbody>
                {(data.recent || []).map(r => (
                  <tr key={r.id} style={{borderBottom:'1px solid var(--border-dim)'}}>
                    <td style={{padding:'0.4rem 0.65rem',fontFamily:'var(--mono)',whiteSpace:'nowrap'}}>{r.tx_date}</td>
                    <td style={{padding:'0.4rem 0.65rem'}}>
                      <span style={{display:'inline-flex',alignItems:'center',gap:6}}>
                        <BankDot name={r.bank_name} size={8}/>
                        {r.bank_name || '–'}
                      </span>
                    </td>
                    <td style={{padding:'0.4rem 0.65rem',color:'#f59e0b',fontWeight:600,fontSize:'0.72rem'}}>
                      {FEE_TYPE_LBL[r.fee_type] || r.fee_type || '–'}
                    </td>
                    <td style={{padding:'0.4rem 0.65rem',maxWidth:380,
                      overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',
                      color:'var(--text-muted)',fontSize:'0.72rem'}}>
                      {r.reference || '–'}
                    </td>
                    <td style={{padding:'0.4rem 0.65rem',textAlign:'right',
                      fontFamily:'var(--mono)',fontWeight:600,whiteSpace:'nowrap'}}>
                      <span style={{color:currencyColor(r.currency)}}>
                        {huFmt(r.amount, r.currency==='HUF'?0:2)}
                      </span>
                      <span style={{marginLeft:6}}>
                        <CurrencyBadge cur={r.currency}/>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
        )}
      </QueryState>
    </div>
  );
}


// ── Accountant ─────────────────────────────────────────────────────────────

const HU_MONTH_NAMES = ['','január','február','március','április','május','június',
  'július','augusztus','szeptember','október','november','december'];

function lastClosedMonth() {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth()-1);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
}
function lastClosedQuarter() {
  const d = new Date();
  let y = d.getFullYear(), q = Math.floor(d.getMonth()/3);  // folyó negyedév (0-alapú)
  if (q === 0) { y -= 1; q = 4; }
  return `${y}-Q${q}`;
}
function monthOptionsList(n=18) {
  const out = []; const d = new Date(); d.setDate(1);
  for (let i=0; i<n; i++) {
    d.setMonth(d.getMonth()-1);
    const v = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    out.push({ value:v, label:`${d.getFullYear()}. ${HU_MONTH_NAMES[d.getMonth()+1]}` });
  }
  return out;
}
function quarterOptionsList(n=8) {
  const out = []; const d = new Date();
  let y = d.getFullYear(), q = Math.floor(d.getMonth()/3);  // folyó (kihagyjuk)
  for (let i=0; i<n; i++) {
    q -= 1; if (q < 0) { q = 3; y -= 1; }
    out.push({ value:`${y}-Q${q+1}`, label:`${y} Q${q+1}` });
  }
  return out;
}

function StatusDot({ ok }) {
  return <span style={{display:'inline-block',width:9,height:9,borderRadius:99,
    background: ok ? 'var(--accent)' : 'var(--danger)', marginRight:6}}/>;
}

// Kivonat PDF feltöltés egy cellához (meglévő statements pipeline: AI parse + auto-match)
function StmtCellUpload({ small }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const onFile = async (f) => {
    if (!f || busy) return;
    setBusy(true); setErr(null);
    try {
      await statementsApi.upload(f);
      qc.invalidateQueries({ queryKey: ['accountant-check'] });
      qc.invalidateQueries({ queryKey: ['statements'] });
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === 'string' ? d : 'hiba');
    }
    setBusy(false);
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <RowDropUpload label={small ? '' : 'Feltölt'} loading={busy}
        accept="application/pdf" onFile={onFile}/>
      {err && <span style={{ fontSize: '0.66rem', color: 'var(--danger)' }}>{err}</span>}
    </span>
  );
}

// Kattintható ÉS drop-célpont feltöltő gomb (táblázat-sorokba)
function RowDropUpload({ onFile, loading, label, accept = 'application/pdf,image/*' }) {
  const fileRef = useRef(null);
  const [drag, setDrag] = useState(false);
  return (
    <span
      onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={e => {
        e.preventDefault(); e.stopPropagation(); setDrag(false);
        const f = filesFromDataTransfer(e.dataTransfer)[0];
        if (f) onFile(f);
      }}
      style={{display:'inline-block', borderRadius:'var(--radius)',
        outline: drag ? '2px dashed var(--primary)' : 'none', outlineOffset: 2}}>
      <input ref={fileRef} type="file" accept={accept} style={{display:'none'}}
        onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }}/>
      <Btn size="sm" variant="secondary" icon={Upload} loading={loading}
        title="Kattints, vagy húzd ide a fájlt"
        onClick={() => fileRef.current?.click()}>
        {label}
      </Btn>
    </span>
  );
}

function MissingPdfRow({ doc, onBillingo, billingoPending, onUpload, uploadPending }) {
  return (
    <tr>
      <td style={{padding:'0.4rem 0.6rem',fontSize:'0.8rem'}}>
        <Badge variant={doc.direction==='outgoing'?'outgoing':'incoming'}>
          {doc.direction==='outgoing'?'KIMENŐ':'BEJÖVŐ'}
        </Badge>
      </td>
      <td style={{padding:'0.4rem 0.6rem',fontSize:'0.82rem',fontWeight:600}}>
        <span title={`Bizonylat #${doc.id}${doc.supplier_tax_number ? ' · adószám: '+doc.supplier_tax_number : ''}`}>
          {doc.supplier_name || doc.customer_name || '–'}
        </span>
        {['proforma','előleg','eloleg'].includes((doc.doc_type||'').toLowerCase()) && (
          <span title="Nem adóügyi bizonylat (díjbekérő / előlegbekérő)"
            style={{marginLeft:6,fontSize:'0.6rem',fontWeight:700,padding:'1px 5px',
              borderRadius:8,color:'#a855f7',border:'1px solid #a855f7',background:'#a855f718'}}>
            {(doc.doc_type||'').toLowerCase()==='proforma'?'Proforma':'Előleg'}
          </span>
        )}
      </td>
      <td style={{padding:'0.4rem 0.6rem',fontSize:'0.8rem',fontFamily:'var(--mono)'}}>{doc.invoice_number||'–'}</td>
      <td style={{padding:'0.4rem 0.6rem',fontSize:'0.8rem',fontFamily:'var(--mono)'}}>{(doc.issue_date||'').slice(0,10)||'–'}</td>
      <td style={{padding:'0.4rem 0.6rem',fontSize:'0.8rem',fontFamily:'var(--mono)',textAlign:'right'}}>
        {doc.gross_amount!=null ? `${huFmt(doc.gross_amount, doc.currency==='HUF'?0:2)} ${doc.currency}` : '–'}
      </td>
      <td style={{padding:'0.4rem 0.6rem',textAlign:'right'}}>
        {doc.can_fetch_billingo ? (
          <Btn size="sm" variant="secondary" loading={billingoPending}
            onClick={()=>onBillingo(doc.id)}>Billingo PDF</Btn>
        ) : (
          <RowDropUpload label="PDF feltöltés" loading={uploadPending}
            onFile={(f)=>onUpload(doc.id, f)}/>
        )}
      </td>
    </tr>
  );
}

const PKG_SEND_CANCELLED = 'pkg-send-cancelled';

export function Accountant() {
  const qc = useQueryClient();
  const { requireFreshStepUp } = useStepUp();
  const posthog = usePostHog();
  const [mode, setMode] = useState('month');
  const [period, setPeriod] = useState(lastClosedMonth());
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sendResult, setSendResult] = useState(null);
  const [sendError, setSendError] = useState(null);
  const [includePaid, setIncludePaid] = useState(false);
  const [confirm, setConfirm] = useState(null); // {title, message, danger, confirmLabel, action}

  const checkQ = useQuery({
    queryKey: ['accountant-check', period],
    queryFn: () => accountantApi.check(period),
  });
  const { data: check, isFetching } = checkQ;
  const { data: history = [] } = useQuery({
    queryKey: ['accountant-history'],
    queryFn: accountantApi.history,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey:['accountant-check'] });

  const waiverMut    = useMutation({ mutationFn: accountantApi.createWaiver, onSuccess: invalidate });
  const delWaiverMut = useMutation({ mutationFn: accountantApi.deleteWaiver, onSuccess: invalidate });
  const billingoMut  = useMutation({ mutationFn: accountantApi.fetchBillingoPdf, onSuccess: invalidate });
  // M11/M8: ha nincs bekötött számlázó program, a kimenő számlák PDF-je nem pótolható
  // automatikusan — ezt a hiányzó ASSET-re mutató, linkes figyelmeztetéssel mondjuk ki.
  const { data: invoicingAssets } = useQuery({
    queryKey: ['company-assets', 'invoicing'],
    queryFn: () => companyAssetsApi.list('invoicing'),
  });
  const noInvoicingAsset = !!invoicingAssets && (invoicingAssets.assets || []).filter(a => !a.disabled_at).length === 0;
  const [rowUploadError, setRowUploadError] = useState(null);
  const uploadMut    = useMutation({
    mutationFn: ({id, file}) => documentsApi.attachPdf(id, file),
    onSuccess: () => { setRowUploadError(null); invalidate(); },
    onError: (e) => {
      const d = e?.response?.data?.detail;
      setRowUploadError(typeof d === 'string' ? d : (e.message || 'Feltöltési hiba'));
    },
  });
  const bulk = useBulkUpload(period);
  // ⚠ 2026-09-26 (tulajdonosi döntés): a csomagküldés FRISS, egyszeri passkey-jegyet kér
  //   (`FRESH_ACTION_ENDPOINTS`); a ceremónia PONTOSAN ezt a törzset jelenti be, és a
  //   `sendFresh` pontosan ezt küldi (`freshRequest`, egy JSON-kör) — eltérés = 403.
  const sendMut = useMutation({
    mutationFn: async (force) => {
      const req = freshRequest('POST /accountant/send-package', { body: {
        period,
        subject: subject.trim() || null,
        body: body.trim() ? body.trim().replace(/\n/g,'<br>') : null,
        force,
        include_paid_in_month: includePaid,
      } });
      if (!await requireFreshStepUp('A könyvelői csomag küldése előtt', [req])) {
        throw new Error(PKG_SEND_CANCELLED);
      }
      return sendFresh(req);
    },
    onSuccess: data => {
      posthog?.capture('accountant_package_sent', {
        period,
        mode,
        doc_count: data?.doc_count,
        emails_sent: data?.emails_sent,
      });
      setSendResult(data); setSendError(null);
      invalidate();
      qc.invalidateQueries({ queryKey:['accountant-history'] });
    },
    onError: err => {
      if (err?.message === PKG_SEND_CANCELLED) {
        setSendError('A küldés megszakítva — a könyvelői csomag küldéséhez passkey-megerősítés kell.');
        return;
      }
      const detail = err?.response?.data?.detail;
      setSendError(typeof detail === 'string' ? detail
        : (detail?.error || 'Küldési hiba – részletek a backend logban'));
    },
  });

  const switchMode = (m) => {
    setMode(m);
    setPeriod(m === 'month' ? lastClosedMonth() : lastClosedQuarter());
    setSendResult(null); setSendError(null);
  };

  const modeBtnS = (active) => ({
    // K-11: token, nem beégetett fehér (Raymondban a sárgán 1,27:1 volt)
    background: active ? 'var(--tab-active-bg)' : 'transparent',
    color: active ? 'var(--tab-active-fg)' : 'var(--text-muted)',
    border: '1px solid ' + (active ? 'var(--tab-active-bg)' : 'var(--border)'),
    borderRadius: 'var(--radius)', padding: '0.4rem 0.9rem',
    fontSize: '0.8rem', cursor: 'pointer', fontWeight: active ? 600 : 400,
  });

  const thS = { padding:'0.45rem 0.6rem', color:'var(--text-muted)', fontWeight:600,
    fontSize:'0.65rem', textTransform:'uppercase', letterSpacing:'0.05em',
    borderBottom:'1px solid var(--border)', textAlign:'left' };

  const docs = check?.documents;
  const stmts = check?.statements;
  const rec = check?.recurring;
  const utx = check?.unmatched_transactions;

  return (
    <div>
      {/* Fejléc: cím + időszak választó + ömlesztett feltöltés egy sorban */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        marginBottom:'1rem',flexWrap:'wrap',gap:8}}>
        <h1 style={{fontSize:'1.25rem',fontWeight:700}}>Könyvelőnek küldés</h1>
        <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          {isFetching && <span style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>Ellenőrzés…</span>}
          <button style={modeBtnS(mode==='month')} onClick={()=>switchMode('month')}>Hónap</button>
          <button style={modeBtnS(mode==='quarter')} onClick={()=>switchMode('quarter')}>Negyedév</button>
          <Select value={period} onChange={e=>{setPeriod(e.target.value); setSendResult(null); setSendError(null);}}
            options={mode==='month' ? monthOptionsList() : quarterOptionsList()}
            style={{minWidth:170}}/>
          <DropButton onFiles={bulk.process} busy={bulk.busy}/>
        </div>
      </div>

      {sendResult && (
        <Alert type="success">
          Elküldve: {sendResult.files} fájl ({sendResult.documents} bizonylat + {sendResult.statements} kivonat){' '}
          {sendResult.emails_sent} levélben → {sendResult.to}
        </Alert>
      )}
      <BulkResults items={bulk.items} notice={bulk.notice} progress={bulk.progress}
        busy={bulk.busy} onCancel={bulk.cancel} onClear={bulk.clear}
        onResolveAttach={bulk.resolveAttach} onResolveCreate={bulk.resolveCreate}
        onResolveDiscard={bulk.resolveDiscard}/>

      {sendError && <Alert type="danger">{sendError}</Alert>}
      {rowUploadError && <Alert type="danger">PDF feltöltési hiba: {rowUploadError}</Alert>}

      {/* ⚠ A könyvelői teljesség-ellenőrzés hibáját KÜLÖN ki kell írni: itt a
          „nincs hiányosság" és a „nem tudtuk lekérdezni" két gyökeresen más
          üzenet — az elsőre le lehet zárni a hónapot, a másodikra nem. */}
      <QueryState query={checkQ} errorText="A teljesség-ellenőrzés betöltése sikertelen.">
        {() => (
        <>
          {/* 1. Bizonylat PDF-ek */}
          <Card title={<span><StatusDot ok={docs.ready}/>1. Számla PDF-ek</span>}
            subtitle={`${check.period_label}: ${docs.total} bizonylat (${docs.incoming} bejövő + ${docs.outgoing} kimenő), ebből ${docs.with_pdf} db PDF-fel`}>
            {docs.missing_pdf.length === 0 ? (
              <p style={{fontSize:'0.82rem',color:'var(--accent)'}}>
                <Check size={13} style={{verticalAlign:'-2px'}}/> Minden bizonylathoz van PDF.
              </p>
            ) : (<>
              {noInvoicingAsset && docs.missing_pdf.some(d => d.direction === 'outgoing') && (
                <AssetRequiredNotice assetType="invoicing" label="Számlázó program" linkTo="/settings/invoicing">
                  Nincs bekötött számlázó program (Billingo), ezért a kimenő számlák PDF-jét csak kézzel
                  tudod pótolni.
                </AssetRequiredNotice>
              )}
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead><tr>
                  <th style={thS}>Irány</th><th style={thS}>Partner</th>
                  <th style={thS}>Számlaszám</th><th style={thS}>Kiállítva</th>
                  <th style={{...thS,textAlign:'right'}}>Bruttó</th><th style={thS}></th>
                </tr></thead>
                <tbody>
                  {docs.missing_pdf.map(d => (
                    <MissingPdfRow key={d.id} doc={d}
                      onBillingo={(id)=>billingoMut.mutate(id)}
                      billingoPending={billingoMut.isPending}
                      onUpload={(id,file)=>uploadMut.mutate({id,file})}
                      uploadPending={uploadMut.isPending}/>
                  ))}
                </tbody>
              </table>
            </>)}
          </Card>

          {/* 2. Bankkivonatok */}
          <Card title={<span><StatusDot ok={stmts.ready}/>2. Bankkivonatok</span>}
            subtitle="Aktív bankszámlánként ellenőrizzük a kivonatot. Ha nem volt tranzakció (pl. Bank Burgenland nem ad kivonatot), hagyd jóvá a hiányt.">
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>
                <th style={thS}>Bank / számla</th>
                {check.months.map(m => <th key={m} style={thS}>{m}</th>)}
              </tr></thead>
              <tbody>
                {stmts.accounts.map(acc => (
                  <tr key={acc.account_id}>
                    <td style={{padding:'0.45rem 0.6rem',fontSize:'0.82rem'}}>
                      <span style={{fontWeight:600}}>{acc.bank_name||'Bank'}</span>{' '}
                      <span style={{color:'var(--text-muted)',fontSize:'0.72rem'}}>
                        {acc.currency} · …{(acc.iban||'').slice(-6)}
                      </span>
                    </td>
                    {check.months.map(m => {
                      const cell = acc.months[m];
                      return (
                        <td key={m} style={{padding:'0.45rem 0.6rem'}}>
                          {cell.has_pdf ? (
                            <Badge variant="success">✓ megvan</Badge>
                          ) : cell.found ? (
                            <span style={{display:'inline-flex',alignItems:'center',gap:4}}>
                              <Badge variant="warning">van, de nincs PDF</Badge>
                              <StmtCellUpload small/>
                            </span>
                          ) : cell.waived ? (
                            <span style={{display:'inline-flex',alignItems:'center',gap:4}}>
                              <Badge variant="default">jóváhagyva: nincs</Badge>
                              <button title="Jóváhagyás visszavonása"
                                onClick={()=>delWaiverMut.mutate(cell.waiver_id)}
                                style={{background:'none',border:'none',cursor:'pointer',
                                  color:'var(--text-dim)',fontSize:'0.75rem'}}>✕</button>
                            </span>
                          ) : (
                            <span style={{display:'inline-flex',alignItems:'center',gap:4}}>
                              <Badge variant="danger">hiányzik</Badge>
                              <StmtCellUpload small/>
                              <Btn size="sm" variant="ghost" loading={waiverMut.isPending}
                                onClick={()=>waiverMut.mutate({waiver_type:'statement', period:m,
                                  account_id:acc.account_id, note:'Nem volt tranzakció'})}>
                                Nem volt tranzakció – OK
                              </Btn>
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {/* 2b. Nem társított banki tételek */}
          <Card title={<span><StatusDot ok={utx ? utx.ready : true}/>2b. Nem társított banki tételek</span>}
            subtitle="Az időszak kivonatain szereplő, bizonylathoz NEM társított tételek (banki díjak és belső deviza-konverziók kizárva). Ezek jellemzően hiányzó bizonylatot jeleznek – a Tranzakciók oldalon a „🔍 Bizonylat” gombbal társíthatók, vagy a hiányzó számla pótlandó.">
            {!utx || utx.count === 0 ? (
              <p style={{fontSize:'0.82rem',color:'var(--text-muted)'}}>
                Minden banki tétel társítva (vagy díj/belső mozgás). ✓
              </p>
            ) : (
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.8rem'}}>
                <thead><tr>
                  <th style={thS}>Dátum</th><th style={thS}>Bank</th>
                  <th style={thS}>Partner</th><th style={{...thS,textAlign:'right'}}>Összeg</th>
                  <th style={thS}>Közlemény</th>
                </tr></thead>
                <tbody>
                  {utx.items.map(t => (
                    <tr key={t.id} style={{borderBottom:'1px solid var(--border)'}}>
                      <td style={{padding:'0.4rem 0.6rem',whiteSpace:'nowrap',fontFamily:'var(--mono)'}}>{t.tx_date}</td>
                      <td style={{padding:'0.4rem 0.6rem',color:'var(--text-muted)',fontSize:'0.72rem'}}>{t.bank_name || '–'}</td>
                      <td style={{padding:'0.4rem 0.6rem'}}>{t.counterparty_name || '–'}</td>
                      <td style={{padding:'0.4rem 0.6rem',textAlign:'right',whiteSpace:'nowrap',fontFamily:'var(--mono)',
                        color: t.tx_type==='credit' ? 'var(--accent)' : 'inherit'}}>
                        {t.tx_type==='credit' ? '+' : '−'}{huFmt(t.amount)} {t.currency}
                      </td>
                      <td style={{padding:'0.4rem 0.6rem',color:'var(--text-muted)',fontSize:'0.72rem',
                        maxWidth:260,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.reference || '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          {/* 3. Rendszeres szállítók */}
          <Card title={<span><StatusDot ok={rec.ready}/>3. Rendszeres szállítók</span>}
            subtitle="Rendszeresen (havi/negyedéves/éves) számlázók darabszám-ellenőrzése. A negyedéves/éves számlázó csak a megfelelő periódus zárásakor értékelt (egyébként „info”). A „portál” jelű (e-mailt NEM küldő) szállítónál a hiány erős figyelmeztetés. Konfiguráció: Beállítások → Rendszeres szállítók.">
            {rec.vendors.length === 0 ? (
              <p style={{fontSize:'0.82rem',color:'var(--text-muted)'}}>
                Még nincs rendszeres szállító konfigurálva.
              </p>
            ) : (
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead><tr>
                  <th style={thS}>Partner</th>
                  <th style={thS}>Gyakoriság / várt</th>
                  {check.months.map(m => <th key={m} style={thS}>{m}</th>)}
                  <th style={thS}></th>
                </tr></thead>
                <tbody>
                  {rec.vendors.map(v => (
                    <tr key={v.partner_id}>
                      <td style={{padding:'0.45rem 0.6rem',fontSize:'0.82rem',fontWeight:600}}>
                        {v.name}
                        {v.requires_portal_download && (
                          <span title="Portálról letöltendő – semmi nem hozza be automatikusan"
                            style={{marginLeft:6,fontSize:'0.62rem',color:'var(--warning)',
                              border:'1px solid var(--warning)',borderRadius:4,padding:'0 4px'}}>portál</span>
                        )}
                      </td>
                      <td style={{padding:'0.45rem 0.6rem',fontSize:'0.8rem'}}>
                        <div>{FREQ_LABELS[v.frequency] || v.frequency || 'havi'}</div>
                        <div style={{fontFamily:'var(--mono)',color:'var(--text-muted)',fontSize:'0.74rem'}}>
                          {v.expected_min ?? '–'} … {v.expected_max ?? '–'}
                        </div>
                      </td>
                      {check.months.map(m => {
                        const cell = v.months[m];
                        return (
                          <td key={m} style={{padding:'0.45rem 0.6rem'}}>
                            {!cell.evaluated ? (
                              <Badge variant="default"
                                title="Gyakoriság szerint nem ebben a periódusban értékelt">
                                {cell.count} db · info
                              </Badge>
                            ) : cell.ok ? (
                              <Badge variant="success">{cell.count} db</Badge>
                            ) : cell.waived ? (
                              <span style={{display:'inline-flex',alignItems:'center',gap:4}}>
                                <Badge variant="default">{cell.count} db – jóváhagyva</Badge>
                                <button title="Jóváhagyás visszavonása"
                                  onClick={()=>delWaiverMut.mutate(cell.waiver_id)}
                                  style={{background:'none',border:'none',cursor:'pointer',
                                    color:'var(--text-dim)',fontSize:'0.75rem'}}>✕</button>
                              </span>
                            ) : (
                              <span style={{display:'inline-flex',alignItems:'center',gap:6}}>
                                <Badge variant={v.severity === 'strong' ? 'danger' : 'warning'}>
                                  {cell.count} db{v.severity === 'strong' ? ' ⚠' : ''}
                                </Badge>
                                <Btn size="sm" variant="ghost" loading={waiverMut.isPending}
                                  onClick={()=>waiverMut.mutate({waiver_type:'recurring', period:m,
                                    partner_id:v.partner_id, note:`${cell.count} db rendben`})}>
                                  Rendben így
                                </Btn>
                              </span>
                            )}
                          </td>
                        );
                      })}
                      <td style={{padding:'0.45rem 0.6rem',textAlign:'right'}}>
                        {v.portal_url && (
                          <a href={v.portal_url} target="_blank" rel="noreferrer"
                            style={{display:'inline-flex',alignItems:'center',gap:4,
                              color:'var(--primary)',fontSize:'0.78rem',textDecoration:'none'}}>
                            <ExternalLink size={13}/> Portál
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {rec.candidates.length > 0 && (
              <div style={{marginTop:10}}>
                <Alert type="info">
                  Gyakori szállítók, amelyek nincsenek rendszeres szállítóként konfigurálva:{' '}
                  {rec.candidates.map(c=>`${c.supplier_name} (${c.months_present}/${c.window_months} hó)`).join(', ')}
                  {' '}– felvétel: Beállítások → Rendszeres szállítók.
                </Alert>
              </div>
            )}
          </Card>

          {/* 4. Küldés */}
          <Card title={<span><StatusDot ok={check.ready}/>Küldés</span>}
            subtitle={`${docs.with_pdf} számla PDF + ${stmts.package_count} kivonat megy csatolmányként (eredeti fájlnevekkel). Nagy csomagnál automatikusan több levélre bontva.`}>
            <div style={{display:'flex',flexDirection:'column',gap:10,maxWidth:640}}>
              <Input label="Tárgy (üresen: alapértelmezett)"
                placeholder={`PhenoGyde Kft. – könyvelési csomag ${check.period_label}`}
                value={subject} onChange={e=>setSubject(e.target.value)}/>
              <label style={{display:'flex',flexDirection:'column',gap:4}}>
                <span style={{fontSize:'0.68rem',fontWeight:600,color:'var(--text-muted)',
                  textTransform:'uppercase',letterSpacing:'0.06em'}}>
                  Levél szövege (üresen: alapértelmezett összesítő)
                </span>
                <textarea value={body} onChange={e=>setBody(e.target.value)} rows={3}
                  placeholder={`Kedves Marianne!\n\nCsatoltan küldöm a(z) ${check.period_label} időszak könyvelési anyagát…`}
                  style={{background:'var(--bg-input)',border:'1px solid var(--border)',
                    borderRadius:'var(--radius)',padding:'0.45rem 0.65rem',
                    color:'var(--text)',fontSize:'0.85rem',resize:'vertical',width:'100%'}}/>
              </label>
              <label style={{display:'flex',alignItems:'center',gap:8,fontSize:'0.82rem',
                cursor:'pointer',color:'var(--text)'}}>
                <input type="checkbox" checked={includePaid}
                  onChange={e=>setIncludePaid(e.target.checked)}
                  style={{width:15,height:15,accentColor:'var(--primary)'}}/>
                Az ebben a hónapban <b>kifizetett</b> számlák is mehetnek (késve érkezett
                bizonylatok, eltérő könyvelési időszakkal is)
              </label>
              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                <Btn icon={Send} loading={sendMut.isPending} disabled={!check.ready}
                  onClick={()=>sendMut.mutate(false)}>
                  Küldés a könyvelőnek
                </Btn>
                {!check.ready && (
                  <>
                    <span style={{fontSize:'0.75rem',color:'var(--danger)'}}>
                      Az ellenőrzés nem zöld – előbb pótold vagy hagyd jóvá a hiányokat.
                    </span>
                    <Btn variant="danger" size="sm" loading={sendMut.isPending}
                      onClick={()=>setConfirm({
                        title:'Hiányos csomag küldése', danger:true, confirmLabel:'Küldés mindenképp',
                        message:'A teljesség-ellenőrzés nem zöld – a csomagból bizonylat vagy kivonat hiányozhat. Biztosan elküldöd így a könyvelőnek?',
                        action:()=>sendMut.mutate(true),
                      })}>
                      Küldés mindenképp
                    </Btn>
                  </>
                )}
              </div>
            </div>
          </Card>

          {/* Korábbi küldések */}
          {history.length > 0 && (
            <Card title="Korábbi küldések">
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead><tr>
                  <th style={thS}>Időszak</th><th style={thS}>Mikor</th><th style={thS}>Címzett</th>
                  <th style={thS}>Fájlok</th><th style={thS}>Levelek</th><th style={thS}>Megjegyzés</th>
                </tr></thead>
                <tbody>
                  {history.map(p => (
                    <tr key={p.id}>
                      <td style={{padding:'0.4rem 0.6rem',fontSize:'0.8rem',fontFamily:'var(--mono)',fontWeight:600}}>{p.period}</td>
                      <td style={{padding:'0.4rem 0.6rem',fontSize:'0.8rem',fontFamily:'var(--mono)'}}>{fmtSentAt(p.sent_at)}</td>
                      <td style={{padding:'0.4rem 0.6rem',fontSize:'0.8rem'}}>{p.recipient}</td>
                      <td style={{padding:'0.4rem 0.6rem',fontSize:'0.8rem',fontFamily:'var(--mono)'}}>
                        {p.total_files} db{p.total_bytes ? ` (${(p.total_bytes/1024/1024).toFixed(1)} MB)` : ''}
                      </td>
                      <td style={{padding:'0.4rem 0.6rem',fontSize:'0.8rem',fontFamily:'var(--mono)'}}>{p.email_count}</td>
                      <td style={{padding:'0.4rem 0.6rem',fontSize:'0.75rem',color:'var(--text-muted)'}}>{p.notes||''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </>
        )}
      </QueryState>

      <ConfirmDialog open={!!confirm} title={confirm?.title} message={confirm?.message}
        danger={confirm?.danger} confirmLabel={confirm?.confirmLabel}
        onCancel={()=>setConfirm(null)}
        onConfirm={()=>{ const a=confirm.action; setConfirm(null); a(); }}/>
    </div>
  );
}

// ── Settings ───────────────────────────────────────────────────────────────
// ── Portál letöltés (Beállítások fül) ──────────────────────────────────────
// (lastClosedMonth + HU_MONTH_NAMES az Accountant szekcióban már definiálva.)
function shiftMonth(ym, delta) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
}
function monthLabel(ym) {
  const [y, m] = ym.split('-').map(Number);
  return `${y}. ${HU_MONTH_NAMES[m]}`;
}

// Egy portál-szolgáltató sora: státusz + szerkeszthető URL + "letöltve" jelölő.
function PortalProviderRow({ p, month, onSaveUrl, onToggleDone, busy }) {
  const [url, setUrl] = useState(p.portal_url || '');
  useEffect(() => { setUrl(p.portal_url || ''); }, [p.portal_url]);
  const dirty = (url || '') !== (p.portal_url || '');
  const tdS = { padding:'0.45rem 0.55rem', fontSize:'0.82rem', verticalAlign:'middle',
    borderBottom:'1px solid var(--border-dim)' };

  return (
    <tr style={{opacity: p.done ? 0.6 : 1}}>
      <td style={{...tdS, fontWeight:600}}>{p.name}</td>
      <td style={{...tdS, textAlign:'center'}}>
        <Badge variant={p.count > 0 ? 'success' : 'warning'}>{p.count} db</Badge>
      </td>
      <td style={tdS}>
        <div style={{display:'flex', gap:6, alignItems:'center'}}>
          <input type="url" value={url} placeholder="https://…"
            onChange={e=>setUrl(e.target.value)}
            style={{flex:1, minWidth:180, background:'var(--bg-input)',
              border:'1px solid var(--border)', borderRadius:'var(--radius)',
              padding:'0.3rem 0.5rem', color:'var(--text)', fontSize:'0.78rem',
              fontFamily:'var(--mono)'}}/>
          {dirty && (
            <Btn size="sm" variant="primary" loading={busy}
              onClick={()=>onSaveUrl(p.partner_id, url.trim() || null)}>Mentés</Btn>
          )}
        </div>
      </td>
      <td style={{...tdS, textAlign:'center'}}>
        {p.portal_url ? (
          <a href={p.portal_url} target="_blank" rel="noreferrer"
            style={{color:'var(--primary)', display:'inline-flex', alignItems:'center',
              gap:4, fontSize:'0.8rem', fontWeight:600, textDecoration:'none'}}>
            <ExternalLink size={13}/> Megnyitás
          </a>
        ) : <span style={{fontSize:'0.74rem', color:'var(--text-dim)'}}>nincs URL</span>}
      </td>
      <td style={{...tdS, textAlign:'center'}}>
        <label style={{display:'inline-flex', alignItems:'center', gap:6,
          cursor:'pointer', fontSize:'0.8rem', color:'var(--text)'}} title="Letöltve ebben a hónapban">
          <input type="checkbox" checked={p.done} disabled={busy}
            onChange={e=>onToggleDone(p, e.target.checked)}
            style={{width:15, height:15, accentColor:'var(--primary)'}}/>
          Letöltve
        </label>
      </td>
      <td style={{...tdS, textAlign:'right'}}>
        <Btn size="sm" variant="ghost" loading={busy}
          title="Eltávolítás a portál-listáról (a partner megmarad)"
          onClick={()=>onSaveUrl(p.partner_id, p.portal_url, { remove:true })}>
          <Trash2 size={14}/>
        </Btn>
      </td>
    </tr>
  );
}

function PortalFetchPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirm, setConfirm] = useState(null); // {title, message, danger, confirmLabel, action}
  const [month, setMonth] = useState(lastClosedMonth());
  const [newName, setNewName] = useState('');
  const [newUrl, setNewUrl] = useState('');

  const portalQ = useQuery({
    queryKey: ['portal-providers', month],
    queryFn: () => portalFetchApi.providers(month),
  });
  const { data, isLoading } = portalQ;
  const invalidate = () => qc.invalidateQueries({ queryKey:['portal-providers'] });

  const updateMut = useMutation({
    mutationFn: ({id, d}) => partnersApi.update(id, d),
    onSuccess: invalidate,
    onError: (err) => toast.error('Mentés sikertelen: ' + (err?.response?.data?.detail || err.message)),
  });
  const createMut = useMutation({
    mutationFn: partnersApi.create,
    onSuccess: () => { setNewName(''); setNewUrl(''); invalidate(); },
  });
  const waiverMut = useMutation({
    mutationFn: accountantApi.createWaiver,
    onSuccess: invalidate,
  });
  const delWaiverMut = useMutation({
    mutationFn: accountantApi.deleteWaiver,
    onSuccess: invalidate,
  });
  const busy = updateMut.isPending || waiverMut.isPending || delWaiverMut.isPending;

  const onSaveUrl = (id, url, opts = {}) => {
    if (opts.remove) {
      const prov = provs.find(x => x.partner_id === id);
      setConfirm({
        title:'Eltávolítás a portál-listáról', danger:true, confirmLabel:'Eltávolítás',
        message:`Leveszed a(z) "${prov?.name || 'szolgáltató'}" szolgáltatót a portál-listáról? (A partner rekord megmarad.)`,
        action:()=>updateMut.mutate({ id, d:{ requires_portal_download:false }},
          { onSuccess:()=>toast.success('Eltávolítva a portál-listáról') }),
      });
    } else {
      updateMut.mutate({ id, d:{ portal_url:url }},
        { onSuccess:()=>toast.success('Mentve') });
    }
  };
  const onToggleDone = (p, checked) => {
    if (checked) waiverMut.mutate({ waiver_type:'portal', period:month, partner_id:p.partner_id });
    else if (p.waiver_id) delWaiverMut.mutate(p.waiver_id);
  };
  const addProvider = () => {
    if (!newName.trim()) return;
    createMut.mutate({ name:newName.trim(), portal_url:newUrl.trim() || null,
      requires_portal_download:true });
  };

  const thS = { padding:'0.45rem 0.55rem', color:'var(--text-muted)', fontWeight:600,
    fontSize:'0.65rem', textTransform:'uppercase', letterSpacing:'0.05em',
    borderBottom:'1px solid var(--border)', textAlign:'left' };
  const provs = data?.providers || [];
  const missing = provs.filter(p => p.count === 0 && !p.done).length;

  return (
    <div style={{display:'flex', flexDirection:'column', gap:14}}>
      <Card title="Portál letöltés"
        subtitle="Azon szolgáltatók (belföldi és külföldi), akik NEM küldenek e-mailt – fel kell menni a portáljukra, bejelentkezni és letölteni a számlát. Itt egy helyen végigmész a bejelentkezéseken, és látod, mi hiányzik még a kiválasztott hónapra (a könyvelőnek küldés előtti ellenőrzéshez).">
        <div style={{display:'flex', flexDirection:'column', gap:12}}>
          {/* Hónap-választó */}
          <div style={{display:'flex', alignItems:'center', gap:10}}>
            <Btn size="sm" variant="ghost" onClick={()=>setMonth(m=>shiftMonth(m,-1))}>◀</Btn>
            <span style={{fontWeight:700, fontSize:'0.95rem', minWidth:130, textAlign:'center'}}>
              {monthLabel(month)}
            </span>
            <Btn size="sm" variant="ghost"
              disabled={month >= shiftMonth(lastClosedMonth(), 1)}
              onClick={()=>setMonth(m=>shiftMonth(m,1))}>▶</Btn>
            {!isLoading && (
              <span style={{fontSize:'0.78rem', color: missing ? 'var(--warning)' : 'var(--accent)',
                marginLeft:8}}>
                {missing ? `⚠ ${missing} szolgáltatótól még 0 számla` : '✓ mindegyiktől érkezett számla'}
              </span>
            )}
          </div>

          <QueryState query={portalQ} empty={provs.length === 0}
            emptyText="Még nincs portál-szolgáltató. Vedd fel lent (pl. Webflow, GoDaddy, LinkedIn…)."
            errorText="A portál-szolgáltatók betöltése sikertelen.">
            <table style={{width:'100%', borderCollapse:'collapse'}}>
              <thead><tr>
                <th style={thS}>Szolgáltató</th>
                <th style={{...thS, textAlign:'center'}}>{monthLabel(month)}</th>
                <th style={thS}>Portál URL</th>
                <th style={{...thS, textAlign:'center'}}>Belépés</th>
                <th style={{...thS, textAlign:'center'}}>Kész</th>
                <th style={thS}></th>
              </tr></thead>
              <tbody>
                {provs.map(p => (
                  <PortalProviderRow key={p.partner_id} p={p} month={month}
                    onSaveUrl={onSaveUrl} onToggleDone={onToggleDone} busy={busy}/>
                ))}
              </tbody>
            </table>
          </QueryState>

          {/* Új szolgáltató */}
          <div style={{display:'flex', gap:8, alignItems:'flex-end', flexWrap:'wrap',
            borderTop:'1px solid var(--border-dim)', paddingTop:12}}>
            <div style={{flex:'1 1 200px'}}>
              <Input label="Új szolgáltató neve" value={newName}
                placeholder="pl. Digital Charging Solutions GmbH"
                onChange={e=>setNewName(e.target.value)}/>
            </div>
            <div style={{flex:'2 1 280px'}}>
              <Input label="Portál URL" value={newUrl} placeholder="https://…"
                onChange={e=>setNewUrl(e.target.value)}/>
            </div>
            <Btn icon={Plus} loading={createMut.isPending} disabled={!newName.trim()}
              onClick={addProvider}>Hozzáadás</Btn>
          </div>
          <p style={{fontSize:'0.72rem', color:'var(--text-muted)', lineHeight:1.5}}>
            A „db" oszlop azt mutatja, hány bejövő bizonylat van már a rendszerben ettől a
            szolgáltatótól a kiválasztott könyvelési hónapra. A „Letöltve" jelölővel
            kipipálhatod, ha végeztél (hónaponként megjegyzi). A szállító ↔ bizonylat
            párosítás adószám, illetve név alapján történik.
          </p>
        </div>
      </Card>

      {/* Bővítmény-útmutató (a behozás eszköze). A ZIP statikus fájl a
          `frontend/public/downloads/`-ban (NEM /api — nem kell neki cég-fejléc), és a
          `scripts/build_extension_zip.py` építi; a CI ellenőrzi, hogy egyezik a forrással. */}
      <Card title="puo PDF-mentő (böngésző-bővítmény)"
        subtitle="A portálról letöltött (vagy bárhol megnyitott) PDF-et a saját, bejelentkezett böngésződből küldi be a kiválasztott cégbe – jelszót, sütit nem tárol. Chrome-ban és Edge-ben működik."
        actions={<a href="/downloads/puo-pdf-mento.zip" download
          style={{display:'inline-flex', alignItems:'center', gap:6, fontSize:'0.8rem',
                  fontWeight:600, color:'var(--primary)', textDecoration:'none'}}>
          <Download size={14}/> Letöltés (ZIP)</a>}>
        <Alert type="info">
          <b>Telepítés egyszer:</b> (1) töltsd le és csomagold ki a ZIP-et egy állandó helyre
          (pl. Dokumentumok) – a kicsomagolt <code>puo-pdf-mento</code> mappát később ne töröld.
          (2) Chrome-ban nyisd meg a <code>chrome://extensions</code>, Edge-ben az{' '}
          <code>edge://extensions</code> oldalt, és kapcsold be a <b>Fejlesztői módot</b>.
          (3) „Kicsomagolt bővítmény betöltése” → válaszd a <code>puo-pdf-mento</code> mappát.
          (4) Tűzd ki az eszköztárra.<br/>
          <b>Használat:</b> legyél belépve a puo-ba ugyanebben a böngészőben; nyisd meg magát a
          PDF-et egy fülön, kattints a puo ikonra, válaszd ki, <b>melyik cégbe</b> menjen, majd
          „PDF beküldése”. A tartalom alapján osztályozunk (számla / kivonat / bér / adó), és a
          duplikátumot kiszűrjük.<br/>
          <b>Frissítés:</b> töltsd le újra, csomagold ki ugyanoda (felülírva), majd a bővítmények
          oldalán kattints a kártya ↻ ikonjára.
        </Alert>
      </Card>

      <ConfirmDialog open={!!confirm} title={confirm?.title} message={confirm?.message}
        danger={confirm?.danger} confirmLabel={confirm?.confirmLabel}
        onCancel={()=>setConfirm(null)}
        onConfirm={()=>{ const a=confirm.action; setConfirm(null); a(); }}/>
    </div>
  );
}

const SETTINGS_TITLES = {
  company:    'Cég beállítások',
  // M11 (2026-09-05): öt típusonkénti asset-oldal (wiki/proposals/asset-registry.md).
  'nav-osa':  'NAV Online Számla',
  banks:      'Bankszámlák',
  invoicing:  'Számlázó programok',
  'nav-upo':  'Adófolyószámlák',
  email:      'E-mail fiókok',
  categories: 'Kategóriák',
  partners:   'Partnerek',
  projects:   'Projektek',
  employees:  'Munkavállalók',
  security:   'Biztonság',
  chat:       'Chat beállítások',
};

// Aláhúzásos fül-stílus: az aktív fül alsó szegélye ráül a konténer
// keretvonalára (marginBottom:-1px), így klasszikus "fül" hatás.
const settingsTabBtnS = (active) => ({
  background: 'transparent',
  border: 'none',
  borderBottom: '2px solid ' + (active ? 'var(--primary)' : 'transparent'),
  color: active ? '#f1f5f9' : 'var(--text-muted)',
  borderRadius: 0,
  padding: '0.5rem 1.05rem',
  marginBottom: '-1px',
  fontSize: '0.84rem',
  cursor: 'pointer',
  fontWeight: active ? 600 : 500,
  transition: 'color 0.12s, border-color 0.12s',
});

function SettingsInnerTabs({ tabs, cur, set }) {
  return (
    <div style={{display:'flex',gap:2,marginBottom:'1.1rem',
      borderBottom:'1px solid var(--border)',flexWrap:'wrap'}}>
      {tabs.map(t => (
        <button key={t.key} style={settingsTabBtnS(cur===t.key)} onClick={()=>set(t.key)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

// A Beállítások a bal app-navból nyílik, csoportonként külön útvonalon
// (/settings/:group). A group nélküli /settings a Cég beállításokra esik.
export function Settings() {
  const { group = 'company' } = useParams();
  const { isAdmin } = useAuth();
  const [bankTab, setBankTab]       = useState('api');
  const [catTab, setCatTab]         = useState('list');
  const [partnerTab, setPartnerTab] = useState('aliases');

  return (
    <div>
      <h1 style={{fontSize:'1.2rem',fontWeight:700,marginBottom:'1.1rem'}}>
        {SETTINGS_TITLES[group] || 'Beállítások'}
      </h1>

      {group === 'company' && (<>
        <CompanyPanel/>
        <AppearancePanel/>
        <DataExportPanel/>
      </>)}

      {group === 'banks' && (<>
        <SettingsInnerTabs cur={bankTab} set={setBankTab} tabs={[
          { key:'api',          label:'API kapcsolat' },
          { key:'accounts',     label:'Számlák nevei' },
          { key:'defaults',     label:'Utalás alapbeállításai' },
          { key:'transactions', label:'Tranzakciók' },
        ]}/>
        {/* 2026-09-05 (Gábor visszajelzése): EGY bankkapcsolat = EGY asset-sor. A regiszter
            listázza a Raiffeisen/Revolut/kivonat-feltöltéses kapcsolatokat; a sor alatt a
            bank saját panelje (utalás-engedély, környezet, consentek, draft-mód) ÉS a
            hitelesítő adatok (maszkolt kijelzéssel, step-upos cserével). Nincs többé külön
            „Raiffeisen” + „Banki API hozzáférés” kártya. */}
        {bankTab === 'api' && (
          <AssetStatusBlock assetType="bank_account" title="Bankkapcsolatok"
            subtitle="Minden bank egy kapcsolat: állapot, hitelesítő adatok (titkok maszkolva), banki beállítások."
            emptyText="Még nincs bankkapcsolat. A fenti gombokkal veheted fel a Raiffeisen vagy a Revolut kapcsolatot; kivonat-feltöltéses számlát a Számlák nevei fülön adhatsz hozzá."
            registerProviders={[{ provider: 'raiffeisen', label: 'Raiffeisen' }, { provider: 'revolut', label: 'Revolut' }]}
            renderExtra={(a) => {
              if (a.provider === 'revolut') return (<>
                <IntegrationsPanel embedded/>
                <AssetCredentialsPanel asset={a} title="Hitelesítő adatok" stepUpWhy="A Revolut-hozzáférés módosítása előtt "
                  whyText="Ezekkel a kulcsokkal utalás indul — a módosítás előtt friss bejelentkezést kérünk."/>
              </>);
              if (a.provider === 'raiffeisen') return (<>
                <RaiffeisenPanel embedded/>
                <RaiffeisenCredentials embedded/>
              </>);
              return null;
            }}/>
        )}
        {bankTab === 'accounts'     && <BankAccountsPanel/>}
        {bankTab === 'defaults'     && <PaymentDefaultsPanel/>}
        {bankTab === 'transactions' && <TransactionsPanel/>}
      </>)}

      {/* ── M11: típusonkénti asset-oldalak ─────────────────────────────────
          Singleton típusoknál (NAV Online Számla, Adófolyószámlák, E-mail fiókok)
          NINCS fül-sáv — egy egyetlen-elemű fül-sáv hamisan sugallna választást ott,
          ahol szerkezetileg (uq_company_assets_singleton) soha nem lehet kettő.
          A ma bekötetlen oldal (NAV Online Számla) egy HARMADIK szövegregiszterrel
          szól: `Alert type="info"`, CTA NÉLKÜL — ez egy MÉG NEM LÉTEZŐ funkcióra
          figyelmeztet, nem egy be nem kötött, de működőre. */}
      {group === 'nav-osa' && (<>
        {/* M10 (alapszint): a meglévő, seedelt `nav` készlet regiszterbe vétele + élő próba
            (queryInvoiceDigest). A technikai felhasználó ÍRÓ felülete még nincs. */}
        <AssetStatusBlock assetType="nav_osa" title="NAV Online Számla kapcsolat"
          subtitle="Bejövő számlák letöltése a NAV Online Számla rendszerből (nem az adófolyószámla). A technikai felhasználót a NAV Online Számla portálon kell létrehozni; a kulcsait itt rögzíted."
          emptyText="Még nincs bekötött NAV Online Számla kapcsolat — a fenti gombbal veheted fel, utána itt adhatod meg a technikai felhasználó kulcsait."
          registerProvider="nav" registerLabel="NAV Online Számla"
          renderExtra={(a) => (a.provider === 'nav'
            ? <AssetCredentialsPanel asset={a} title="Technikai felhasználó" stepUpWhy="A NAV-hozzáférés módosítása előtt "
                whyText="A NAV-válaszból jön a bejövő számlák adata és a szállítók bankszámlaszáma — a módosítás előtt friss bejelentkezést kérünk."/>
            : null)}/>
      </>)}

      {/* M7 (2026-09-05): a Billingo-fiókok felvétele/kulcs-cseréje a saját paneljén,
          step-up-pal — több fiók, fiókonként külön készlet. */}
      {group === 'invoicing' && <BillingoAccountsPanel/>}

      {group === 'nav-upo' && (<>
        <AssetStatusBlock assetType="nav_upo" title="Adófolyószámla kapcsolat (NAV ÜPO)"
          subtitle="A regiszter szerinti kapcsolat-állapot — külön a lenti napi szinkron állapotától."
          emptyText="Még nincs bekötött adófolyószámla-kapcsolat a regiszterben — a hozzáférés lent, a meglévő panelen állítható, a fenti gombbal pedig felvehető a regiszterbe."
          registerProvider="nav_upo" registerLabel="NAV adófolyószámla"/>
        <NavUpoPanel/>
      </>)}

      {group === 'email' && (<>
        <AssetStatusBlock assetType="email" title="E-mail fiók kapcsolat (Microsoft 365)"
          subtitle="Egyetlen, közös hitelesítő-készlet (ms_graph); a lenti címek ennek a beállításai."
          emptyText="Még nincs bekötött e-mail fiók a regiszterben — a fenti gombbal veheted fel a meglévő Microsoft 365 kapcsolatot."
          registerProvider="ms_graph" registerLabel="Microsoft 365"
          renderExtra={(a) => (a.provider === 'ms_graph'
            ? <AssetCredentialsPanel asset={a} title="Azure AD app-hozzáférés" stepUpWhy="Az e-mail hozzáférés módosítása előtt "
                whyText="Ezzel a hozzáféréssel olvassuk a postafiókot és írunk a OneDrive-ra — a módosítás előtt friss bejelentkezést kérünk."/>
            : null)}/>
        <EmailAccountsPanel/>
      </>)}

      {group === 'categories' && (<>
        <SettingsInnerTabs cur={catTab} set={setCatTab} tabs={[
          { key:'list',  label:'Kategóriák' },
          { key:'rules', label:'Szabályok' },
        ]}/>
        {catTab === 'list'  && <CategoryListPanel/>}
        {catTab === 'rules' && <CategoryRulesPanel/>}
      </>)}

      {group === 'partners' && (<>
        <SettingsInnerTabs cur={partnerTab} set={setPartnerTab} tabs={[
          { key:'master',    label:'Partner-törzs' },
          { key:'aliases',   label:'Alias-ok' },
          { key:'recurring', label:'Rendszeres szállítók' },
          { key:'portal',    label:'Portál letöltés' },
        ]}/>
        {partnerTab === 'master'    && <PartnerMasterPanel/>}
        {partnerTab === 'aliases'   && <PartnerAliasesPanel/>}
        {partnerTab === 'recurring' && <RecurringVendorsPanel/>}
        {partnerTab === 'portal'    && <PortalFetchPanel/>}
      </>)}

      {group === 'projects'  && <ProjectsPanel/>}
      {/* 2026-09-29: a Munkavállalók csak cég-adminisztrátornak (a menüpont sem látszik) */}
      {group === 'employees' && (isAdmin ? <Employees/> : (
        <p style={{color:'var(--text-muted)',fontSize:'0.85rem'}}>A munkavállalói adatokat csak cég-adminisztrátor látja.</p>
      ))}
      {group === 'security'  && <SecurityPanel/>}
      {group === 'chat'      && <ChatSettingsPanel/>}
    </div>
  );
}

// Utalás alapbeállításai – devizánkénti alap-forrásszámla. A forrásokat a
// /api/payments/sources adja (utalás-engedélyezett bankok számlái), a választás
// a payment_default_sources Setting-be (JSON) mentődik.
function PaymentDefaultsPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: sources = [], isLoading } = useQuery({
    queryKey: ['payment-sources'], queryFn: paymentsApi.sources,
  });
  const { data: settings = {} } = useQuery({ queryKey: ['settings'], queryFn: settingsApi.get });

  let defaults = {};
  try { defaults = JSON.parse(settings.payment_default_sources || '{}') || {}; } catch { defaults = {}; }

  const saveMut = useMutation({
    mutationFn: (map) => settingsApi.set('payment_default_sources', JSON.stringify(map)),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['settings'] }); toast.success('Mentve'); },
    onError: (err) => toast.error('Mentés sikertelen: ' + (err?.response?.data?.detail || err.message)),
  });

  const srcKey  = (s) => `${s.bank}:${s.account_id || s.iban}`;
  const bankName = (b) => b === 'revolut' ? 'Revolut' : b === 'raiffeisen' ? 'Raiffeisen' : b;
  const currencies = ['HUF', 'EUR', 'USD'];

  const setDefault = (ccy, key) => {
    const map = { ...defaults };
    if (!key) { delete map[ccy]; }
    else {
      const s = sources.find(x => srcKey(x) === key);
      if (s) map[ccy] = { bank:s.bank, account_id:s.account_id||null, iban:s.iban||null, label:s.label, currency:ccy };
    }
    saveMut.mutate(map);
  };

  return (
    <Card title="Utalás alapbeállításai"
      subtitle="Devizánkénti alapértelmezett forrásszámla (pl. HUF → Raiffeisen, EUR → Revolut). Az utalás-modal a bizonylat devizája szerint ezt tölti be defaultként – felülírható.">
      {isLoading ? (
        <p style={{color:'var(--text-muted)',fontSize:'0.82rem'}}>Betöltés…</p>
      ) : sources.length === 0 ? (
        <Alert>Nincs utalás-engedélyezett bank vagy forrásszámla. Kapcsold be az utalást a
          Bankszámlák → API kapcsolat alatt (Revolut), illetve a Raiffeisennél hozz létre és
          hagyj jóvá egy consentet – utána itt megjelennek a számlák.</Alert>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:12,maxWidth:520}}>
          {currencies.map(ccy => {
            const opts = sources.filter(s => s.currency === ccy);
            const cur = defaults[ccy];
            const curKey = cur ? `${cur.bank}:${cur.account_id || cur.iban}` : '';
            const optList = [
              { value:'', label:'— nincs alapértelmezett —' },
              ...opts.map(s => ({ value:srcKey(s), label:`${bankName(s.bank)} – ${s.label}` })),
            ];
            if (cur && !opts.some(s => srcKey(s) === curKey)) {
              optList.push({ value:curKey, label:`${bankName(cur.bank)} – ${cur.label} (nem elérhető)` });
            }
            return (
              <div key={ccy} style={{display:'flex',alignItems:'center',gap:14}}>
                <div style={{width:64,flexShrink:0}}><CurrencyBadge cur={ccy} size="lg"/></div>
                <div style={{flex:1}}>
                  <Select value={curKey} onChange={e => setDefault(ccy, e.target.value)} options={optList}/>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}


function CompanyPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  // ⚠ A cégadat a `companies` táblából jön (companyApi), NEM a settings
  // kulcs-érték tárolóból. 2026-08-12 előtt ez a panel a settings-be írt,
  // miközben a bizonylat-pipeline a companies oszlopokat olvasta — a két érték
  // szét is csúszott. A LOGÓ marad settings-alapú (az valóban UI-állapot).
  const companyQ = useQuery({ queryKey: ['company'], queryFn: companyApi.get });
  const { data: settings = {} } = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
  });
  const isLoading = companyQ.isLoading;
  const [taxRef, setTaxRef] = useState('');
  const [companyName, setCompanyName] = useState('');
  // C2 (2026-09-06, Gábor SZŰKÍTETT listája): ÁFA-bevallási gyakoriság + üzleti év kezdő
  // hónapja. TEÁOR / létszám / árbevétel SZÁNDÉKOSAN nincs. C4: statisztika opt-in (a
  // felhasználás nem indul az ügyvédi jóváhagyás előtt — a mechanizmus épül).
  const [vatFreq, setVatFreq] = useState('');
  const [fyStart, setFyStart] = useState(1);
  const [statsOptIn, setStatsOptIn] = useState(false);
  // M11 (2026-09-05): a három e-mail mező (bejövő postafiók, bérszámfejtő, küldő/
  // OneDrive) + az OneDrive-gyökér, és Gábor 2026-09-05-i döntése óta a KÖNYVELŐ címe is
  // a `Beállítások → E-mail fiókok` oldalra költözött (`EmailAccountsPanel`) — ugyanaz a
  // `PATCH /api/company`, csak a felületi hely mozog.
  const logoRef = useRef(null);
  const initRef = useRef(false);

  useEffect(() => {
    const c = companyQ.data;
    if (!initRef.current && c) {
      setTaxRef(c.tax_reference_number || '');
      setCompanyName(c.name || '');
      setVatFreq(c.vat_frequency || '');
      setFyStart(c.fiscal_year_start_month || 1);
      setStatsOptIn(!!c.stats_opt_in_at);
      initRef.current = true;
    }
  }, [companyQ.data]);

  const setMut = useMutation({
    mutationFn: companyApi.patch,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['company'] }),
  });
  const logoUploadMut = useMutation({
    mutationFn: settingsApi.uploadLogo,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  });
  const logoDeleteMut = useMutation({
    mutationFn: settingsApi.deleteLogo,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  });

  const save = () => {
    // EGY kérés, nem három: a három korábbi settings-írás nem volt atomi —
    // ha a második elhasalt, az első már elmentődött.
    setMut.mutateAsync({
      name:                 companyName.trim(),
      tax_reference_number: taxRef.trim(),
      vat_frequency:        vatFreq || null,
      fiscal_year_start_month: Number(fyStart) || 1,
      stats_opt_in:         statsOptIn,
    }).then(() => toast.success('Cégadatok mentve'))
      .catch(err => toast.error('Mentés sikertelen: '
        + (err?.response?.data?.detail || err.message)));
  };

  const hasLogo = !!settings.company_logo_updated;
  // ⚠ a cég azonosítója a címben — lásd `Layout.js` `logoKey` (2026-09-28).
  // ⚠ blobként, az aktív cég nevében — lásd `Layout.js` és components/AuthedFile.js.
  const logo = useAuthedObjectUrl(hasLogo
    ? `/api/settings/logo?c=${companyQ.data?.id || ''}&v=${settings.company_logo_updated}` : null);

  return (
    <Card title="Cég adatok" subtitle="Multi-tenant beállítások — a TAX utalások (ÁFA, IPA, járulékok) közleményébe ez a referencia kerül.">
      {isLoading ? (
        <p style={{color:'var(--text-muted)',fontSize:'0.82rem'}}>Betöltés…</p>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:12,maxWidth:500}}>
          <div>
            <Input label="Cégnév"
              value={companyName}
              onChange={e => setCompanyName(e.target.value)}
              placeholder="pl. PhenoGyde Kft."/>
            <div style={{fontSize:'0.72rem',color:'var(--text-muted)',marginTop:4,lineHeight:1.45}}>
              A bal felső sarokban jelenik meg, ha nincs logó feltöltve.
            </div>
          </div>
          <div>
            <span style={{fontSize:'0.68rem',fontWeight:600,color:'var(--text-muted)',
              textTransform:'uppercase',letterSpacing:'0.06em'}}>Cég logó</span>
            <div style={{display:'flex',alignItems:'center',gap:12,marginTop:6}}>
              {hasLogo && logo.src ? (
                <img src={logo.src} alt="Cég logó"
                  style={{maxHeight:44,maxWidth:180,objectFit:'contain',
                    background:'var(--bg-surface)',border:'1px solid var(--border)',
                    borderRadius:'var(--radius)',padding:'4px 8px'}}/>
              ) : (
                <span style={{fontSize:'0.78rem',color:'var(--text-dim)'}}>
                  {!hasLogo ? 'Nincs logó feltöltve' : logo.error ? 'A logó nem tölthető be' : 'Logó betöltése…'}
                </span>
              )}
              <input ref={logoRef} type="file" accept="image/*" style={{display:'none'}}
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (f) logoUploadMut.mutate(f);
                  e.target.value = '';
                }}/>
              <Btn size="sm" variant="secondary" icon={Upload}
                loading={logoUploadMut.isPending}
                onClick={() => logoRef.current?.click()}>
                {hasLogo ? 'Csere' : 'Feltöltés'}
              </Btn>
              {hasLogo && (
                <Btn size="sm" variant="ghost" icon={Trash2}
                  loading={logoDeleteMut.isPending}
                  onClick={() => logoDeleteMut.mutate()}>
                  Törlés
                </Btn>
              )}
            </div>
            <div style={{fontSize:'0.72rem',color:'var(--text-muted)',marginTop:4,lineHeight:1.45}}>
              PNG / JPG / SVG, max 1 MB. A bal felső sarokban a monogram + cégnév
              helyére kerül. Sötét háttéren jól látható (világos vagy átlátszó hátterű) logót válassz.
            </div>
            {logoUploadMut.isError && (
              <div style={{fontSize:'0.74rem',color:'var(--danger)',marginTop:4}}>
                {logoUploadMut.error?.response?.data?.detail || 'Feltöltési hiba'}
              </div>
            )}
          </div>
          <div>
            <Input label="Cég adószáma (TAX referencia)"
              value={taxRef}
              onChange={e => setTaxRef(e.target.value)}
              placeholder="pl. 24973395-2-41"/>
            <div style={{fontSize:'0.72rem',color:'var(--text-muted)',marginTop:4,lineHeight:1.45}}>
              Ezt a sztringet írjuk az ÁFA / IPA / járulék utalások <b>közlemény</b> mezőjébe,
              amikor a NAV-nak vagy önkormányzatnak küldjük. A NAV Online Számla hívások
              a 8 jegyű törzsszámot használják belőle{companyQ.data?.tax_number8
                ? ` (jelenleg: ${companyQ.data.tax_number8})` : ''}.
            </div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <Select label="ÁFA-bevallás gyakorisága" value={vatFreq}
              onChange={e=>setVatFreq(e.target.value)}
              options={[{value:'',label:'– nincs megadva –'},{value:'monthly',label:'havi'},
                        {value:'quarterly',label:'negyedéves'},{value:'yearly',label:'éves'}]}/>
            <Select label="Üzleti év kezdő hónapja" value={String(fyStart)}
              onChange={e=>setFyStart(Number(e.target.value))}
              options={['január','február','március','április','május','június','július',
                        'augusztus','szeptember','október','november','december']
                        .map((n,i)=>({value:String(i+1),label:n}))}/>
          </div>
          <div>
            <Checkbox label="Hozzájárulok, hogy a cég adatai ANONIM, összesített statisztikába kerüljenek"
              checked={statsOptIn} onChange={e=>setStatsOptIn(e.target.checked)}/>
            <div style={{fontSize:'0.72rem',color:'var(--text-muted)',marginTop:4,lineHeight:1.45}}>
              Bármikor visszavonható. Cégnév, bankszámlaszám és összeg cégek között SOHA nem jelenik meg;
              csak kategória-szintű, legalább 20 cég adatából képzett arányok. Ilyen statisztika ma még
              nem készül — a hozzájárulást előre rögzítjük.
              {companyQ.data?.stats_opt_in_at ? ` Hozzájárulás: ${String(companyQ.data.stats_opt_in_at).slice(0,10)}.` : ''}
              <b>Nem hagyható üresen</b> — rossz közleménnyel a NAV nem azonosítja a befizetést.
            </div>
          </div>
          <div style={{fontSize:'0.72rem',color:'var(--text-muted)',lineHeight:1.45}}>
            A postafiókok (bejövő számlák, bérszámfejtő, küldő / OneDrive) és a könyvelő
            e-mail címe a <b>Beállítások → E-mail fiókok</b> oldalon állíthatók.
          </div>
          <div>
            <Btn loading={setMut.isPending} disabled={setMut.isPending}
              onClick={save}>Mentés</Btn>
          </div>
        </div>
      )}
    </Card>
  );
}


// ── E-mail fiókok (M11, 2026-09-05) ──────────────────────────────────────────
// A `CompanyPanel`-ból kiköltöztetett három cím + a csak-olvasható OneDrive-gyökér.
// A `PATCH /api/company` hívás, a mezők és a jogosultsági kapu (ADMIN) VÁLTOZATLANOK,
// csak a felületi hely mozgott (D3 UX §1). Az e-mail asset singleton (0-vagy-1):
// mind a négy cím EGY közös `ms_graph` credential-készletre épül, tehát ezek
// KONFIGURÁCIÓ, nem külön hitelesítő adat.
function EmailAccountsPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const companyQ = useQuery({ queryKey: ['company'], queryFn: companyApi.get });
  const [invoiceEmail, setInvoiceEmail] = useState('');
  const [payrollSender, setPayrollSender] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  // Gábor döntése (2026-09-05): a könyvelő címe is ide tartozik — nem csak címzett
  // (csomagküldés), hanem a könyvelőtől érkező / általad továbbított levelek
  // felismerésének alapja is (`classify_email`: feladó vagy a törzsben szereplő cím).
  const [acctEmail, setAcctEmail] = useState('');
  const initRef = useRef(false);

  useEffect(() => {
    const c = companyQ.data;
    if (!initRef.current && c) {
      setInvoiceEmail(c.invoice_email || '');
      setPayrollSender(c.payroll_sender || '');
      setSenderEmail(c.sender_email || '');
      setAcctEmail(c.accountant_email || '');
      initRef.current = true;
    }
  }, [companyQ.data]);

  const setMut = useMutation({
    mutationFn: companyApi.patch,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['company'] }),
  });
  const save = () => {
    setMut.mutateAsync({
      invoice_email:    invoiceEmail.trim(),
      payroll_sender:   payrollSender.trim(),
      sender_email:     senderEmail.trim(),
      accountant_email: acctEmail.trim(),
    }).then(() => toast.success('E-mail beállítások mentve'))
      .catch(err => toast.error('Mentés sikertelen: '
        + (err?.response?.data?.detail || err.message)));
  };

  return (
    <Card title="Postafiókok" subtitle="Bejövő bizonylatok, bérszámfejtés és a küldő fiók, egy közös Microsoft 365 kapcsolaton.">
      {companyQ.isLoading ? (
        <p style={{color:'var(--text-muted)',fontSize:'0.82rem'}}>Betöltés…</p>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:12,maxWidth:500}}>
          <div>
            <Input label="Bejövő számlák postafiókja" type="email"
              value={invoiceEmail}
              onChange={e => setInvoiceEmail(e.target.value)}
              placeholder="invoice@ceged.hu"/>
            <div style={{fontSize:'0.72rem',color:'var(--text-muted)',marginTop:4,lineHeight:1.45}}>
              Ezt a postafiókot olvassa az e-mail pipeline a beérkező bizonylatokért.
            </div>
          </div>
          <div>
            <Input label="Bérszámfejtő e-mail címe" type="email"
              value={payrollSender}
              onChange={e => setPayrollSender(e.target.value)}
              placeholder="berszamfejto@pelda.hu"/>
            <div style={{fontSize:'0.72rem',color:'var(--text-muted)',marginTop:4,lineHeight:1.45}}>
              Erről a címről érkező levelet ismerjük fel bérszámfejtésként.
            </div>
          </div>
          <div>
            <Input label="Könyvelő e-mail címe" type="email"
              value={acctEmail}
              onChange={e => setAcctEmail(e.target.value)}
              placeholder="konyvelo@pelda.hu"/>
            <div style={{fontSize:'0.72rem',color:'var(--text-muted)',marginTop:4,lineHeight:1.45}}>
              A könyvelési csomagok címzettje, ÉS erről a címről (vagy ezt a címet a
              törzsben hordozó, általad továbbított levélből) ismerjük fel a könyvelői
              adó-tételeket.
            </div>
          </div>
          <div>
            <Input label="Küldő postafiók / OneDrive tulajdonos" type="email"
              value={senderEmail}
              onChange={e => setSenderEmail(e.target.value)}
              placeholder="te@ceged.hu"/>
            <div style={{fontSize:'0.72rem',color:'var(--warning)',marginTop:4,lineHeight:1.45}}>
              ⚠ A rendszer ERRŐL a postafiókról küld leveleket, és <b>ennek a
              OneDrive-jára</b> mentjük a PDF-eket. Átírása után a korábban feltöltött
              fájlok a régi helyen maradnak.
            </div>
          </div>
          {companyQ.data?.onedrive_root_folder && (
            <div style={{fontSize:'0.72rem',color:'var(--text-muted)',lineHeight:1.45,
              padding:'0.5rem 0.65rem',background:'var(--bg-surface)',
              border:'1px solid var(--border-dim)',borderRadius:'var(--radius)'}}>
              <b>OneDrive gyökér:</b>{' '}
              <span style={{fontFamily:'var(--mono)'}}>{companyQ.data.onedrive_root_folder}</span>
              {' '}— szándékosan nem szerkeszthető: a bizonylatok útvonala ehhez képest
              relatív, az átírása minden korábbi PDF-et elérhetetlenné tenne.
            </div>
          )}
          <div>
            <Btn loading={setMut.isPending} disabled={setMut.isPending}
              onClick={save}>Mentés</Btn>
          </div>
        </div>
      )}
    </Card>
  );
}


// ── Megjelenítés (skin-váltó: PhenoFin ⟷ Raymond) ──────────────────────────
// A Raymond a fehércímkés banki skin (munkacím) – a myRaiffeisen portál
// token-leltára alapján (raw/RAYMOND_DESIGN_TOKENS.md). Technika: a gyökér-
// elem data-skin attribútuma vált tokenkészletet (index.css), a választást a
// DB Setting (ui_skin) őrzi; az alkalmazást a Layout végzi betöltéskor.
// D7 (2026-09-06): a bérlő saját adatának exportja — CSV adatkörönként vagy egy ZIP.
// ADMIN jog kell (a backend ellenőrzi), minden letöltés audit-sort ír. Nem a könyvelői
// csomag: ez a NYERS tábla-tartalom, minden oszloppal (bezártság-félelem ellen).
function DataExportPanel() {
  const toast = useToast();
  const [busy, setBusy] = useState(null);
  const manifestQ = useQuery({ queryKey: ['export-manifest'], queryFn: exportApi.manifest, retry: false });
  const forbidden = manifestQ.error?.response?.status === 403;
  const dl = async (key) => {
    setBusy(key);
    try {
      const name = await exportApi.download(key);
      toast.success(`Letöltve: ${name}`);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? 'Adatexporthoz ADMIN jogosultság kell.' : 'Az export sikertelen.');
    } finally { setBusy(null); }
  };
  if (forbidden) return null;
  const ds = manifestQ.data?.datasets || [];
  const total = ds.reduce((a, d) => a + (d.rows || 0), 0);
  return (
    <Card title="Adatexport" subtitle="A cég minden adata CSV-ben (UTF-8, pontosvesszős, Excel-barát) vagy egy ZIP-ben. Az adat a tiéd — bármikor kiviheted."
      actions={<Btn size="sm" icon={Download} loading={busy === 'all'} onClick={() => dl('all')}>Minden adat (ZIP)</Btn>}
      style={{marginTop:'0.75rem'}}>
      <QueryState query={manifestQ} empty={!ds.length} emptyText="Nincs exportálható adat." errorText="Az exportálható adatkörök betöltése sikertelen.">
        {() => (
          <>
            <div style={{fontSize:'0.72rem',color:'var(--text-muted)',marginBottom:8}}>
              {ds.length} adatkör · {huFmt(total, 0)} sor összesen · minden letöltés naplózódik
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(260px, 1fr))',gap:6}}>
              {ds.map(d => (
                <div key={d.key} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,
                  padding:'6px 10px',border:'1px solid var(--border-dim)',borderRadius:'var(--radius)',background:'var(--bg-input)'}}>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:'0.8rem',fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{d.label}</div>
                    <div style={{fontSize:'0.68rem',color:'var(--text-dim)',fontFamily:'var(--mono)'}}>{d.key} · {huFmt(d.rows, 0)} sor · {d.columns} oszlop</div>
                  </div>
                  <Btn size="sm" variant="ghost" icon={Download} loading={busy === d.key} disabled={!d.rows} onClick={() => dl(d.key)}>CSV</Btn>
                </div>
              ))}
            </div>
          </>
        )}
      </QueryState>
    </Card>
  );
}

function AppearancePanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: settings = {} } = useQuery({ queryKey: ['settings'], queryFn: settingsApi.get });
  const isRaymond = settings.ui_skin === 'raymond';
  const setMut = useMutation({
    mutationFn: ({ key, value }) => settingsApi.set(key, value),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['settings'] }); toast.success('Megjelenítés mentve'); },
    onError: (err) => toast.error('Mentés sikertelen: ' + (err?.response?.data?.detail || err.message)),
  });
  const apply = (raymond) => {
    // azonnali vizuális váltás + perzisztálás
    if (raymond) document.documentElement.setAttribute('data-skin', 'raymond');
    else document.documentElement.removeAttribute('data-skin');
    setMut.mutate({ key: 'ui_skin', value: raymond ? 'raymond' : 'phenofin' });
  };
  return (
    <Card title="Megjelenítés"
      subtitle="A felület arculata. A Raymond a fehércímkés banki skin (munkacím) – a chartok és a szemantikus színek változatlanok.">
      <div style={{display:'flex',alignItems:'center',gap:14}}>
        <span style={{fontSize:'0.85rem',fontWeight: isRaymond ? 400 : 700,
          color: isRaymond ? 'var(--text-muted)' : 'var(--text)'}}>PhenoFin</span>
        <Toggle checked={isRaymond} onChange={apply}
          onColor="#fee600" offColor="var(--primary)"/>
        <span style={{fontSize:'0.85rem',fontWeight: isRaymond ? 700 : 400,
          color: isRaymond ? 'var(--text)' : 'var(--text-muted)'}}>Raymond</span>
        {setMut.isPending && <Spinner size={13}/>}
      </div>
    </Card>
  );
}


// ── Chat beállítások (D-WP-08) + Telegram (WP-02) ───────────────────────
//
// B3 döntés (telegram-settings-ui.md): a Chat oldal KÉT fület kap — "Chat
// beállítások" (kvóta + adatvédelmi tájékoztató, D-WP-08, változatlan) és
// "Telegram" (kapcsolódás-életciklus, WP-02, új).
//
// ⚠⚠ SZÁNDÉKOS HATÓKÖR-KORLÁTOZÁS a "Chat beállítások" fülön: az ADR-06
// tartalom-osztály-szűrés (dashboard-chat.md, "Data-driven filtering by
// content class") NINCS megépítve — nincs `chat_binding_content_policy`-szerű
// tábla, nincs resolver, nincs toggle. Ezt a fület ezért csak OLVASHATÓ
// adatvédelmi tájékoztatóval építettük meg, nem szerkeszthető beállítással —
// egy kitalált content-class kapcsoló itt egy soha nem hatóan érvényesülő,
// hazug UI-t adna. Ha a tulajdonos dönt egy ilyen finomhangolásról, az egy
// külön kör (új tábla + resolver + tool-oldali kikényszerítés).
function ChatSettingsPanel() {
  const [tab, setTab] = useState('settings');
  const segS = active => ({
    background: active ? 'var(--tab-active-bg)' : 'transparent',
    color: active ? 'var(--tab-active-fg)' : 'var(--text-muted)',
    border: 'none', borderRadius: 8, padding: '0.35rem 0.85rem',
    fontSize: '0.8rem', cursor: 'pointer', fontWeight: active ? 600 : 400,
  });
  // ⏸⏸ TELEGRAM KIVEZETVE a UI-ról (2026-09-05, Gábor döntése) — a chat a
  //   MOBILAPPBA költözik. A `TelegramSettingsTab` KÓDJA MEGMARAD alább, csak
  //   nem érhető el: a fül-váltó és a fül-gomb kikommentelve. A backend
  //   `/api/chat-settings` routere is ki van kapcsolva (`app/main.py`), tehát a
  //   fül visszakapcsolása ÖNMAGÁBAN nem elég — ott is vissza kell tenni.
  //   Visszaállítás: ezt a blokkot cseréld vissza az alatta lévő eredetire.
  return (
    <div style={{display:'flex', flexDirection:'column', gap:16}}>
      <ChatQuotaPrivacyTab/>
    </div>
  );
  /* EREDETI (Telegram-füllel) — visszaállításkor ez lép életbe:
  return (
    <div style={{display:'flex', flexDirection:'column', gap:16}}>
      <div style={{ display: 'inline-flex', gap: 4, background: 'var(--bg-input)',
        border: '1px solid var(--border)', borderRadius: 11, padding: 4, width: 'fit-content' }}>
        <button onClick={() => setTab('settings')} style={segS(tab === 'settings')}>Chat beállítások</button>
        <button onClick={() => setTab('telegram')} style={segS(tab === 'telegram')}>Telegram</button>
      </div>
      {tab === 'settings' ? <ChatQuotaPrivacyTab/> : <TelegramSettingsTab/>}
    </div>
  );
  */
}

function ChatCostCard() {
  const costQ = useQuery({ queryKey: ['chat-cost', 30], queryFn: () => getChatCost(30) });
  const d = costQ.data;
  const usd = (v, dec = 2) => `${Number(v || 0).toFixed(dec)} USD`;
  return (
    <Card title="Chat-költség (mérés)"
      subtitle="Lista-áras becslés a naplózott token-számokból (USD / 1M token). Nem számla: a cache-kedvezmény nincs benne. Csak számlálók — a kérdések szövege nincs tárolva.">
      <QueryState query={costQ} empty={!d?.models?.length} emptyText="Még nem volt chat-hívás ennél a cégnél."
        errorText="A chat-költség betöltése sikertelen.">
        {() => (
          <div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))',gap:8,marginBottom:10}}>
              {[
                ['Utolsó 30 nap', `${d.recent.calls} hívás · ${usd(d.recent.est_cost_usd, 3)}`],
                ['Napi átlag', usd(d.recent.est_cost_usd_per_day, 4)],
                ['Havi kivetítés', usd(d.recent.projection_month_usd)],
                ['Kvóta', `${d.quota_per_user_per_day} kérdés / fő / nap`],
              ].map(([k, v]) => (
                <div key={k} style={{padding:'8px 10px',border:'1px solid var(--border-dim)',borderRadius:'var(--radius)',background:'var(--bg-input)'}}>
                  <div style={{fontSize:'0.66rem',color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.05em'}}>{k}</div>
                  <div style={{fontFamily:'var(--mono)',fontSize:'0.9rem',fontWeight:600}}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.78rem'}}>
                <thead><tr>
                  {['Modell','Hívás','Átl. be / ki token','Átl. kör','Átl. válaszidő','Becsült / hívás','Összes (becsült)'].map(h => (
                    <th key={h} style={{textAlign:'left',padding:'4px 6px',color:'var(--text-muted)',fontSize:'0.64rem',textTransform:'uppercase',borderBottom:'1px solid var(--border)'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {d.models.map(m => (
                    <tr key={m.model}>
                      <td style={{padding:'4px 6px',fontFamily:'var(--mono)'}}>{m.model}{!m.known_price && <span title="Ismeretlen ár — a legdrágább ismert árral becsülve" style={{color:'var(--warning)'}}> ?</span>}</td>
                      <td style={{padding:'4px 6px',fontFamily:'var(--mono)'}}>{m.calls}{m.not_ok ? <span style={{color:'var(--danger)'}}> ({m.not_ok} hibás)</span> : null}</td>
                      <td style={{padding:'4px 6px',fontFamily:'var(--mono)'}}>{m.avg_input_per_call.toLocaleString('hu-HU')} / {m.avg_output_per_call.toLocaleString('hu-HU')}</td>
                      <td style={{padding:'4px 6px',fontFamily:'var(--mono)'}}>{m.avg_rounds}</td>
                      <td style={{padding:'4px 6px',fontFamily:'var(--mono)'}}>{(m.avg_latency_ms/1000).toFixed(1)} s</td>
                      <td style={{padding:'4px 6px',fontFamily:'var(--mono)'}}>{usd(m.est_cost_usd_per_call, 4)}</td>
                      <td style={{padding:'4px 6px',fontFamily:'var(--mono)',fontWeight:600}}>{usd(m.est_cost_usd_total, 3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{fontSize:'0.7rem',color:'var(--text-dim)',marginTop:8}}>
              Viszonyítás (adat-audit, 2026-08): a teljes dokumentum-feldolgozó futószalag ~0,69 USD / cég / hó.
              A chat rendszerpromptja + eszköz-sémái körönként újraküldve mennek — ez az egyetlen prompt, ami a
              prompt-cache küszöbét hihetően átlépi (mérendő).
            </div>
          </div>
        )}
      </QueryState>
    </Card>
  );
}

function ChatQuotaPrivacyTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { data: quota, isLoading } = useQuery({
    queryKey: ['chat-quota'],
    queryFn: getChatQuota,
    refetchInterval: 30000,
  });
  const used = quota?.used ?? 0;
  const limit = quota?.limit ?? 100;
  const pct = Math.min(100, Math.round((used / Math.max(limit, 1)) * 100));

  const deleteMut = useMutation({
    mutationFn: deleteChatHistory,
    onSuccess: (data) => {
      toast.success(`${data.deleted} beszélgetés-sor törölve.`);
      qc.invalidateQueries({ queryKey: ['chat-quota'] });
    },
    onError: (e) => toast.error(e.message || 'Nem sikerült törölni.'),
  });

  return (
    <div style={{display:'flex', flexDirection:'column', gap:16}}>
      <Card title="Napi kérdés-kvóta"
        subtitle="Minden felhasználónak saját, napi keret jár — szerepkörtől függetlenül (Europe/Budapest szerinti nap).">
        {isLoading ? <Spinner size={14}/> : (
          <div>
            <div style={{display:'flex', justifyContent:'space-between', fontSize:'0.85rem', marginBottom:6}}>
              <span>{used} / {limit} kérdés ma</span>
              {used >= limit && <span style={{color:'var(--danger)', fontWeight:600}}>Elérve</span>}
            </div>
            <div style={{height:8, borderRadius:999, background:'var(--bg-input)', overflow:'hidden'}}>
              <div style={{height:'100%', width:`${pct}%`, borderRadius:999,
                background: pct >= 100 ? 'var(--danger)' : 'var(--brand, var(--primary))',
                transition:'width 0.2s'}}/>
            </div>
          </div>
        )}
      </Card>

      {/* E4 (2026-09-06): a chat-csatorna költsége — eddig sehol nem volt kiértékelve */}
      <ChatCostCard/>

      <Card title="Adatvédelem" subtitle="Amit a chatről tudni érdemes.">
        <ul style={{margin:0, paddingLeft:'1.1rem', fontSize:'0.84rem', color:'var(--text)',
          lineHeight:1.8}}>
          <li>A beszélgetéseidet <b>1 évig</b> tároljuk, utána automatikusan törlődnek.</li>
          <li>A beszélgetésedet a cégen belül <b>más nem látja</b> — a chat személyes, nem
            csapat-szintű funkció.</li>
          <li>A chat <b>nem tud utalást indítani, adatot menteni vagy törölni</b> — csak
            olvas és válaszol.</li>
          <li>A válaszok egy külső AI-modelltől (Google Gemini) származnak; a rendszer erre
            minden beszélgetés elején figyelmeztet.</li>
          <li>Ugyanez az előzmény jelenik meg <b>Telegramon is</b> — a két csatorna közös
            beszélgetés-előzményt és napi kvótát használ.</li>
        </ul>
        <div style={{marginTop:14, paddingTop:14, borderTop:'1px solid var(--border-dim)'}}>
          <Btn variant="ghost" onClick={() => setConfirmDelete(true)} loading={deleteMut.isPending}>
            <Trash2 size={14} style={{marginRight:6}}/> Előzmény törlése most
          </Btn>
        </div>
      </Card>

      <ConfirmDialog open={confirmDelete}
        title="Beszélgetés-előzmény törlése"
        message="Ez törli a teljes beszélgetés-előzményedet — a Dashboardon ÉS Telegramon egyaránt. A törlés végleges, és nem érinti a többi kollégád beszélgetését."
        danger confirmLabel="Törlés"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => { setConfirmDelete(false); deleteMut.mutate(); }}/>
    </div>
  );
}

// ── Telegram (WP-02) ─────────────────────────────────────────────────────
//
// ⚠ A ma élő végpontok csak az App-oldali felet adják (nonce-kiadás, a
// SEC-NEW-02/B3 kétoldali megerősítés App-oldali fele, kötés-lista, kézi
// lecsatlakoztatás) — a TÉNYLEGES `/start <nonce>` Telegram-üzenet fogadása
// (webhook) egy jövőbeli work package (WP-03) dolga. Amíg az nincs meg, a
// "Csatlakoztatás" gomb linket ad, de a link megnyitása után a "Te voltál?"
// megerősítő kártya NEM fog itt megjelenni — az élő beváltás a webhook
// hiányában nem történik meg.
const TELEGRAM_STATE_LABELS = {
  PENDING: 'Függőben', ACTIVE: 'Aktív', SUSPENDED: 'Felfüggesztve',
  ORPHANED: 'Elárvult', REVOKED: 'Lecsatlakoztatva', EXPIRED: 'Lejárt',
};
const TELEGRAM_STATE_VARIANTS = {
  ACTIVE: 'success', PENDING: 'warning', SUSPENDED: 'warning',
  REVOKED: 'default', ORPHANED: 'danger', EXPIRED: 'default',
};

function TelegramSettingsTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();
  const [nonceLink, setNonceLink] = useState(null);

  const { data: pending } = useQuery({
    queryKey: ['telegram-pending'], queryFn: getTelegramPending, refetchInterval: 5000,
  });
  const { data: bindings, isLoading: bindingsLoading } = useQuery({
    queryKey: ['telegram-bindings'], queryFn: getTelegramBindings,
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['telegram-pending'] });
    qc.invalidateQueries({ queryKey: ['telegram-bindings'] });
  };

  const nonceMut = useMutation({
    mutationFn: issueTelegramNonce,
    onSuccess: (data) => setNonceLink(data.deep_link),
    onError: (e) => toast.error(e.message || 'Nem sikerült a hivatkozást előállítani.'),
  });
  const confirmMut = useMutation({
    mutationFn: confirmTelegramPending,
    onSuccess: () => { invalidateAll(); toast.success('Kapcsolódás megerősítve.'); },
    onError: (e) => toast.error(e.message || 'Nem sikerült megerősíteni.'),
  });
  const disownMut = useMutation({
    mutationFn: disownTelegramPending,
    onSuccess: () => { invalidateAll(); toast.success('Elutasítva.'); },
    onError: (e) => toast.error(e.message || 'Nem sikerült elutasítani.'),
  });
  const revokeMut = useMutation({
    mutationFn: revokeTelegramBinding,
    onSuccess: () => { invalidateAll(); toast.success('Lecsatlakoztatva.'); },
    onError: (e) => toast.error(e.message || 'Nem sikerült lecsatlakoztatni.'),
  });
  const settingsMut = useMutation({
    mutationFn: ({ id, fields }) => updateTelegramBinding(id, fields),
    onSuccess: () => invalidateAll(),
    onError: (e) => toast.error(e.message || 'Nem sikerült menteni.'),
  });
  const testMut = useMutation({
    mutationFn: sendTelegramTestMessage,
    onSuccess: (data) => data.ok
      ? toast.success('Teszt-üzenet elküldve — nézd meg a Telegramot.')
      : toast.error(data.error || 'Nem sikerült elküldeni.'),
    onError: (e) => toast.error(e.message || 'Nem sikerült elküldeni.'),
  });

  return (
    <div style={{display:'flex', flexDirection:'column', gap:16}}>
      {(pending || []).map(p => (
        <Card key={p.id} title="Új Telegram-kapcsolódás"
          subtitle={`${p.consumed_platform_display || 'Ismeretlen Telegram-fiók'} szeretne csatlakozni ehhez a céghez.`}>
          <div style={{fontSize:'0.8rem', color:'var(--text-muted)', marginBottom:10}}>
            Te indítottad ezt a Telegramban?
          </div>
          <div style={{display:'flex', gap:8}}>
            <Btn onClick={() => confirmMut.mutate(p.id)} loading={confirmMut.isPending}>
              <Check size={16} style={{marginRight:6}}/> Igen, ez én voltam
            </Btn>
            <Btn variant="ghost" onClick={() => disownMut.mutate(p.id)} loading={disownMut.isPending}>
              <X size={16} style={{marginRight:6}}/> Nem én voltam
            </Btn>
          </div>
        </Card>
      ))}

      <Card title="Csatlakoztatás"
        subtitle="Kösd össze a fiókodat a puo Telegram-botjával, hogy a chatet Telegramon is elérd.">
        {nonceLink ? (
          <div style={{display:'flex', flexDirection:'column', gap:8}}>
            <a href={nonceLink} target="_blank" rel="noreferrer"
              style={{display:'inline-flex', alignItems:'center', gap:6, color:'var(--primary)',
                fontWeight:600, fontSize:'0.85rem'}}>
              Megnyitás Telegramban <ExternalLink size={14}/>
            </a>
            <span style={{fontSize:'0.72rem', color:'var(--text-muted)'}}>
              A hivatkozás 15 percig érvényes, és csak egyszer használható.
            </span>
          </div>
        ) : (
          <Btn onClick={() => nonceMut.mutate()} loading={nonceMut.isPending}>Csatlakoztatás</Btn>
        )}
      </Card>

      <Card title="Kapcsolódások" subtitle="A cégedhez kötött Telegram-kapcsolatok.">
        {bindingsLoading ? <Spinner size={14}/> : (bindings || []).length === 0 ? (
          <div style={{fontSize:'0.82rem', color:'var(--text-muted)'}}>Még nincs Telegram-kapcsolat.</div>
        ) : (
          <div style={{display:'flex', flexDirection:'column', gap:2}}>
            {bindings.map(b => (
              <TelegramBindingRow key={b.id} b={b} isOwn={b.created_by_user_id === user?.id}
                onRevoke={() => revokeMut.mutate(b.id)} revoking={revokeMut.isPending}
                onSaveSettings={(fields) => settingsMut.mutate({ id: b.id, fields })}
                savingSettings={settingsMut.isPending}
                onTest={() => testMut.mutate(b.id)} testing={testMut.isPending}/>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function TelegramBindingRow({ b, isOwn, onRevoke, revoking, onSaveSettings, savingSettings, onTest, testing }) {
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({
    sensitive_enabled: b.sensitive_enabled, quiet_from: (b.quiet_from || '20:00').slice(0, 5),
    quiet_to: (b.quiet_to || '08:00').slice(0, 5), skip_weekends: b.skip_weekends,
  });
  const dirty = form.sensitive_enabled !== b.sensitive_enabled
    || form.quiet_from !== (b.quiet_from || '20:00').slice(0, 5)
    || form.quiet_to !== (b.quiet_to || '08:00').slice(0, 5)
    || form.skip_weekends !== b.skip_weekends;
  const canManage = isOwn && b.state === 'ACTIVE';

  return (
    <div style={{padding:'0.55rem 0', borderBottom:'1px solid var(--border-dim)'}}>
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
        <div style={{display:'flex', alignItems:'center', gap:10, cursor: canManage ? 'pointer' : 'default'}}
          onClick={() => canManage && setExpanded(v => !v)}>
          <span style={{fontSize:'0.84rem', fontWeight:600}}>{b.chat_title || 'Privát chat'}</span>
          <Badge variant={TELEGRAM_STATE_VARIANTS[b.state] || 'default'}>
            {TELEGRAM_STATE_LABELS[b.state] || b.state}
          </Badge>
          {/* M13 (2026-09-05): egy Telegram-chat több céghez is köthető; a ✓ azt jelzi,
              hogy a chat MOST erről a cégről beszél (a /cegek gombbal váltható). */}
          {b.state === 'ACTIVE' && b.is_current_for_chat && (
            <Badge variant="primary">✓ ezzel a céggel beszél</Badge>
          )}
          {b.state === 'ACTIVE' && b.is_current_for_chat === false && (
            <span style={{fontSize:'0.7rem', color:'var(--text-muted)'}}
              title="A chat most egy másik cégeddel beszél — Telegramban a /cegek paranccsal válthatsz.">
              — a chat most másik cégre áll (/cegek)
            </span>
          )}
          {!isOwn && b.state !== 'REVOKED' && (
            <span style={{fontSize:'0.7rem', color:'var(--text-muted)'}}>— egy másik kolléga kötése</span>
          )}
        </div>
        <div style={{display:'flex', gap:8}}>
          {canManage && (
            <Btn variant="ghost" onClick={() => setExpanded(v => !v)}>
              {expanded ? 'Bezárás' : 'Beállítások'}
            </Btn>
          )}
          {b.state !== 'REVOKED' && isOwn && (
            <Btn variant="ghost" onClick={onRevoke} loading={revoking}>
              <Trash2 size={14} style={{marginRight:6}}/> Lecsatlakoztatás
            </Btn>
          )}
        </div>
      </div>

      {expanded && canManage && (
        <div style={{marginTop:10, padding:'10px 12px', background:'var(--bg-input)',
          borderRadius:'var(--radius)', display:'flex', flexDirection:'column', gap:12}}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
            <div>
              <div style={{fontSize:'0.82rem', fontWeight:600}}>Érzékeny adatok (összeg, partnernév)</div>
              <div style={{fontSize:'0.72rem', color:'var(--text-muted)', maxWidth:380}}>
                Bekapcsolva a Telegram-válaszok konkrét összeget és partnernevet is
                tartalmazhatnak (pl. "ACME Kft. — 45 000 Ft"). Kikapcsolva csak jelzést kapsz
                (pl. "egy utalásod elutasításra került"), összeg/partnernév nélkül.
              </div>
            </div>
            <Toggle checked={form.sensitive_enabled}
              onChange={(v) => setForm(f => ({ ...f, sensitive_enabled: v }))}/>
          </div>

          <div>
            <div style={{fontSize:'0.82rem', fontWeight:600, marginBottom:6}}>Csendes óra</div>
            <div style={{display:'flex', alignItems:'center', gap:8, fontSize:'0.8rem'}}>
              <input type="time" value={form.quiet_from}
                onChange={e => setForm(f => ({ ...f, quiet_from: e.target.value }))}
                style={{background:'var(--bg-card)', border:'1px solid var(--border)',
                  borderRadius:'var(--radius)', padding:'0.3rem 0.4rem', color:'var(--text)'}}/>
              <span style={{color:'var(--text-muted)'}}>–</span>
              <input type="time" value={form.quiet_to}
                onChange={e => setForm(f => ({ ...f, quiet_to: e.target.value }))}
                style={{background:'var(--bg-card)', border:'1px solid var(--border)',
                  borderRadius:'var(--radius)', padding:'0.3rem 0.4rem', color:'var(--text)'}}/>
              <label style={{display:'flex', alignItems:'center', gap:6, marginLeft:12,
                fontSize:'0.78rem', color:'var(--text-muted)', cursor:'pointer'}}>
                <Checkbox checked={form.skip_weekends}
                  onChange={e => setForm(f => ({ ...f, skip_weekends: e.target.checked }))}/>
                Hétvégén se zavarjon
              </label>
            </div>
            <div style={{fontSize:'0.72rem', color:'var(--text-muted)', marginTop:4}}>
              Ebben az időszakban a rendszer nem küld önindított üzenetet — a kérdéseidre
              válaszol, csak a jelzéseket (pl. elutasított utalás) halasztja.
            </div>
          </div>

          <div style={{display:'flex', gap:8, justifyContent:'space-between', alignItems:'center'}}>
            <Btn variant="ghost" onClick={onTest} loading={testing}>Teszt-üzenet küldése</Btn>
            <Btn onClick={() => onSaveSettings({
              sensitive_enabled: form.sensitive_enabled, quiet_from: form.quiet_from,
              quiet_to: form.quiet_to, skip_weekends: form.skip_weekends,
            })} disabled={!dirty} loading={savingSettings}>Mentés</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Rendszeres szállítók (Beállítások fül) ─────────────────────────────────
const FREQ_LABELS = { monthly:'havi', quarterly:'negyedéves', yearly:'éves' };

function RecurringVendorRow({ p, months, onSave, saving }) {
  const [form, setForm] = useState({
    is_recurring_vendor: p.is_recurring_vendor,
    invoice_frequency: p.invoice_frequency || 'monthly',
    expected_monthly_min: p.expected_min ?? '',
    expected_monthly_max: p.expected_max ?? '',
    portal_url: p.portal_url ?? '',
  });
  const dirty = form.is_recurring_vendor !== p.is_recurring_vendor
    || form.invoice_frequency !== (p.invoice_frequency || 'monthly')
    || String(form.expected_monthly_min) !== String(p.expected_min ?? '')
    || String(form.expected_monthly_max) !== String(p.expected_max ?? '')
    || (form.portal_url||'') !== (p.portal_url||'');

  const numOrNull = v => (v === '' || v == null) ? null : parseInt(v, 10);
  const tdS = { padding:'0.4rem 0.5rem', fontSize:'0.82rem', verticalAlign:'middle',
    borderBottom:'1px solid var(--border-dim)' };
  const inS = { background:'var(--bg-input)', border:'1px solid var(--border)',
    borderRadius:'var(--radius)', padding:'0.3rem 0.4rem', color:'var(--text)',
    fontSize:'0.8rem' };

  return (
    <tr style={{opacity: form.is_recurring_vendor ? 1 : 0.55}}>
      <td style={tdS}>
        <input type="checkbox" checked={form.is_recurring_vendor}
          onChange={e=>setForm(f=>({...f,is_recurring_vendor:e.target.checked}))}
          style={{accentColor:'var(--primary)'}}/>
      </td>
      <td style={{...tdS,fontWeight:600}}>
        {p.name}
        {p.requires_portal_download && (
          <span title="Portálról letöltendő – semmi nem hozza be automatikusan"
            style={{marginLeft:6,fontSize:'0.62rem',color:'var(--warning)',
              border:'1px solid var(--warning)',borderRadius:4,padding:'0 4px'}}>portál</span>
        )}
      </td>
      {months.map(m => (
        <td key={m} style={{...tdS,fontFamily:'var(--mono)',textAlign:'center',
          color: p.counts[m] ? 'var(--text)' : 'var(--text-dim)'}}>
          {p.counts[m]}
        </td>
      ))}
      <td style={tdS}>
        <select value={form.invoice_frequency}
          onChange={e=>setForm(f=>({...f,invoice_frequency:e.target.value}))}
          style={{...inS, cursor:'pointer'}}>
          <option value="monthly">havi</option>
          <option value="quarterly">negyedéves</option>
          <option value="yearly">éves</option>
        </select>
      </td>
      <td style={tdS}>
        <input type="number" min="0" value={form.expected_monthly_min}
          placeholder={String(p.suggested_min)}
          onChange={e=>setForm(f=>({...f,expected_monthly_min:e.target.value}))}
          style={{...inS, width:52, textAlign:'center'}}/>
      </td>
      <td style={tdS}>
        <input type="number" min="0" value={form.expected_monthly_max}
          placeholder={String(p.suggested_max)}
          onChange={e=>setForm(f=>({...f,expected_monthly_max:e.target.value}))}
          style={{...inS, width:52, textAlign:'center'}}/>
      </td>
      <td style={tdS}>
        <input type="url" value={form.portal_url} placeholder="https://…"
          onChange={e=>setForm(f=>({...f,portal_url:e.target.value}))}
          style={{...inS, width:'100%', minWidth:140}}/>
      </td>
      <td style={{...tdS,fontSize:'0.74rem',whiteSpace:'nowrap'}}>
        {/* B3 (2026-09-06): a tényadatból számolt sáv — a felhasználó FOGADJA EL, nem automatika */}
        {p.suggestion?.is_recurring ? (
          <span title={p.suggestion.reason} style={{display:'inline-flex',alignItems:'center',gap:6}}>
            <span style={{color:'var(--text-muted)'}}>
              {{monthly:'havi',quarterly:'negyedéves',yearly:'éves'}[p.suggestion.frequency]} {p.suggestion.min}–{p.suggestion.max}
              <span style={{color:'var(--text-dim)'}}> · {p.suggestion.months_active} hó</span>
            </span>
            {(!p.is_recurring_vendor || p.invoice_frequency !== p.suggestion.frequency
              || p.expected_min !== p.suggestion.min || p.expected_max !== p.suggestion.max) && (
              <Btn size="sm" variant="secondary" loading={saving}
                title={`Elfogadás: ${p.suggestion.reason}`}
                onClick={()=>onSave(p.partner_id, {
                  is_recurring_vendor: true,
                  invoice_frequency: p.suggestion.frequency,
                  expected_monthly_min: p.suggestion.min,
                  expected_monthly_max: p.suggestion.max,
                  portal_url: form.portal_url.trim() || null,
                })}>Elfogad</Btn>
            )}
          </span>
        ) : (
          <span title={p.suggestion?.reason || ''} style={{color:'var(--text-dim)'}}>—</span>
        )}
      </td>
      <td style={{...tdS,textAlign:'right'}}>
        <Btn size="sm" variant={dirty?'primary':'ghost'} disabled={!dirty} loading={saving}
          onClick={()=>onSave(p.partner_id, {
            is_recurring_vendor: form.is_recurring_vendor,
            invoice_frequency: form.invoice_frequency,
            expected_monthly_min: numOrNull(form.expected_monthly_min),
            expected_monthly_max: numOrNull(form.expected_monthly_max),
            portal_url: form.portal_url.trim() || null,
          })}>Mentés</Btn>
      </td>
    </tr>
  );
}

/* Partner-törzs (2026-09-06, migráció a5b6c7d8e9f0). A céget az ADÓSZÁM (törzsszám)
 * azonosítja, külföldinél ország + VAT; egy partnernek több IBAN-ja lehet devizánként.
 *
 * ⚠⚠ 2026-09-16 (K9): a „Megerősítve" OSZLOP ÉS PIPA INNEN ELTÁVOLÍTVA. A megerősítés
 * mint felhasználói fogalom megszűnt — a `partner_identity.resolve()` név-ága a
 * `verified_at` feltétel nélkül fut, az egyértelműség (egy kanonikus név → pontosan egy
 * partner) marad az egyetlen kapu, és a döntés provenienciája a bizonylaton látszik
 * (`documents.partner_match_source`). Egy 299 soros megerősítési munkalista rossz üzlet
 * lett volna: a névre illesztés tétje MÉRVE 8 bizonylat (0,6%).
 *
 * A teljes partner-nézet a `/partners` oldalon van; ez a panel a törzs technikai
 * (azonosító/fajta/IBAN) nézete marad, amíg a beolvasztás meg nem történik. */
function PartnerMasterPanel() {
  const q = useQuery({ queryKey: ['partners'], queryFn: partnersApi.list });
  const partners = q.data ?? [];
  const [filter, setFilter] = useState('');
  const KIND = { company:'cég', sole_trader:'egyéni vállalkozó', foreign:'külföldi',
                 individual:'magánszemély', authority:'hatóság', unknown:'ismeretlen' };
  const thS = { padding:'0.5rem 0.6rem', color:'var(--text-muted)', fontWeight:600,
    fontSize:'0.66rem', textTransform:'uppercase', letterSpacing:'0.05em',
    borderBottom:'1px solid var(--border)', textAlign:'left' };
  const tdS = { padding:'0.5rem 0.6rem', fontSize:'0.8rem', verticalAlign:'top',
    borderBottom:'1px solid var(--border-dim)' };
  const f = filter.trim().toLowerCase();
  const rows = partners.filter(p => !f || (p.name||'').toLowerCase().includes(f)
    || (p.tax_root||'').includes(f) || (p.foreign_vat_number||'').toLowerCase().includes(f)
    || (p.bank_accounts||[]).some(a => a.iban.toLowerCase().includes(f)));
  return (
    <Card title="Partner-törzs"
      subtitle="A céget az adószám (törzsszám) azonosítja, külföldinél az ország + VAT-szám. Egy partnernek több bankszámlája lehet devizánként — az IBAN-váltás új sorként látszik, dátummal. Névre akkor azonosítunk, ha a kanonikus név PONTOSAN EGY partnerre illeszkedik."
      actions={<Input placeholder="Keresés: név, törzsszám, VAT, IBAN" value={filter}
        onChange={e=>setFilter(e.target.value)} style={{width:260}}/>}>
      <QueryState query={q} empty={partners.length === 0}
        emptyText="Még nincs partner a törzsben. A bizonylatokból származtatott javaslat (299 partner) a raw/sessions/2026-09-06-partner-torzs-szaraz-futas.md riportban vár jóváhagyásra."
        errorText="A partnerek betöltése sikertelen.">
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr>
              <th style={thS}>Partner</th><th style={thS}>Azonosító</th><th style={thS}>Fajta</th>
              <th style={thS}>Bankszámlák</th><th style={thS}>Kategória</th>
            </tr></thead>
            <tbody>
              {rows.map(p => (
                <tr key={p.id}>
                  <td style={tdS}>
                    <div style={{fontWeight:600}}>{p.name}</div>
                    {p.notes && <div style={{fontSize:'0.68rem',color:'var(--text-dim)',marginTop:2,maxWidth:320}}>{p.notes}</div>}
                  </td>
                  <td style={{...tdS,fontFamily:'var(--mono)',fontSize:'0.72rem'}}>
                    {p.tax_root ? `HU ${p.tax_root}` : (p.foreign_vat_number ? `${p.country_code||'?'} ${p.foreign_vat_number}` : <span style={{color:'var(--text-dim)'}}>– nincs –</span>)}
                  </td>
                  <td style={tdS}>{KIND[p.partner_kind] || <span style={{color:'var(--text-dim)'}}>–</span>}</td>
                  <td style={{...tdS,fontFamily:'var(--mono)',fontSize:'0.7rem'}}>
                    {(p.bank_accounts||[]).length === 0
                      ? (p.bank_account_iban ? <div>{fmtIban(p.bank_account_iban)}</div> : <span style={{color:'var(--text-dim)'}}>–</span>)
                      : p.bank_accounts.map(a => (
                        <div key={a.id} title={`${a.seen_count} bizonylat · ${a.first_seen||'?'} – ${a.last_seen||'?'}`}
                          style={{opacity: a.is_primary ? 1 : 0.65}}>
                          {a.is_primary ? '★ ' : ''}{fmtIban(a.iban)} <span style={{color:'var(--text-muted)'}}>{a.currency}</span>
                        </div>
                      ))}
                  </td>
                  <td style={tdS}>{p.default_category ? <Badge>{p.default_category}</Badge> : <span style={{color:'var(--text-dim)'}}>–</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </QueryState>
    </Card>
  );
}


function RecurringVendorsPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const recQ = useQuery({
    queryKey: ['recurring-stats'],
    queryFn: accountantApi.recurringStats,
  });
  const { data } = recQ;
  const invalidate = () => qc.invalidateQueries({ queryKey:['recurring-stats'] });
  const updateMut = useMutation({
    mutationFn: ({id, d}) => partnersApi.update(id, d),
    onSuccess: () => { invalidate(); toast.success('Mentve'); },
    onError: (err) => toast.error('Mentés sikertelen: ' + (err?.response?.data?.detail || err.message)),
  });
  const createMut = useMutation({
    mutationFn: partnersApi.create,
    onSuccess: invalidate,
  });

  const thS = { padding:'0.45rem 0.5rem', color:'var(--text-muted)', fontWeight:600,
    fontSize:'0.65rem', textTransform:'uppercase', letterSpacing:'0.05em',
    borderBottom:'1px solid var(--border)', textAlign:'left' };

  // ⚠ Korábban `isLoading || !data` → egyetlen „Betöltés…" ág. Hibánál a `data`
  // undefined maradt, tehát a panel ÖRÖKRE töltésben állt, hibaüzenet nélkül.
  if (recQ.isLoading || recQ.isError || !data) {
    return (
      <Card title="Rendszeres szállítók">
        <QueryState query={recQ}
          errorText="A rendszeres szállítók betöltése sikertelen." />
      </Card>
    );
  }

  const recurring = data.partners.filter(p => p.is_recurring_vendor);
  const others    = data.partners.filter(p => !p.is_recurring_vendor);

  const table = (rows) => (
    <table style={{width:'100%',borderCollapse:'collapse'}}>
      <thead><tr>
        <th style={thS} title="Rendszeres szállító?">✓</th>
        <th style={thS}>Partner</th>
        {data.months.map(m => <th key={m} style={{...thS,textAlign:'center'}}>{m.slice(2)}</th>)}
        <th style={thS}>Gyakoriság</th>
        <th style={thS} title="Várt darab / időszak">Min</th>
        <th style={thS} title="Várt darab / időszak">Max</th>
        <th style={thS}>Ügyfélportál URL</th>
        <th style={thS} title="A tényadatból (24 lezárt hónap) számolt sáv-javaslat">Javaslat</th>
        <th style={thS}></th>
      </tr></thead>
      <tbody>
        {rows.map(p => (
          <RecurringVendorRow key={p.partner_id} p={p} months={data.months}
            onSave={(id,d)=>updateMut.mutate({id,d})} saving={updateMut.isPending}/>
        ))}
      </tbody>
    </table>
  );

  return (
    <>
      <Card title="Rendszeres szállítók"
        subtitle="Akik rendszeresen (havonta / negyedévente / évente) számláznak. A könyvelési ellenőrzés azt nézi, hogy az adott időszakban a várt darabszám-sávban van-e a számla. A negyedéves/éves számlázót csak a megfelelő periódus zárásakor ellenőrzi. A „portál” jelű (e-mailt NEM küldő) szállítónál a hiány erős figyelmeztetés, mert semmi nem hozza be automatikusan. A hónap-oszlopok az utolsó 6 lezárt hónap tényadatai; a Min/Max placeholder a javaslat (havi alapon).">
        {recurring.length === 0
          ? <p style={{fontSize:'0.82rem',color:'var(--text-muted)',marginBottom:10}}>
              Még nincs rendszeres szállító. Pipáld be a lenti listából, vagy vedd fel a javaslatok közül.
            </p>
          : table(recurring)}
      </Card>

      {data.suggestions.length > 0 && (
        <Card title="Javaslatok" subtitle="Gyakori szállítók a számlák közül, amelyekhez még nincs partner rekord.">
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            {data.suggestions.map((s,i) => (
              <div key={i} style={{display:'flex',alignItems:'center',gap:10,fontSize:'0.82rem'}}>
                <span style={{fontWeight:600}}>{s.supplier_name}</span>
                <span style={{color:'var(--text-muted)',fontSize:'0.75rem'}}>
                  {s.months_present}/{data.window_months || 6} hónapban számlázott ({s.total} db)
                </span>
                <Btn size="sm" variant="secondary" icon={Plus} loading={createMut.isPending}
                  onClick={()=>createMut.mutate({ name: s.supplier_name, is_recurring_vendor: true })}>
                  Felvétel rendszeres szállítóként
                </Btn>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card title="További partnerek" subtitle="Pipáld be a ✓ oszlopot, ha a partner rendszeresen számláz és ellenőrizni kell. A „Javaslat” oszlop a 24 hónap tényadatából számolt sáv — az Elfogad gomb egyben be is pipálja."
        actions={(() => {
          const acc = others.filter(p => p.suggestion?.is_recurring);
          return acc.length > 0 ? (
            <Btn size="sm" variant="secondary" loading={updateMut.isPending}
              title={acc.map(p => `${p.name}: ${p.suggestion.reason}`).join('\n')}
              onClick={async () => {
                for (const p of acc) {
                  await updateMut.mutateAsync({ id: p.partner_id, d: {
                    is_recurring_vendor: true, invoice_frequency: p.suggestion.frequency,
                    expected_monthly_min: p.suggestion.min, expected_monthly_max: p.suggestion.max,
                    portal_url: p.portal_url || null } });
                }
              }}>Összes javaslat elfogadása ({acc.length})</Btn>
          ) : null;
        })()}>
        {others.length === 0
          ? <p style={{fontSize:'0.82rem',color:'var(--text-muted)'}}>Nincs több partner.</p>
          : table(others)}
      </Card>
    </>
  );
}


/* Card vagy sima div — MODUL-SZINTŰ komponens, mert egy renderen belül definiált
 * wrapper minden renderen új típus lenne, és a React újramountolná a gyerekeit
 * (elveszne az inputok fókusza/állapota). */
function MaybeCard({ embedded, title, subtitle, children }) {
  return embedded
    ? <div style={{marginTop:8}}>{children}</div>
    : <Card title={title} subtitle={subtitle}>{children}</Card>;
}

/* `embedded`: Card NÉLKÜL rendereli a tartalmat — 2026-09-05 óta a Revolut-kapcsolat
 * egy asset-sor a Bankszámlák → API kapcsolat regiszterében, ez a panel a sor alá kerül
 * (Gábor visszajelzése: „1-1 bankkapcsolatot asset-ként", nem külön kártyák). */
/* PSU-4 (2026-09-24, tulajdonosi döntés): a banki utalás-kapcsoló (`<bank>_transfers_enabled`)
 * BEkapcsolása — az utalás ENGEDÉLYEZÉSE — friss, egyszeri, erre a kérésre kötött passkeyt kér
 * (a backend `set_setting` feltételes kapuja, `FRESH_ACTION_ENDPOINTS` „POST /settings");
 * a KIkapcsolás (vészfék) nem kér passkeyt, csak admin-jogot. A passkey-szándék és a tényleges
 * kérés UGYANABBÓL a leíróból megy (`freshRequest` → `sendFresh`), különben 403. Megszakításnál
 * nem mutálunk — a Toggle a query-állapotot tükrözi, vizuálisan KI marad. */
const transferSwitchReq = (key) => freshRequest('POST /settings', { query: { key, value: 'true' } });
const setSettingFn = ({ key, value, fresh }) => (fresh ? sendFresh(fresh) : settingsApi.set(key, value));
function useTransferSwitch(key, setMut) {
  const { requireFreshStepUp } = useStepUp();
  return async (next) => {
    if (!next) { setMut.mutate({ key, value: 'false' }); return; }
    const fresh = transferSwitchReq(key);
    if (!await requireFreshStepUp('Az utalás engedélyezése előtt ', [fresh])) return;
    setMut.mutate({ key, value: 'true', fresh });
  };
}

function IntegrationsPanel({ embedded = false }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { isAdmin } = useAuth();
  const { requireFreshStepUp } = useStepUp();
  const [confirm, setConfirm] = useState(null); // {title, message, danger, confirmLabel, action}
  const { data: settings = {}, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
  });

  // ── M8 (2026-09-05): a draft-mód KAPCSOLAT-SZINTŰ (company_assets.settings) ──
  // A Revolut „draft vagy azonnali" nem cég-szintű beállítás: a Raiffeisennél
  // fogalmilag nem létezik. A régi `revolut_use_drafts` Setting írását a backend
  // 400-zal utasítja el; olvasni még tartalékként olvassa, amíg nincs Revolut-asset.
  const { data: bankAssets } = useQuery({
    queryKey: ['company-assets', 'bank_account'],
    queryFn: () => companyAssetsApi.list('bank_account'),
  });
  const revAsset = (bankAssets?.assets || []).find(a => a.provider === 'revolut' && !a.disabled_at);
  const legacyDrafts = settings.revolut_use_drafts !== 'false';
  const useDrafts = revAsset
    ? (typeof revAsset.settings?.use_drafts === 'boolean' ? revAsset.settings.use_drafts : legacyDrafts)
    : legacyDrafts;
  const invalidateAssets = () => qc.invalidateQueries({ queryKey: ['company-assets', 'bank_account'] });

  const setMut = useMutation({
    mutationFn: setSettingFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
    // F5 (2026-09-24): a banki utalás-kapcsolót csak admin írhatja — a 403 detail objektum.
    onError: (e) => { const d = e?.response?.data?.detail; toast.error((d && typeof d === 'object' ? d.message : d) || 'A beállítás mentése nem sikerült.'); },
  });
  // A draft-védelem KIkapcsolása a pénz útja: friss, erre a kérésre kötött passkey kell —
  // a leíró EGY helyen készül, a passkey-szándék és a PATCH is ebből megy (2026-09-24).
  const draftOffReq = (assetId) => freshRequest('PATCH /company-assets/{asset_id}/settings',
    { path: { asset_id: assetId }, body: { settings: { use_drafts: false } } });
  const draftMut = useMutation({
    mutationFn: (next) => (next
      ? companyAssetsApi.patchSettings(revAsset.id, { use_drafts: true })
      : sendFresh(draftOffReq(revAsset.id))),
    onSuccess: (_d, next) => {
      toast.success(next ? 'Draft-védelem bekapcsolva.' : 'Draft-védelem KIKAPCSOLVA — az utalások azonnal elindulnak.');
      invalidateAssets();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'A módosítás nem sikerült.'),
  });
  const revAssetReq = () => freshRequest('POST /company-assets',
    { body: { asset_type: 'bank_account', provider: 'revolut', label: 'Revolut' } });
  const createRevAssetMut = useMutation({
    mutationFn: (req) => sendFresh(req),
    onSuccess: () => { toast.success('Revolut-kapcsolat felvéve a regiszterbe.'); invalidateAssets(); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'A felvétel nem sikerült.'),
  });

  /* Irányfüggő lánc (D3 security 3.2 / UX 4.): false→true közvetlen mutáció;
   * true→false előbb ConfirmDialog, majd step-up, és CSAK sikeres step-up után a
   * PATCH. Megszakításnál nem mutálunk — a Toggle a query-állapotot tükrözi, tehát
   * vizuálisan magától `true`-n marad. */
  const toggleDrafts = (next) => {
    if (!revAsset) return;
    if (next) { draftMut.mutate(true); return; }
    setConfirm({
      title: 'Draft-védelem kikapcsolása', danger: true, confirmLabel: 'Kikapcsolom',
      message: 'Ezután az „Utal" gomb AZONNAL, jóváhagyás nélkül indítja a Revolut-utalást. '
             + 'A kapcsoló átállításához friss bejelentkezést kérünk.',
      action: async () => {
        if (!await requireFreshStepUp('A draft-védelem kikapcsolása előtt ',
                                      [draftOffReq(revAsset.id)])) return;
        draftMut.mutate(false);
      },
    });
  };
  const onCreateRevAsset = async () => {
    const req = revAssetReq();
    if (!await requireFreshStepUp('A Revolut-kapcsolat felvétele előtt ', [req])) return;
    createRevAssetMut.mutate(req);
  };

  // Utalás engedélyezve? (KI = a bank csak lekérdezésre, nem forrás az utalásban)
  const transfersOn = settings.revolut_transfers_enabled !== 'false';
  // BE: friss passkey (PSU-4); KI: csak admin — lásd `useTransferSwitch`.
  const toggleTransfers = useTransferSwitch('revolut_transfers_enabled', setMut);

  const RED = '#ef4444', GREEN = '#22c55e';

  // ── Környezet (sandbox / éles) — CSAK MEGJELENÍTÉS (M3/M4, 2026-09-04) ──
  // A sandbox/éles a CÉG-SZINTŰ `companies.is_sandbox` flag (minden integrációra
  // egyszerre), amit kizárólag a rendszergazda állít az admin konzolon. A régi
  // önkiszolgáló kapcsoló (`settingsApi.set('revolut_environment')`) megszűnt: a
  // backend a kulcsot 400-zal utasítja el. Itt csak MUTATJUK, melyikben vagyunk.
  const { data: envStatus = {} } = useQuery({
    queryKey: ['revolut-env-status'],
    queryFn: revolutApi.envStatus,
  });
  const env = envStatus.environment || 'sandbox';
  const isSandbox = env !== 'production';
  const prodConfigured = !!envStatus.production_configured;

  return (
    <MaybeCard embedded={embedded} title="Revolut" subtitle="Környezet és banki utalások">
      {isLoading ? (
        <p style={{color:'var(--text-muted)',fontSize:'0.82rem'}}>Betöltés…</p>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          {/* Utalás engedélyezve / csak információ */}
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            <div style={{fontSize:'0.7rem',fontWeight:700,color:'var(--text-muted)',
              textTransform:'uppercase',letterSpacing:'0.06em'}}>Utalás</div>
            <div style={{display:'flex',alignItems:'center',gap:14}}>
              <div style={{textAlign:'right',flex:1}}>
                <div style={{fontWeight: transfersOn?500:700, fontSize:'0.86rem',
                  color: transfersOn ? 'var(--text-muted)' : '#94a3b8'}}>Csak információ</div>
                <div style={{fontSize:'0.72rem',color:'var(--text-muted)'}}>nem forrás az utalásban</div>
              </div>
              <Toggle checked={transfersOn} disabled={setMut.isPending}
                onChange={toggleTransfers} onColor={GREEN} offColor={'#64748b'} />
              <div style={{textAlign:'left',flex:1}}>
                <div style={{fontWeight: transfersOn?700:500, fontSize:'0.86rem',
                  color: transfersOn ? GREEN : 'var(--text-muted)'}}>Utalás engedélyezve</div>
                <div style={{fontSize:'0.72rem',color:'var(--text-muted)'}}>forrásként választható</div>
              </div>
            </div>
          </div>

          <div style={{height:1,background:'var(--border)'}}/>

          {/* Környezet — csak megjelenítés; cég-szinten, az admin konzolon állítható */}
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            <div style={{fontSize:'0.7rem',fontWeight:700,color:'var(--text-muted)',
              textTransform:'uppercase',letterSpacing:'0.06em'}}>Környezet</div>
            <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
              <Badge variant={isSandbox ? 'success' : 'danger'}>
                {isSandbox ? 'SANDBOX (teszt)' : 'ÉLES'}
              </Badge>
              <span style={{fontSize:'0.72rem',color:'var(--text-dim)'}}
                title="A teszt/éles környezet cég-szintű beállítás: minden integrációra (bankok, NAV) egyszerre érvényes.">
                A környezetet a rendszergazda állítja, cég-szinten.
              </span>
            </div>
            {!isSandbox && prodConfigured && (
              <div style={{fontSize:'0.76rem',color:RED,lineHeight:1.45,
                borderLeft:`3px solid ${RED}`,paddingLeft:10}}>
                ⚠ <b>ÉLES Revolut aktív</b> – minden utalás valódi pénzt mozgat.
              </div>
            )}
            {!isSandbox && !prodConfigured && (
              <div style={{fontSize:'0.72rem',color:RED}}>
                ⚠ Éles környezet éles Revolut-hitelesítő adatok NÉLKÜL — a hívások el fognak halni.
                Pótlás: Beállítások → Bankszámlák → API kapcsolat.
              </div>
            )}
          </div>

          <div style={{height:1,background:'var(--border)'}}/>

          {/* M8: a kapcsoló a Revolut-KAPCSOLAT beállítása (company_assets.settings).
              Ha még nincs Revolut-asset, előbb fel kell venni — a régi cég-szintű
              Setting csak olvasott tartalék, írni már nem lehet. */}
          {!revAsset && !embedded && (
            <Alert type="info" style={{marginBottom:0}}>
              A draft-mód a Revolut-kapcsolat saját beállítása. Ehhez a kapcsolatot fel kell
              venni a regiszterbe (a hitelesítő adatokat ez nem érinti).{' '}
              <Btn size="sm" variant="secondary" disabled={!isAdmin || createRevAssetMut.isPending}
                title={isAdmin ? undefined : 'Csak cég-adminisztrátor módosíthatja.'}
                onClick={onCreateRevAsset}>Revolut-kapcsolat felvétele</Btn>
            </Alert>
          )}
          {/* bal = azonnali (piros) · jobb = jóváhagyásos (zöld, ajánlott, default) */}
          <div style={{display:'flex',alignItems:'center',gap:14}}>
            <div style={{textAlign:'right',flex:1}}>
              <div style={{fontWeight: useDrafts?500:700, fontSize:'0.86rem',
                color: useDrafts ? 'var(--text-muted)' : RED}}>
                Azonnali utalás
              </div>
              <div style={{fontSize:'0.72rem',color:'var(--text-muted)'}}>
                jóváhagyás nélkül
              </div>
            </div>
            <Toggle checked={useDrafts} disabled={!revAsset || !isAdmin || draftMut.isPending}
              title={!isAdmin ? 'Csak cég-adminisztrátor módosíthatja.'
                   : !revAsset ? 'Előbb vedd fel a Revolut-kapcsolatot a regiszterbe.' : undefined}
              onChange={toggleDrafts} onColor={GREEN} offColor={RED} />
            <div style={{textAlign:'left',flex:1}}>
              <div style={{fontWeight: useDrafts?700:500, fontSize:'0.86rem',
                color: useDrafts ? GREEN : 'var(--text-muted)'}}>
                Jóváhagyás kötelező
                <span style={{marginLeft:8,fontSize:'0.64rem',fontWeight:700,
                  color: useDrafts ? GREEN : 'var(--text-muted)',
                  border:`1px solid ${useDrafts ? GREEN : 'var(--text-muted)'}`,
                  borderRadius:6,padding:'1px 6px'}}>AJÁNLOTT</span>
              </div>
              <div style={{fontSize:'0.72rem',color:'var(--text-muted)'}}>
                alapértelmezett
              </div>
            </div>
          </div>

          {/* állapot-függő magyarázat */}
          {useDrafts ? (
            <div style={{fontSize:'0.76rem',color:'var(--text-muted)',lineHeight:1.45,
              borderLeft:`3px solid ${GREEN}`,paddingLeft:10}}>
              A "Utal" / kötegelt "Utalás (N)" gomb nem indít közvetlen utalást, hanem egyetlen
              <b> payment draft</b>-ot készít a Revolut Businessben. A draft a Revolut Business
              mobilappban a "Payment drafts" között jelenik meg, és <b>csak akkor megy ki, amikor
              ott manuálisan jóváhagyod</b>. Ez a biztonságos, ajánlott működés.
            </div>
          ) : (
            <div style={{fontSize:'0.76rem',color:RED,lineHeight:1.45,
              borderLeft:`3px solid ${RED}`,paddingLeft:10}}>
              ⚠ A "Utal" / kötegelt "Utalás (N)" gomb <b>azonnal, jóváhagyás nélkül elindítja
              az utalást</b> (illetve automatikusan jóváhagyásra kerül, ha a szervezeti policy úgy
              kéri). Csak akkor használd, ha tudatosan ezt akarod.
            </div>
          )}
        </div>
      )}

      <ConfirmDialog open={!!confirm} title={confirm?.title} message={confirm?.message}
        danger={confirm?.danger} confirmLabel={confirm?.confirmLabel}
        onCancel={()=>setConfirm(null)}
        onConfirm={()=>{ const a=confirm.action; setConfirm(null); a(); }}/>
    </MaybeCard>
  );
}


/* `embedded`: Card nélkül, egy Raiffeisen asset-sor alatt (2026-09-05). */
function RaiffeisenPanel({ embedded = false }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirm, setConfirm] = useState(null); // {title, message, danger, confirmLabel, action}
  const RED = '#ef4444', GREEN = '#22c55e';
  const { data: settings = {} } = useQuery({ queryKey:['settings'], queryFn: settingsApi.get });
  const { data: env = {} } = useQuery({ queryKey:['raiffeisen-env-status'], queryFn: raiffeisenApi.envStatus });
  const { data: consents = [] } = useQuery({ queryKey:['raiffeisen-consents'], queryFn: raiffeisenApi.consents });

  const setMut = useMutation({ mutationFn: setSettingFn,
    onSuccess:()=>qc.invalidateQueries({ queryKey: ['settings'] }),
    onError: (e) => { const d = e?.response?.data?.detail; toast.error((d && typeof d === 'object' ? d.message : d) || 'A beállítás mentése nem sikerült.'); } });

  // Környezet — CSAK MEGJELENÍTÉS (M3/M4, 2026-09-04): a cég-szintű
  // `companies.is_sandbox` flag, az admin konzolon állítható; a régi önkiszolgáló
  // `raiffeisen_environment` Setting-kapcsolót a backend 400-zal utasítja el.
  const environment  = env.environment || 'sandbox';
  const isSandbox    = environment !== 'production';
  const prodConfigured = !!env.production_configured;
  const transfersOn  = settings.raiffeisen_transfers_enabled !== 'false';

  // BE: friss passkey (PSU-4); KI: csak admin — lásd `useTransferSwitch`.
  const toggleTransfers = useTransferSwitch('raiffeisen_transfers_enabled', setMut);

  // Consent
  const [signer, setSigner] = useState('');
  const [ibans, setIbans]   = useState('');
  const [expiry, setExpiry] = useState('');
  const createMut = useMutation({
    mutationFn: () => raiffeisenApi.createConsent({
      signer: signer.trim(),
      expiry_date: expiry ? new Date(expiry).toISOString() : '',
      account_list: ibans.split(',').map(s=>s.trim()).filter(Boolean),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['raiffeisen-consents'] }); setSigner(''); setIbans(''); setExpiry(''); },
  });
  const refreshMut = useMutation({ mutationFn:(id)=>raiffeisenApi.refreshConsent(id),
    onSuccess:()=>qc.invalidateQueries({ queryKey: ['raiffeisen-consents'] }) });

  // Tanúsítvány (lejárat-figyelés). Elsődlegesen a cert-fájlból jön; ha az nincs meg,
  // a kézzel rögzített dátum a fallback – lejárat után MINDEN Raiffeisen-hívás elhal.
  const cert = env.certificate || {};
  const certWarnDays = cert.warn_days ?? 30;
  const [manualExp, setManualExp] = useState('');
  const certExpMut = useMutation({
    mutationFn: (v) => settingsApi.set(`raiffeisen_cert_expiry_${environment}`, v),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['raiffeisen-env-status'] });
                       qc.invalidateQueries({ queryKey: ['settings'] }); },
  });

  // Egyenleg – csak a jóváhagyott consentek számláira kérdezhető le, gombra.
  // A backend a consent-párost és a tulajdonos-nevet magától kitölti.
  const [balances, setBalances] = useState({});   // { [iban]: {loading|data|error} }
  const balanceIbans = useMemo(() => {
    const seen = [];
    consents.filter(c => c.status === 'APPROVED').forEach(c => {
      (c.account_list && c.account_list.length ? c.account_list : [c.account_iban])
        .forEach(i => { if (i && !seen.includes(i)) seen.push(i); });
    });
    return seen;
  }, [consents]);
  const fetchBalance = (iban) => {
    setBalances(b => ({ ...b, [iban]: { loading: true } }));
    raiffeisenApi.balance({ iban })
      .then(d => setBalances(b => ({ ...b, [iban]: { data: d } })))
      .catch(e => setBalances(b => ({ ...b, [iban]:
        { error: e?.response?.data?.detail || e.message } })));
  };

  // SMS OTP (sandbox portál)
  const [otpId, setOtpId] = useState('');
  const [otp, setOtp]     = useState(null);
  const otpMut = useMutation({ mutationFn:()=>raiffeisenApi.smsOtp(otpId.trim()),
    onSuccess:(d)=>setOtp(d.otp || (d.raw ? JSON.stringify(d.raw) : '—')),
    onError:(e)=>setOtp('Hiba: ' + (e?.response?.data?.detail || e.message)) });

  const stColor = (st) => st==='APPROVED' ? GREEN : (st==='DECLINED'||st==='REVOKED') ? RED : '#f59e0b';
  const dFmt = (iso) => { const d = new Date(iso); return isNaN(d) ? '—' :
    `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')}`; };
  const subS = { fontSize:'0.7rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em' };
  const smallBtn = { background:'transparent', border:'1px solid var(--border)', borderRadius:'var(--radius)',
    color:'var(--text-muted)', fontSize:'0.72rem', padding:'0.2rem 0.55rem', cursor:'pointer' };
  const Divider = () => <div style={{height:1,background:'var(--border)'}}/>;

  return (
    <MaybeCard embedded={embedded} title="Raiffeisen" subtitle="Környezet, utalás-engedély, tanúsítvány és consentek">
      <div style={{display:'flex',flexDirection:'column',gap:14}}>

        {/* Utalás BE/KI */}
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          <div style={subS}>Utalás</div>
          <div style={{display:'flex',alignItems:'center',gap:14}}>
            <div style={{textAlign:'right',flex:1}}>
              <div style={{fontWeight:transfersOn?500:700,fontSize:'0.86rem',color:transfersOn?'var(--text-muted)':'#94a3b8'}}>Csak információ</div>
            </div>
            <Toggle checked={transfersOn} disabled={setMut.isPending} onChange={toggleTransfers} onColor={GREEN} offColor={'#64748b'}/>
            <div style={{textAlign:'left',flex:1}}>
              <div style={{fontWeight:transfersOn?700:500,fontSize:'0.86rem',color:transfersOn?GREEN:'var(--text-muted)'}}>Utalás engedélyezve</div>
            </div>
          </div>
        </div>

        <Divider/>

        {/* Környezet — csak megjelenítés; cég-szinten, az admin konzolon állítható */}
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          <div style={subS}>Környezet</div>
          <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
            <Badge variant={isSandbox ? 'success' : 'danger'}>
              {isSandbox ? 'SANDBOX (teszt)' : 'ÉLES'}
            </Badge>
            <span style={{fontSize:'0.72rem',color:'var(--text-dim)'}}
              title="A teszt/éles környezet cég-szintű beállítás: minden integrációra (bankok, NAV) egyszerre érvényes.">
              A környezetet a rendszergazda állítja, cég-szinten.
            </span>
          </div>
          {!isSandbox && !prodConfigured && (
            <div style={{fontSize:'0.72rem',color:RED}}>
              ⚠ Éles környezet éles Raiffeisen-hitelesítő adatok NÉLKÜL — a hívások el fognak halni.
              Pótlás: Beállítások → Bankszámlák → API kapcsolat.
            </div>
          )}
        </div>

        <Divider/>

        {/* Tanúsítvány – lejárat-figyelés */}
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          <div style={subS}>Tanúsítvány</div>
          {cert.not_after ? (() => {
            const d = cert.days_left;
            const state = cert.expired ? 'expired' : (d <= certWarnDays ? 'soon' : 'ok');
            const color = state==='expired' ? RED : state==='soon' ? '#f59e0b' : GREEN;
            return (
              <div style={{display:'flex',flexDirection:'column',gap:6,fontSize:'0.78rem',
                border:`1px solid ${state==='ok'?'var(--border)':color}`,borderRadius:'var(--radius)',padding:'0.5rem 0.6rem'}}>
                <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                  <span style={{fontWeight:700,color}}>
                    {state==='expired' ? '⚠ LEJÁRT' : state==='soon' ? '⚠ Hamarosan lejár' : 'Érvényes'}
                  </span>
                  <span>lejár: <b>{dFmt(cert.not_after)}</b></span>
                  {!cert.expired && <span style={{color:'var(--text-muted)'}}>({d} nap)</span>}
                  <span style={{color:'var(--text-muted)',fontSize:'0.72rem'}}>
                    {cert.source==='manual' ? 'kézzel rögzített dátum' : 'a tanúsítvány-fájlból'}
                  </span>
                </div>
                {(cert.label || cert.subject_cn) && (
                  <div style={{color:'var(--text-muted)',fontSize:'0.72rem'}}>
                    {cert.label && <>{cert.label} · </>}
                    {cert.subject_cn && <span style={{fontFamily:'var(--mono)'}}>{cert.subject_cn}</span>}
                    {cert.serial_hex && <> · sorszám <span style={{fontFamily:'var(--mono)'}}>{cert.serial_hex}</span></>}
                  </div>
                )}
                {state!=='ok' && (
                  <div style={{color,fontSize:'0.72rem',lineHeight:1.4}}>
                    Lejárat után a Raiffeisen MINDEN hívást elutasít (utalás, kivonat, consent).
                    Új kulcspár + CSR generálása és feltöltése kell a Pay Portálra.
                  </div>
                )}
                {cert.kid_serial_match === false && (
                  <div style={{color:RED,fontSize:'0.72rem',lineHeight:1.4}}>
                    ⚠ A tanúsítvány sorszáma NEM egyezik a beállított KID <code>/SN=</code> értékével —
                    valószínűleg új certet töltöttél fel, de a <code>.env</code> KID-je a régié.
                  </div>
                )}
              </div>
            );
          })() : (
            <div style={{display:'flex',flexDirection:'column',gap:6,fontSize:'0.78rem'}}>
              <div style={{color:'var(--text-muted)',lineHeight:1.4}}>
                {cert.error || 'A tanúsítvány lejárata nem ismert.'} A hívásokhoz a cert-fájl nem kell,
                de nélküle nem tudunk lejáratra figyelmeztetni — töltsd le a Pay Portálról a
                <code> backend/</code> könyvtárba, vagy add meg a lejáratot kézzel:
              </div>
              <div style={{display:'flex',gap:8,alignItems:'flex-end',flexWrap:'wrap'}}>
                <Input label="Lejárat (kézi)" type="date" value={manualExp}
                  onChange={e=>setManualExp(e.target.value)} style={{maxWidth:160}}/>
                <Btn onClick={()=>certExpMut.mutate(manualExp)} disabled={!manualExp || certExpMut.isPending}>Mentés</Btn>
              </div>
            </div>
          )}
        </div>

        <Divider/>

        {/* Consentek */}
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          <div style={subS}>Consentek (banki felhatalmazás, max 60 nap)</div>
          {consents.length === 0 ? (
            <div style={{fontSize:'0.78rem',color:'var(--text-muted)'}}>Még nincs consent ebben a környezetben.</div>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:6}}>
              {consents.map(c => {
                const exp = c.expiry_date ? new Date(c.expiry_date) : null;
                const expired = exp && exp <= new Date();
                const soon = exp && !expired && (exp - new Date()) < 14*864e5;
                return (
                  <div key={c.id} style={{display:'flex',alignItems:'center',gap:10,fontSize:'0.78rem',flexWrap:'wrap',
                    border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'0.4rem 0.6rem'}}>
                    <span style={{fontWeight:700,color:stColor(c.status)}}>{c.status}</span>
                    <span style={{color:'var(--text-muted)'}}>signer {c.signer}</span>
                    <span style={{fontFamily:'var(--mono)',color:'var(--text-muted)'}}>{c.account_iban}</span>
                    {exp && <span style={{color: expired?RED:(soon?'#f59e0b':'var(--text-muted)')}}>
                      lejár: {exp.toLocaleDateString('hu-HU')}{expired?' (lejárt!)':soon?' (hamarosan)':''}</span>}
                    <button onClick={()=>refreshMut.mutate(c.id)} disabled={refreshMut.isPending} style={{marginLeft:'auto',...smallBtn}}>Frissít</button>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'flex-end',marginTop:4}}>
            <Input label="Jóváhagyó DirektNet ID" value={signer} onChange={e=>setSigner(e.target.value)} style={{maxWidth:170}}/>
            <Input label="Számla IBAN(ok), vesszővel" value={ibans} onChange={e=>setIbans(e.target.value)} style={{maxWidth:280}}/>
            <Input label="Lejárat" type="date" value={expiry} onChange={e=>setExpiry(e.target.value)} style={{maxWidth:160}}/>
            <Btn onClick={()=>createMut.mutate()} disabled={createMut.isPending || !signer || !ibans || !expiry}>Consent létrehozása</Btn>
          </div>
          {createMut.isError && <Alert type="danger">Hiba: {createMut.error?.response?.data?.detail || createMut.error?.message}</Alert>}
          <div style={{fontSize:'0.72rem',color:'var(--text-muted)',lineHeight:1.4}}>
            A létrehozott consentet a jóváhagyónak a Pay Portálon kell elfogadnia (jelszó+SMS) → utána APPROVED.
            Minden utaláshoz 2 db APPROVED consent kell (önálló aláírónál ugyanaz kétszer).
          </div>
        </div>

        <Divider/>

        {/* Egyenleg – SZÁNDÉKOSAN gombra fut, nem automatikusan: a bank sandboxában
            a balance-query timeoutol (bank-oldali hiba, 2026-08-03-án elismerve),
            így egy automatikus lekérdezés minden panel-nyitást megvárakoztatna. */}
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          <div style={subS}>Egyenleg</div>
          {balanceIbans.length === 0 ? (
            <div style={{fontSize:'0.78rem',color:'var(--text-muted)'}}>
              Nincs jóváhagyott (APPROVED) consent — egyenleget csak azzal lehet lekérdezni.
            </div>
          ) : balanceIbans.map(iban => {
            const st = balances[iban];
            return (
              <div key={iban} style={{display:'flex',alignItems:'center',gap:10,fontSize:'0.78rem',flexWrap:'wrap',
                border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'0.4rem 0.6rem'}}>
                <span style={{fontFamily:'var(--mono)',color:'var(--text-muted)'}}>{iban}</span>
                {st?.loading && <span style={{color:'var(--text-muted)'}}>lekérdezés…</span>}
                {st?.error && <span style={{color:'#f59e0b',flex:'1 1 240px',lineHeight:1.4}}>{st.error}</span>}
                {st?.data && (
                  <span style={{fontFamily:'var(--mono)',fontWeight:700}}>
                    {st.data.available ?? st.data.current ?? '—'} {st.data.currency || 'HUF'}
                    {st.data.current != null && st.data.available != null && st.data.current !== st.data.available && (
                      <span style={{fontWeight:400,color:'var(--text-muted)'}}> (könyvelt: {st.data.current})</span>
                    )}
                  </span>
                )}
                <button onClick={()=>fetchBalance(iban)} disabled={st?.loading}
                  style={{marginLeft:'auto',...smallBtn}}>Lekérdez</button>
              </div>
            );
          })}
        </div>

        {/* SMS OTP — CSAK SANDBOX: a bank testing-tool SMS API-ja (`/api/raiffeisen/sms-otp`)
            a Pay Portál teszt-környezetéhez adja ki a kódot; élesben a jóváhagyó a saját
            telefonjára kapja, ez a blokk ott értelmetlen → nem jelenik meg. */}
        {isSandbox && (<>
          <Divider/>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            <div style={subS}>SMS kód (sandbox portál)</div>
            <div style={{display:'flex',gap:8,alignItems:'flex-end',flexWrap:'wrap'}}>
              <Input label="DirektNet ID" value={otpId} onChange={e=>setOtpId(e.target.value)} style={{maxWidth:170}}/>
              <Btn onClick={()=>otpMut.mutate()} disabled={otpMut.isPending || !otpId}>SMS kód lekérése</Btn>
              {otp && <span style={{fontFamily:'var(--mono)',fontWeight:700,fontSize:'0.95rem',color:'var(--text)'}}>{otp}</span>}
            </div>
            <div style={{fontSize:'0.72rem',color:'var(--text-muted)'}}>
              A portálon a jelszó beírása UTÁN kérd le (a legutolsó művelethez tartozó kódot adja). Csak sandbox.
            </div>
          </div>
        </>)}

      </div>

      <ConfirmDialog open={!!confirm} title={confirm?.title} message={confirm?.message}
        danger={confirm?.danger} confirmLabel={confirm?.confirmLabel}
        onCancel={()=>setConfirm(null)}
        onConfirm={()=>{ const a=confirm.action; setConfirm(null); a(); }}/>
    </MaybeCard>
  );
}


/** Kétállapotú slider (toggle switch). onColor = bekapcsolt, offColor = kikapcsolt szín. */
/* `title`: natív tooltip — CO_WORKER-nek `disabled` állapotban ebből derül ki, MIÉRT
 * nem kattintható („Csak cég-adminisztrátor módosíthatja.") — M8, D3 UX 4. pont. */
function Toggle({ checked, onChange, disabled, title, onColor = 'var(--primary)', offColor = 'var(--border, #4a4a4a)' }) {
  return (
    <button type="button" role="switch" aria-checked={checked}
      disabled={disabled} title={title}
      onClick={() => !disabled && onChange(!checked)}
      style={{
        flexShrink:0,
        width:46, height:24, borderRadius:12, border:'none',
        padding:0, position:'relative', cursor: disabled ? 'default' : 'pointer',
        background: checked ? onColor : offColor,
        opacity: disabled ? 0.6 : 1,
        transition:'background 0.15s ease',
      }}>
      <span style={{
        position:'absolute', top:2, left: checked ? 24 : 2,
        width:20, height:20, borderRadius:'50%', background:'#fff',
        transition:'left 0.15s ease',
        boxShadow:'0 1px 2px rgba(0,0,0,0.35)',
      }}/>
    </button>
  );
}


function PartnerAliasesPanel() {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(null); // {title, message, danger, confirmLabel, action}
  const [form, setForm] = useState({ alias:'', partner_name:'', notes:'' });
  const aliasQ = useQuery({
    queryKey: ['partner-aliases'],
    queryFn: partnerAliasApi.list,
  });
  const aliases = aliasQ.data ?? [];
  const createMut = useMutation({
    mutationFn: partnerAliasApi.create,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['partner-aliases'] }); setForm({alias:'',partner_name:'',notes:''}); },
  });
  const delMut = useMutation({
    mutationFn: partnerAliasApi.delete,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['partner-aliases'] }),
  });

  const addAlias = () => {
    const a = form.alias.trim(), p = form.partner_name.trim();
    if (!a || !p) return;
    createMut.mutate({ alias:a, partner_name:p, notes:form.notes || null, is_active:true });
  };

  const tdS = { padding:'0.5rem 0.75rem', fontSize:'0.82rem', verticalAlign:'middle',
                borderBottom:'1px solid var(--border-dim)' };
  const thS = { padding:'0.55rem 0.75rem', color:'var(--text-muted)', fontWeight:600,
                fontSize:'0.68rem', textTransform:'uppercase', letterSpacing:'0.06em',
                borderBottom:'1px solid var(--border)' };

  return (
    <Card title="Partner alias-ok" subtitle="Banki tranzakcióban szereplő partner név → a számlán szereplő tényleges szállító. Auto-match algoritmus ezt használja.">
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr auto',gap:8,marginBottom:14}}>
        <Input label="Banki név (alias)" placeholder="pl. forpsi" value={form.alias}
          onChange={e=>setForm(f=>({...f,alias:e.target.value}))}/>
        <Input label="Tényleges partner (számlán)" placeholder="pl. BlazeArts Kft." value={form.partner_name}
          onChange={e=>setForm(f=>({...f,partner_name:e.target.value}))}/>
        <Input label="Megjegyzés" value={form.notes}
          onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/>
        <Btn icon={Plus} loading={createMut.isPending} onClick={addAlias}
          style={{alignSelf:'flex-end'}}>Hozzáad</Btn>
      </div>
      <QueryState query={aliasQ} empty={aliases.length === 0}
        emptyText={'Nincs alias rögzítve. Pl. "forpsi" → "BlazeArts Kft."'}
        errorText="Az aliasok betöltése sikertelen.">
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.82rem'}}>
            <thead><tr>
              <th style={thS}>Banki név (alias)</th>
              <th style={thS}>Számlán szereplő partner</th>
              <th style={thS}>Megjegyzés</th>
              <th style={thS}></th>
            </tr></thead>
            <tbody>
              {aliases.map(a => (
                <tr key={a.id}>
                  <td style={tdS}><span style={{fontFamily:'var(--mono)'}}>{a.alias}</span></td>
                  <td style={tdS}>{a.partner_name}</td>
                  <td style={{...tdS,color:'var(--text-muted)',fontSize:'0.76rem'}}>{a.notes || ''}</td>
                  <td style={tdS}>
                    <Btn size="sm" variant="ghost" icon={Trash2}
                      onClick={()=>setConfirm({
                        title:'Alias törlése', danger:true, confirmLabel:'Törlés',
                        message:`Töröld a "${a.alias}" alias-t?`,
                        action:()=>delMut.mutate(a.id),
                      })}/>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </QueryState>

      <ConfirmDialog open={!!confirm} title={confirm?.title} message={confirm?.message}
        danger={confirm?.danger} confirmLabel={confirm?.confirmLabel}
        onCancel={()=>setConfirm(null)}
        onConfirm={()=>{ const a=confirm.action; setConfirm(null); a(); }}/>
    </Card>
  );
}

// ── Biztonság (passkey / WebAuthn) ───────────────────────────────────────────
// wiki/proposals/deferred-features.md §15. A GoTrue passkey-lista pontos
// mezőneveit ("id", "friendly_name", "created_at", "last_used_at") a gyártói
// doksi (raw/supabase-passkeys.md) alapján vettük át - élő fiókkal még
// megerősítendő az első kézi kipróbáláskor.
function SecurityPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirm, setConfirm] = useState(null);
  const [renaming, setRenaming] = useState(null); // {id, value}
  const [adding, setAdding] = useState(false);
  const [mfaOpen, setMfaOpen] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaBusy, setMfaBusy] = useState(false);
  // R1 (2026-09-28): az ELSŐ passkeyhez e-mailben küldött kód kell (a jelszó egyedül nem elég).
  const [codeOpen, setCodeOpen] = useState(false);
  const [codeValue, setCodeValue] = useState('');
  const [codeInfo, setCodeInfo] = useState(null);
  const [codeBusy, setCodeBusy] = useState(false);
  const [codeError, setCodeError] = useState('');
  const codeRef = useRef(null);
  const { requireFreshStepUp } = useStepUp();
  const supported = browserSupportsWebAuthn();

  const listQ = useQuery({ queryKey: ['passkeys'], queryFn: passkeyApi.list });
  const passkeys = listQ.data?.passkeys ?? (Array.isArray(listQ.data) ? listQ.data : []);

  // A passkey-kezelő műveletek (regisztráció, átnevezés, TÖRLÉS - élő mérés,
  // 2026-08-24) mind AAL2-t követelnek meg, ha a fióknak már van MFA-faktora
  // (az admin console TOTP-je UGYANEZEN a Supabase Auth projekten). Ez a
  // wrapper elkapja az `insufficient_aal`-t, egy TOTP-kód-kérő modallal
  // felemeli a sessiont AAL2-re, majd EGYSZER újrapróbálja a műveletet -
  // nem végtelen ciklus. A `pendingRef` viszi át a "mit próbáljunk újra"
  // callback-et a modal beküldéséig (vagy a Mégse-ig, ahol elutasítjuk).
  const pendingRef = useRef(null);
  const isCancelled = (err) => err?.name === 'NotAllowedError' || err?.__mfaCancelled === true;

  const withAal2StepUp = (action) => {
    return action().catch((err) => {
      if (err?.response?.data?.detail !== 'insufficient_aal') throw err;
      // ⚠ 2026-08-24-i élő hiba: egy MÁSODIK, gyorsan egymás után indított
      // AAL2-igénylő művelet csendben felülírta a `pendingRef`-et - a modal
      // beküldése ekkor a MÁSIK (rosszul) várt műveletet futtatta újra
      // (törlés helyett hozzáadás). Ha már fut egy step-up, ne írjuk felül -
      // ez a második hívás egyszerűen elutasítva bukik, ahogy máskor is.
      if (pendingRef.current) throw err;
      return new Promise((resolve, reject) => {
        pendingRef.current = { action, resolve, reject };
        setMfaOpen(true);
      });
    });
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setMfaBusy(true);
    try {
      const { factors } = await authApi.mfaFactors();
      const totp = factors.find(f => f.factor_type === 'totp');
      if (!totp) {
        toast.error('Nincs regisztrált kétfaktoros hitelesítőd.');
        return;
      }
      const { id: challengeId } = await authApi.mfaChallenge(totp.id);
      await authApi.mfaVerify(totp.id, challengeId, mfaCode.trim());
      setMfaOpen(false);
      setMfaCode('');
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (pending) {
        // Az eredeti művelet EGYSZERI újrapróbálása - a WebAuthn-ceremóniát
        // (ha volt) is újra lefuttatja, mert a passkey-regisztrációnál az
        // insufficient_aal még a challenge-lekérésnél jön, a böngésző-ceremónia
        // előtt.
        pending.action().then(pending.resolve, pending.reject);
      }
    } catch (err) {
      toast.error(passkeyErrorMessage(err, 'A kétfaktoros megerősítés sikertelen.'));
    } finally {
      setMfaBusy(false);
    }
  };

  const cancelMfa = () => {
    setMfaOpen(false);
    setMfaCode('');
    const pending = pendingRef.current;
    pendingRef.current = null;
    pending?.reject(Object.assign(new Error('mfa step-up megszakítva'), { __mfaCancelled: true }));
  };

  const renameMut = useMutation({
    mutationFn: ({ id, friendlyName }) => withAal2StepUp(() => passkeyApi.rename(id, friendlyName)),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['passkeys'] }); setRenaming(null); },
    onError: (err) => { if (!isCancelled(err)) toast.error(passkeyErrorMessage(err, 'Az átnevezés sikertelen.')); },
  });
  // ⚠ R1 (2026-09-28): a felvétel (verify) és a törlés friss passkey-jóváhagyást kér, a
  // KONKRÉT kérésre kötve (`backend/app/core/passkey_guard.py`). A ceremónia a
  // `withAal2StepUp`-on BELÜL fut: ha a GoTrue a 2FA-t a jóváhagyás után kéri, az újrapróba új
  // ceremóniát indít (a felhasznált jegy nem jó másodszor — ez szándékos).
  const cancelled = () => Object.assign(new Error('megszakítva'), { __mfaCancelled: true });
  const deleteMut = useMutation({
    mutationFn: (id) => withAal2StepUp(async () => {
      const req = freshRequest('DELETE /auth/passkeys/{passkey_id}', { path: { passkey_id: id } });
      if (!await requireFreshStepUp('Passkey törlése: hagyd jóvá egy passkeyeddel.', [req])) {
        throw cancelled();
      }
      return sendFresh(req);
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['passkeys'] }),
    onError: (err) => { if (!isCancelled(err)) toast.error(passkeyErrorMessage(err, 'A törlés sikertelen.')); },
  });

  const fmtDate = (s) => (s ? String(s).slice(0, 10).replace(/-/g, '.') : '–');

  // Az első passkeyhez: kód kérése, a modal a kódot a SZERVEREN ellenőrzi (a ceremónia ELŐTT),
  // és az így kapott egyszeri jeggyel (vagy null-lal, ha megszakították) old fel. Elgépelésnél
  // a modal nyitva marad (a kódnak 3 próbája van) — így nem marad árva kulcs a jelszókezelőben.
  const askEmailTicket = async () => {
    const info = await passkeyApi.firstCode();
    setCodeInfo(info);
    setCodeValue('');
    setCodeError('');
    setCodeOpen(true);
    return new Promise((resolve) => { codeRef.current = resolve; });
  };
  const finishCode = (ticket) => {
    setCodeOpen(false);
    const r = codeRef.current;
    codeRef.current = null;
    r?.(ticket);
  };
  const submitCode = async (e) => {
    e.preventDefault();
    setCodeBusy(true);
    setCodeError('');
    try {
      const { ticket } = await passkeyApi.firstCodeVerify(codeValue.trim());
      finishCode(ticket);
    } catch (err) {
      setCodeError(passkeyErrorMessage(err, 'A kód ellenőrzése sikertelen.'));
    } finally {
      setCodeBusy(false);
    }
  };
  const resendCode = async () => {
    setCodeBusy(true);
    try {
      setCodeInfo(await passkeyApi.firstCode());
      setCodeValue('');
      setCodeError('');
      toast.success('Új kódot küldtünk.');
    } catch (err) {
      toast.error(passkeyErrorMessage(err, 'A kód küldése sikertelen.'));
    } finally {
      setCodeBusy(false);
    }
  };

  const addPasskey = async () => {
    setAdding(true);
    try {
      const hasPasskey = passkeys.length > 0;
      let firstTicket = null;
      if (!hasPasskey) {
        firstTicket = await askEmailTicket();
        if (!firstTicket) return;
      }
      await withAal2StepUp(async () => {
        const { challenge_id: challengeId, options } = await passkeyApi.registrationOptions();
        const credential = await startRegistration({ optionsJSON: options });
        if (hasPasskey) {
          const req = freshRequest('POST /auth/passkeys/registration/verify',
            { body: { challenge_id: challengeId, credential } });
          if (!await requireFreshStepUp(
            'Új passkey felvétele: hagyd jóvá egy meglévő passkeyeddel.', [req])) {
            throw cancelled();
          }
          await sendFresh(req);
        } else {
          await passkeyApi.registrationVerify(challengeId, credential, firstTicket);
        }
      });
      qc.invalidateQueries({ queryKey: ['passkeys'] });
      toast.success('Passkey hozzáadva.');
    } catch (err) {
      if (isCancelled(err)) {
        // felhasználó szakította meg (WebAuthn-dialógus vagy az MFA-modal) - néma
      } else if (err?.name === 'InvalidStateError') {
        toast.error('Ez az eszköz/kulcs már regisztrálva van.');
      } else {
        toast.error(passkeyErrorMessage(err, 'A passkey regisztrációja sikertelen.'));
      }
    } finally {
      setAdding(false);
    }
  };

  const tdS = { padding:'0.5rem 0.75rem', fontSize:'0.82rem', verticalAlign:'middle',
                borderBottom:'1px solid var(--border-dim)' };
  const thS = { padding:'0.55rem 0.75rem', color:'var(--text-muted)', fontWeight:600,
                fontSize:'0.68rem', textTransform:'uppercase', letterSpacing:'0.06em',
                borderBottom:'1px solid var(--border)' };

  // Amíg BÁRMELYIK AAL2-igénylő művelet (vagy maga a 2FA-modal) fut, a többi
  // passkey-gombot letiltjuk - enélkül két gyorsan egymás után indított
  // művelet versenyhelyzetbe kerülhetne a közös `pendingRef`-en (élő hiba,
  // 2026-08-24: törlés helyett hozzáadás futott le a 2FA után).
  const busy = adding || renameMut.isPending || deleteMut.isPending || mfaOpen || codeOpen;

  return (
    <Card title="Passkey-ek"
      subtitle="Biometrikus vagy PIN-alapú belépés az eszközödről. Új passkeyt egy meglévővel hagysz jóvá; az elsőhöz e-mailben küldünk kódot. Minden felvételről és törlésről e-mailt kapsz."
      actions={supported && (
        <Btn icon={Fingerprint} loading={adding} disabled={busy && !adding} onClick={addPasskey}>Passkey hozzáadása</Btn>
      )}>
      {!supported && (
        <Alert type="info">Ez a böngésző nem támogatja a passkey-t.</Alert>
      )}
      <QueryState query={listQ} empty={passkeys.length === 0}
        emptyText="Nincs regisztrált passkey."
        errorText="A passkey-lista betöltése sikertelen.">
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.82rem'}}>
            <thead><tr>
              <th style={thS}>Eszköz</th>
              <th style={thS}>Létrehozva</th>
              <th style={thS}>Utoljára használva</th>
              <th style={thS}></th>
            </tr></thead>
            <tbody>
              {passkeys.map(p => (
                <tr key={p.id}>
                  <td style={tdS}>
                    {renaming?.id === p.id ? (
                      <div style={{display:'flex',gap:6,alignItems:'center'}}>
                        <Input value={renaming.value}
                          onChange={e=>setRenaming(r=>({...r,value:e.target.value}))}/>
                        <Btn size="sm" icon={Check} loading={renameMut.isPending}
                          disabled={busy && !renameMut.isPending}
                          onClick={()=>renameMut.mutate({id:p.id, friendlyName: renaming.value.trim()})}/>
                        <Btn size="sm" variant="ghost" icon={X} disabled={busy}
                          onClick={()=>setRenaming(null)}/>
                      </div>
                    ) : (
                      <span style={{display:'flex',alignItems:'center',gap:8}}>
                        <Fingerprint size={14}/> {p.friendly_name || 'Névtelen passkey'}
                      </span>
                    )}
                  </td>
                  <td style={tdS}>{fmtDate(p.created_at)}</td>
                  <td style={{...tdS,color:'var(--text-muted)'}}>{fmtDate(p.last_used_at)}</td>
                  <td style={tdS}>
                    <div style={{display:'flex',gap:4,justifyContent:'flex-end'}}>
                      <Btn size="sm" variant="ghost" icon={Edit2} disabled={busy}
                        onClick={()=>setRenaming({id:p.id, value:p.friendly_name || ''})}/>
                      <Btn size="sm" variant="ghost" icon={Trash2}
                        loading={deleteMut.isPending && deleteMut.variables === p.id}
                        disabled={busy && !(deleteMut.isPending && deleteMut.variables === p.id)}
                        onClick={()=>setConfirm({
                          title:'Passkey törlése', danger:true, confirmLabel:'Törlés',
                          message:`Töröld a(z) "${p.friendly_name || 'Névtelen passkey'}" passkey-t? Ezzel az eszközzel többé nem lehet belépni.`,
                          action:()=>deleteMut.mutate(p.id),
                        })}/>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </QueryState>

      <ConfirmDialog open={!!confirm} title={confirm?.title} message={confirm?.message}
        danger={confirm?.danger} confirmLabel={confirm?.confirmLabel}
        onCancel={()=>setConfirm(null)}
        onConfirm={()=>{ const a=confirm.action; setConfirm(null); a(); }}/>

      <Modal open={codeOpen} onClose={() => finishCode(null)} width={400}
        title="Első passkey: e-mail-megerősítés">
        <form onSubmit={submitCode}
          style={{display:'flex',flexDirection:'column',gap:'0.8rem'}}>
          <p style={{fontSize:'0.8rem',color:'var(--text-muted)',lineHeight:1.5,margin:0}}>
            Elküldtünk egy 6 jegyű kódot ide: <b style={{color:'var(--text)'}}>{codeInfo?.email}</b>.
            A kód 10 percig érvényes. Utána a böngésző kéri az ujjlenyomatodat vagy a PIN-kódodat.
          </p>
          <Input label="Kód" value={codeValue} autoFocus inputMode="numeric" maxLength={6}
            onChange={e=>setCodeValue(e.target.value.replace(/\D/g, ''))} required/>
          {codeError && <Alert type="danger">{codeError}</Alert>}
          <div style={{display:'flex',gap:8,justifyContent:'space-between',flexWrap:'wrap'}}>
            <Btn type="button" variant="ghost" loading={codeBusy} onClick={resendCode}>Új kód</Btn>
            <div style={{display:'flex',gap:8}}>
              <Btn type="button" variant="ghost" onClick={() => finishCode(null)}>Mégse</Btn>
              <Btn type="submit" loading={codeBusy} disabled={codeValue.trim().length !== 6}>Tovább</Btn>
            </div>
          </div>
        </form>
      </Modal>

      <Modal open={mfaOpen} onClose={cancelMfa} width={380}
        title="Kétfaktoros megerősítés">
        <form onSubmit={submitMfa} style={{display:'flex',flexDirection:'column',gap:'0.8rem'}}>
          <p style={{fontSize:'0.8rem',color:'var(--text-muted)',lineHeight:1.5,margin:0}}>
            Ehhez a passkey-művelethez a fiókod kétfaktoros megerősítése szükséges. Add meg a
            hitelesítő alkalmazásod (pl. admin console TOTP) 6 jegyű kódját.
          </p>
          <Input label="Kód" value={mfaCode} autoFocus inputMode="numeric" maxLength={6}
            onChange={e=>setMfaCode(e.target.value)} required/>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
            <Btn type="button" variant="ghost" onClick={cancelMfa}>Mégse</Btn>
            <Btn type="submit" loading={mfaBusy}>Megerősítés</Btn>
          </div>
        </form>
      </Modal>
    </Card>
  );
}

// ── Tranzakciós log (kimenő utalások – Raiffeisen + Revolut) ─────────────────
const _TX_ST = {
  approve:['Jóváhagyásra vár','#f59e0b'], init:['Elindítva','#3b82f6'],
  prog:['Folyamatban','#0ea5e9'], done:['Teljesült','#10b981'],
  rej:['Elutasítva','#ef4444'], cancel:['Visszavonva','#6b7280'],
};
// Ékezet-hajtó + kisbetűs (Unicode NFD → diakritikák eldobása) – bármely ékezetre.
const _txNorm = s => (s == null ? '' : s.toString())
  .normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const _txDt = iso => (iso || '').slice(0,19).replace('T',' ');

function TxBadge({ st }) {
  const [lbl,c] = _TX_ST[st] || [st,'#6b7280'];
  return <span style={{display:'inline-block',fontSize:'0.66rem',fontWeight:700,
    padding:'2px 8px',borderRadius:6,whiteSpace:'nowrap',
    color:c,background:c+'1f',border:`1px solid ${c}66`}}>{lbl}</span>;
}

function TransactionsPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [banks, setBanks] = useState(new Set());       // üres = mind
  const [statuses, setStatuses] = useState(new Set()); // üres = mind
  const [cur, setCur] = useState('');                  // '' = mind
  const [detail, setDetail] = useState(null);
  const [busyId, setBusyId] = useState(null);          // épp frissülő sor kulcsa
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [dateP, setDateP] = useState('all');           // all | year | prevmonth | custom
  const [dFrom, setDFrom] = useState('');
  const [dTo, setDTo] = useState('');
  const polledRef = useRef(false);                     // auto-poll csak egyszer/mount

  const { data: txs = [], isLoading, isError } = useQuery({
    queryKey: ['payments-transactions'],
    queryFn: paymentsApi.transactions,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['payments-transactions'] });
    qc.invalidateQueries({ queryKey: ['documents'] });
  };
  const rowKey = t => `${t.source}:${t.local_id}`;
  const doRowRefresh = async (t) => {
    setBusyId(rowKey(t));
    try { await paymentsApi.refreshTransaction(t.source, t.local_id); invalidate(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Frissítési hiba'); }
    finally { setBusyId(null); }
  };
  const doRefreshAll = async () => {
    setRefreshingAll(true);
    // ⚠ `force: true` — a kézi gomb jelentése „MOST kérdezd meg", tehát átlépi a
    //   backend 60 s-os fékét. Enélkül a gomb néha látszólag nem tenne semmit.
    try { await paymentsApi.refreshPending(true); } catch { /* csendes */ }
    invalidate(); setRefreshingAll(false);
  };
  // ⚠⚠ 2026-08-25, élő hiba (#249): a fenti "Státuszok frissítése" a
  // SUBMITTING (bank-válasz nélkül maradt) sorokat NEM tudja lezárni - a
  // mögötte lévő refresh csak a már SUBMITTED sorokra működik. Erre a
  // reconcile-út való, aminek eddig nem volt UI-gombja, csak app-induláskor
  // futott le - egyetlen rekedt sor a dupla-utalás kapun át tartósan
  // blokkolta az adott bizonylat MINDEN további utalási kísérletét.
  const doReconcile = async () => {
    setReconciling(true);
    try {
      const r = await paymentsApi.reconcile();
      const resolved = r?.resolved?.length ?? 0;
      const errors = r?.errors?.length ?? 0;
      if (resolved > 0) toast.success(`Egyeztetés kész: ${resolved} tétel rendeződött.`);
      else if (errors > 0) toast.error(`Egyeztetés: ${errors} tételnél a bank most sem válaszolt - próbáld később.`);
      else toast.info('Egyeztetés kész: nincs rekedt tétel.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Az egyeztetés sikertelen.');
    } finally {
      invalidate(); setReconciling(false);
    }
  };
  // AUTO-POLLER: a fül megnyitásakor egyszer lefrissíti a nem-végleges tételeket.
  useEffect(() => {
    if (polledRef.current || isLoading) return;
    if (!txs.some(t => t.can_refresh)) return;
    polledRef.current = true;
    (async () => {
      try { await paymentsApi.refreshPending(); invalidate(); } catch { /* csendes */ }
    })();
  }, [isLoading, txs]);
  // Bizonylat megnyitása: a PDF-előnézet új lapon (Gábor döntése, 2026-07-17).
  // Az aktív cég nevében (window.open nem vinné a cég-fejlécet — components/AuthedFile.js).
  const openDoc = (docId) => openAuthedFile(previewUrl(docId));

  const toggle = (setFn, val) => setFn(prev => {
    const n = new Set(prev); n.has(val) ? n.delete(val) : n.add(val); return n;
  });

  const hay = t => _txNorm([_txDt(t.date), t.bank, t.account_name, t.creditor_name,
    t.creditor_iban, t.debtor_iban, t.amount, t.currency, t.txid, t.status_label,
    t.reference, t.document_id, t.raw_status].filter(x => x != null).join(' '));

  const inDateRange = (iso) => {
    const d = (iso || '').slice(0, 10);
    if (dateP === 'all') return true;
    if (!d) return false;
    const now = new Date();
    if (dateP === 'year') return d.slice(0, 4) === String(now.getFullYear());
    if (dateP === 'prevmonth') {
      const pm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const ym = `${pm.getFullYear()}-${String(pm.getMonth() + 1).padStart(2, '0')}`;
      return d.slice(0, 7) === ym;
    }
    if (dateP === 'custom') return (!dFrom || d >= dFrom) && (!dTo || d <= dTo);
    return true;
  };

  const tokens = _txNorm(q).split(/\s+/).filter(Boolean);
  const rows = txs.filter(t =>
    (banks.size === 0 || banks.has(t.bank)) &&
    (statuses.size === 0 || statuses.has(t.status)) &&
    (!cur || t.currency === cur) &&
    inDateRange(t.date) &&
    tokens.every(tok => hay(t).includes(tok)));
  const waiting = txs.filter(t => t.status === 'approve').length;
  // a nyitott modal mindig a FRISS sorból renderel (frissítés után se maradjon elavult)
  const detailRow = detail ? (txs.find(t => rowKey(t) === rowKey(detail)) || detail) : null;

  const thS = { padding:'0.5rem 0.6rem', color:'var(--text-muted)', fontWeight:600,
                fontSize:'0.66rem', textTransform:'uppercase', letterSpacing:'0.05em',
                borderBottom:'1px solid var(--border)', textAlign:'left', whiteSpace:'nowrap' };
  const tdS = { padding:'0.5rem 0.6rem', fontSize:'0.8rem', verticalAlign:'middle',
                borderBottom:'1px solid var(--border-dim)', whiteSpace:'nowrap',
                overflow:'hidden', textOverflow:'ellipsis', maxWidth:0 };
  const mono = { fontFamily:'var(--mono)' };
  const _txIconBtn = { border:'1px solid var(--border)', background:'var(--bg-input)', borderRadius:6,
    width:28, height:26, cursor:'pointer', color:'var(--text-muted)',
    display:'inline-flex', alignItems:'center', justifyContent:'center' };
  const fmtAmt = t => `−${huFmt(t.amount || 0, (t.currency==='HUF')?0:2)} ${t.currency||''}`;

  return (
    <Card title="Kimenő utalások"
      subtitle={isLoading ? 'Betöltés…'
        : `${rows.length} utalás${waiting ? ` · ${waiting} jóváhagyásra vár` : ''}`}
      actions={<div style={{display:'flex',gap:6}}>
        <Btn size="sm" variant="ghost" icon={RefreshCw} loading={refreshingAll} onClick={doRefreshAll}>
          Státuszok frissítése</Btn>
        <Btn size="sm" variant="ghost" icon={Check} loading={reconciling} onClick={doReconcile}
          title="A rekedt (SUBMITTING) tételeket egyezteti a bankkal - ezt a fenti frissítés nem éri el">
          Egyeztetés</Btn>
      </div>}>

      {/* okos kereső – a hover-adatokra is keres, ékezet-/kisbetű-független */}
      <SearchField value={q} onChange={setQ} onClear={() => setQ('')}
        placeholder="Keresés mindenben – kedvezményezett, IBAN, számla, közlemény, banki azonosító, összeg…"
        style={{marginBottom:10}}/>

      <div style={{display:'flex',flexWrap:'wrap',gap:6,alignItems:'center',marginBottom:12}}>
        {[['all','Mind'],['year','Aktuális év'],['prevmonth','Előző hónap'],['custom','Egyéni']].map(([k,lbl]) =>
          <Pill key={k} active={dateP===k} onClick={() => setDateP(k)}>{lbl}</Pill>)}
        {dateP === 'custom' && <>
          <input type="date" value={dFrom} onChange={e => setDFrom(e.target.value)}
            style={{background:'var(--bg-input)',border:'1px solid var(--border)',borderRadius:8,
              padding:'3px 8px',color:'var(--text)',fontSize:'0.75rem'}} />
          <span style={{color:'var(--text-muted)'}}>–</span>
          <input type="date" value={dTo} onChange={e => setDTo(e.target.value)}
            style={{background:'var(--bg-input)',border:'1px solid var(--border)',borderRadius:8,
              padding:'3px 8px',color:'var(--text)',fontSize:'0.75rem'}} />
        </>}
        <span style={{width:1,height:20,background:'var(--border)',margin:'0 4px'}} />
        {['raiffeisen','revolut'].map(b =>
          <Pill key={b} active={banks.has(b)} onClick={() => toggle(setBanks, b)}
            style={{display:'inline-flex',alignItems:'center',gap:6}}>
            <span style={{width:8,height:8,borderRadius:'50%',background:bankColor(b)}} />
            {b === 'raiffeisen' ? 'Raiffeisen' : 'Revolut'}</Pill>)}
        <span style={{width:1,height:20,background:'var(--border)',margin:'0 4px'}} />
        <Pill active={!cur} onClick={() => setCur('')}>Mind</Pill>
        {['HUF','EUR','USD'].map(c =>
          <Pill key={c} active={cur===c} onClick={() => setCur(c)}>{c}</Pill>)}
        <span style={{width:1,height:20,background:'var(--border)',margin:'0 4px'}} />
        {Object.entries(_TX_ST).map(([k,[lbl]]) =>
          <Pill key={k} active={statuses.has(k)} onClick={() => toggle(setStatuses, k)}>{lbl}</Pill>)}
      </div>

      {isError && <div style={{marginBottom:10}}><Alert type="danger">
        Nem sikerült betölteni a tranzakciókat. Ellenőrizd a kapcsolatot, majd frissítsd az oldalt.</Alert></div>}

      <div style={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',tableLayout:'fixed'}}>
          <colgroup>
            <col style={{width:'150px'}}/><col style={{width:'150px'}}/><col/>
            <col style={{width:'130px'}}/><col style={{width:'128px'}}/><col style={{width:'116px'}}/>
            <col style={{width:'48px'}}/><col style={{width:'44px'}}/>
          </colgroup>
          <thead><tr>
            <th style={thS}>Dátum / idő</th><th style={thS}>Számla</th><th style={thS}>Kedvezményezett</th>
            <th style={{...thS,textAlign:'right'}}>Összeg</th><th style={thS}>Azonosító</th>
            <th style={thS}>Státusz</th><th style={{...thS,textAlign:'center'}}>Biz.</th><th style={thS}></th>
          </tr></thead>
          <tbody>
            {isLoading && <tr><td colSpan={8} style={{padding:'2rem',textAlign:'center'}}><Spinner/></td></tr>}
            {!isLoading && rows.length === 0 &&
              <tr><td colSpan={8} style={{padding:'2rem',textAlign:'center',color:'var(--text-muted)'}}>
                {txs.length ? 'Nincs a szűrésnek megfelelő utalás.' : 'Még nincs kimenő utalás.'}</td></tr>}
            {rows.map(t => (
              <tr key={rowKey(t)} onClick={() => setDetail(t)}
                style={{cursor:'pointer', background: t.status==='approve' ? 'rgba(245,158,11,0.05)' : 'transparent'}}>
                <td style={{...tdS,...mono}} title={`${t.document_id ? `Bizonylat rekord #${t.document_id}` : 'Nincs kapcsolt bizonylat'} · Tranzakció-rekord #${t.local_id} (${t.source}) · ${_txDt(t.date)}`}>{_txDt(t.date)}</td>
                <td style={tdS}>
                  <span style={{display:'inline-flex',alignItems:'center',gap:7,maxWidth:'100%'}}>
                    <span style={{width:8,height:8,borderRadius:'50%',background:bankColor(t.bank),flexShrink:0}}/>
                    <span style={{overflow:'hidden',textOverflow:'ellipsis'}} title={`${t.bank} · ${t.account_name||''}`}>{t.account_name || '–'}</span>
                    <span style={{fontFamily:'var(--mono)',fontSize:'0.55rem',fontWeight:700,padding:'1px 5px',
                      borderRadius:4,flexShrink:0,
                      color: t.environment==='production' ? '#10b981' : '#f59e0b',
                      background: (t.environment==='production' ? '#10b981' : '#f59e0b')+'22'}}>
                      {t.environment==='production' ? 'ÉLES' : 'SANDBOX'}</span>
                  </span>
                </td>
                <td style={tdS} title={`${t.creditor_name||''} · ${t.creditor_iban||''}`}>{t.creditor_name || '–'}</td>
                <td style={{...tdS,...mono,textAlign:'right',fontWeight:600}}>{fmtAmt(t)}</td>
                <td style={{...tdS,...mono,color:'var(--text-muted)',fontSize:'0.75rem'}} title={t.txid||''}>{t.txid || '–'}</td>
                <td style={tdS}><TxBadge st={t.status}/></td>
                <td style={{...tdS,textAlign:'center',overflow:'visible'}}>
                  {t.document_id
                    ? <button title={`Bizonylat rekord #${t.document_id} – megnyitás`} onClick={e => { e.stopPropagation(); openDoc(t.document_id); }}
                        style={_txIconBtn}><FileText size={14}/></button>
                    : <span style={{color:'var(--text-dim)'}}>–</span>}
                </td>
                <td style={{...tdS,textAlign:'center',overflow:'visible'}}>
                  {t.can_refresh &&
                    <button title="Státusz frissítése" disabled={busyId===rowKey(t)}
                      onClick={e => { e.stopPropagation(); doRowRefresh(t); }}
                      style={_txIconBtn}>
                      {busyId===rowKey(t) ? <Spinner size={12}/> : <RefreshCw size={14}/>}</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detailRow && <TxDetailModal t={detailRow} onClose={() => setDetail(null)}
        onRefresh={() => doRowRefresh(detailRow)} busy={busyId===rowKey(detailRow)} />}
    </Card>
  );
}

function TxDetailModal({ t, onClose, onRefresh, busy }) {
  const bankLbl = t.bank === 'raiffeisen' ? 'Raiffeisen' : 'Revolut';
  const row = (k,v) => (
    <div style={{display:'flex',flexDirection:'column',gap:2}}>
      <span style={{fontSize:'0.62rem',fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.05em'}}>{k}</span>
      <span style={{fontSize:'0.82rem',wordBreak:'break-word'}}>{v || <span style={{color:'var(--text-dim)'}}>–</span>}</span>
    </div>
  );
  return (
    <Modal open={true} onClose={onClose} title={`${bankLbl} utalás`} width={560}>
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12,flexWrap:'wrap'}}>
        <span style={{fontFamily:'var(--mono)',fontSize:'1.15rem',fontWeight:700}}>
          −{huFmt(t.amount||0,(t.currency==='HUF')?0:2)} {t.currency}</span>
        <TxBadge st={t.status}/>
        <span style={{fontFamily:'var(--mono)',fontSize:'0.72rem',color:'var(--text-dim)'}}>{_txDt(t.date)}</span>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'13px 20px'}}>
        {row('Kedvezményezett', t.creditor_name)}
        {row('Kedvezm. IBAN', t.creditor_iban)}
        {row('Forrás számla', `${bankLbl} · ${t.account_name || (t.debtor_iban||'')}`)}
        {row('Státusz', <><TxBadge st={t.status}/> <span style={{fontFamily:'var(--mono)',fontSize:'0.7rem',color:'var(--text-dim)',marginLeft:6}}>{t.raw_status}</span></>)}
        {t.status_reason && <div style={{gridColumn:'1/3'}}>{row('Bank indoklása', <span style={{color:'var(--danger)'}}>{t.status_reason}</span>)}</div>}
        <div style={{gridColumn:'1/3'}}>{row('Közlemény', t.reference)}</div>
        <div style={{gridColumn:'1/3',background:'var(--bg-input)',border:'1px solid var(--border-dim)',
          borderRadius:'var(--radius)',padding:'10px 12px',display:'grid',gap:6}}>
          {Object.entries(t.ids || {}).map(([k,v]) => v == null || v === '' ? null : (
            <div key={k} style={{display:'flex',justifyContent:'space-between',gap:12,fontSize:'0.72rem'}}>
              <span style={{color:'var(--text-muted)'}}>{k}</span>
              <span style={{fontFamily:'var(--mono)',textAlign:'right',wordBreak:'break-all'}}>
                {Array.isArray(v) ? v.join(', ') : String(v)}</span>
            </div>
          ))}
        </div>
        {row('Környezet', t.environment === 'production' ? 'ÉLES' : 'SANDBOX')}
        {row('Kapcsolt bizonylat', t.document_id
          ? <span style={{color:'var(--primary)',cursor:'pointer',textDecoration:'underline'}}
              onClick={() => openAuthedFile(previewUrl(t.document_id))}>Bizonylat #{t.document_id} – megnyitás</span>
          : null)}
      </div>
      <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:18}}>
        {t.can_refresh && <Btn variant="secondary" icon={RefreshCw} loading={busy} onClick={onRefresh}>Státusz frissítése</Btn>}
        <Btn variant="ghost" onClick={onClose}>Bezárás</Btn>
      </div>
    </Modal>
  );
}

function BankAccountsPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const { isAdmin } = useAuth();
  const [confirm, setConfirm] = useState(null); // {title, message, danger, confirmLabel, action}
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  // M8: tudatos onboarding — „Bankszámla hozzáadása" (kivonat-feltöltéses). Eddig a
  // számla IMPLICIT, az első felismert kivonaton jött létre.
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ iban: '', currency: 'HUF', bank_name: '' });

  const accountsQ = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: statementsApi.accounts,
  });
  const accounts = accountsQ.data ?? [];

  const createMut = useMutation({
    mutationFn: (data) => statementsApi.createAccount(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
      qc.invalidateQueries({ queryKey: ['company-assets', 'bank_account'] });
      setAddOpen(false); setAddForm({ iban: '', currency: 'HUF', bank_name: '' });
      toast.success('Bankszámla felvéve — aktív, kivonat-feltöltéssel (nincs élő teszt).');
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'A felvétel nem sikerült.'),
  });

  const saveMut = useMutation({
    mutationFn: ({id, data}) => statementsApi.updateAccount(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['bank-accounts'] }); setEditingId(null); setForm({}); toast.success('Mentve'); },
    onError: (err) => toast.error('Mentés sikertelen: ' + (err?.response?.data?.detail || err.message)),
  });
  const delMut = useMutation({
    mutationFn: (id) => statementsApi.deleteAccount(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bank-accounts'] }),
    onError: (err) => toast.error(err?.response?.data?.detail || 'Törlési hiba'),
  });

  const startEdit = (a) => {
    setEditingId(a.id);
    setForm({
      bank_name: a.bank_name || '',
      holder:    a.holder || '',
      currency:  a.currency || 'HUF',
      is_active: !!a.is_active,
      is_default: !!a.is_default,
      notes:     a.notes || '',
    });
  };

  const thS = { padding:'0.55rem 0.75rem', color:'var(--text-muted)', fontWeight:600,
                fontSize:'0.68rem', textTransform:'uppercase', letterSpacing:'0.06em',
                borderBottom:'1px solid var(--border)' };
  const tdS = { padding:'0.55rem 0.75rem', fontSize:'0.82rem', verticalAlign:'middle',
                borderBottom:'1px solid var(--border-dim)' };

  return (
    <Card title="Bankszámlák" subtitle="Kézzel felvett és kivonatból felismert számlák. Szerkeszthető a banknév, tulajdonos, deviza, megjegyzés."
      actions={<Btn size="sm" icon={Plus} disabled={!isAdmin}
        title={isAdmin ? undefined : 'Csak cég-adminisztrátor módosíthatja.'}
        onClick={()=>setAddOpen(true)}>Bankszámla hozzáadása</Btn>}>
      <Modal open={addOpen} onClose={()=>setAddOpen(false)} title="Bankszámla hozzáadása" width={480}>
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          <Input label="IBAN vagy magyar számlaszám" value={addForm.iban} autoComplete="off" spellCheck={false}
            placeholder="HU42 1177 3016 … vagy 11773016-11111018"
            onChange={e=>setAddForm(f=>({...f,iban:e.target.value}))}/>
          <div style={{display:'flex',gap:10}}>
            <Input label="Deviza" value={addForm.currency} style={{width:90}}
              onChange={e=>setAddForm(f=>({...f,currency:e.target.value.toUpperCase()}))}/>
            <Input label="Bank neve (opcionális)" value={addForm.bank_name} style={{flex:1}}
              placeholder="pl. MBH Bank"
              onChange={e=>setAddForm(f=>({...f,bank_name:e.target.value}))}/>
          </div>
          <Alert type="info" style={{marginBottom:0}}>
            A számla <b>azonnal aktív</b> lesz, kivonat-feltöltéssel — API-kapcsolat és élő teszt nélkül.
            A feltöltött kivonatok ehhez a számlához kerülnek.
          </Alert>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
            <Btn variant="ghost" onClick={()=>setAddOpen(false)} disabled={createMut.isPending}>Mégse</Btn>
            <Btn icon={Plus} loading={createMut.isPending} disabled={!addForm.iban.trim()}
              onClick={()=>createMut.mutate({ iban: addForm.iban.trim(), currency: addForm.currency.trim() || 'HUF',
                                             bank_name: addForm.bank_name.trim() || null })}>Felvétel</Btn>
          </div>
        </div>
      </Modal>
      <QueryState query={accountsQ} empty={accounts.length === 0}
        emptyText={'Nincs egyetlen bankszámla sem. Vedd fel a „Bankszámla hozzáadása” gombbal, vagy az első kivonat feldolgozása után automatikusan ide kerülnek a felismert számlák.'}
        errorText="A bankszámlák betöltése sikertelen.">
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.82rem'}}>
            <thead>
              <tr>
                <th style={thS}>Bank név</th>
                <th style={thS}>IBAN</th>
                <th style={thS}>Deviza</th>
                <th style={thS}>Tulajdonos</th>
                <th style={thS}>Megjegyzés</th>
                <th style={thS}>Aktív</th>
                <th style={thS}></th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a, idx) => {
                // C5 (2026-09-06): egy fizikai számla (IBAN) több deviza-sora EGY csoport —
                // a Revolut 5 sora egy kapcsolat, nem 5 számla. A lista IBAN szerint rendezett,
                // ezért az első előfordulásnál egy vékony csoport-fejléc kerül a sorok fölé.
                const ibanRows = accounts.filter(x => x.iban === a.iban);
                const firstOfGroup = idx === accounts.findIndex(x => x.iban === a.iban);
                const groupHeader = ibanRows.length > 1 && firstOfGroup ? (
                  <tr key={`g-${a.iban}`}>
                    <td colSpan={7} style={{padding:'0.45rem 0.75rem 0.15rem',fontSize:'0.68rem',
                      color:'var(--text-muted)',borderBottom:'none'}}>
                      <span style={{fontWeight:700,textTransform:'uppercase',letterSpacing:'0.06em'}}>
                        {a.asset_label || a.bank_name || 'Számla'}
                      </span>
                      <span style={{marginLeft:8,fontFamily:'var(--mono)'}}>{a.iban}</span>
                      <span style={{marginLeft:8}}>· egy fizikai számla, {ibanRows.length} deviza</span>
                    </td>
                  </tr>
                ) : null;
                return [groupHeader, editingId === a.id ? (
                <tr key={a.id} style={{background:'rgba(59,130,246,0.06)'}}>
                  <td style={tdS}>
                    <Input value={form.bank_name} onChange={e=>setForm(f=>({...f,bank_name:e.target.value}))}
                      placeholder="pl. Bank Burgenland"/>
                  </td>
                  <td style={{...tdS,fontFamily:'var(--mono)',fontSize:'0.7rem',color:'var(--text-muted)'}}>
                    {a.iban}
                  </td>
                  <td style={tdS}>
                    <Input value={form.currency} onChange={e=>setForm(f=>({...f,currency:e.target.value.toUpperCase()}))}
                      style={{width:60}}/>
                  </td>
                  <td style={tdS}>
                    <Input value={form.holder} onChange={e=>setForm(f=>({...f,holder:e.target.value}))}/>
                  </td>
                  <td style={tdS}>
                    <Input value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/>
                  </td>
                  <td style={tdS}>
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <Checkbox label="Aktív" checked={form.is_active}
                        onChange={e=>setForm(f=>({...f,is_active:e.target.checked}))}/>
                      <Checkbox label="Alapért." checked={form.is_default}
                        onChange={e=>setForm(f=>({...f,is_default:e.target.checked}))}/>
                    </div>
                  </td>
                  <td style={tdS}>
                    <div style={{display:'flex',gap:4}}>
                      <Btn size="sm" loading={saveMut.isPending}
                        onClick={()=>saveMut.mutate({id:a.id,data:form})}>Mentés</Btn>
                      <Btn size="sm" variant="ghost" onClick={()=>{setEditingId(null);setForm({});}}>Mégse</Btn>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={a.id} style={{opacity: a.is_active ? 1 : 0.55}}>
                  <td style={tdS}>
                    <div style={{display:'flex',alignItems:'center',gap:6}}>
                      {a.is_default && (
                        <span title="Alapértelmezett számla"
                          style={{color:'#f59e0b',fontSize:'0.85rem',lineHeight:1}}>★</span>
                      )}
                      <span style={{fontWeight:500}}>{a.bank_name || <span style={{color:'var(--text-dim)'}}>– nincs név –</span>}</span>
                    </div>
                    {/* M8: a kivonat-feltöltéses, tudatosan felvett számla második,
                        halványabb segédszöveget kap — nem ígér élő kapcsolatot. */}
                    {a.company_asset_id && a.connection_kind === 'statement_upload' && (
                      <div style={{fontSize:'0.68rem',color:'var(--text-dim)',marginTop:2}}>
                        aktív · nincs élő teszt (kivonat-feltöltéssel aktiválva)
                      </div>
                    )}
                    {a.connection_kind === 'api' && (
                      <div style={{fontSize:'0.68rem',color:'var(--text-dim)',marginTop:2}}>API-kapcsolat</div>
                    )}
                  </td>
                  <td style={{...tdS,fontFamily:'var(--mono)',fontSize:'0.72rem'}}>{a.iban}</td>
                  <td style={tdS}>{a.currency || '–'}</td>
                  <td style={tdS}>{a.holder || <span style={{color:'var(--text-dim)'}}>–</span>}</td>
                  <td style={{...tdS,fontSize:'0.74rem',color:'var(--text-muted)',
                    maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {a.notes || ''}
                  </td>
                  <td style={tdS}>
                    {a.is_active
                      ? <Check size={13} color="var(--accent)"/>
                      : <X size={13} color="var(--danger)"/>}
                  </td>
                  <td style={tdS}>
                    <div style={{display:'flex',gap:4}}>
                      <Btn size="sm" variant="ghost" onClick={()=>startEdit(a)}>Szerkeszt</Btn>
                      <Btn size="sm" variant="ghost" icon={Trash2}
                        onClick={()=>setConfirm({
                          title:'Bankszámla törlése', danger:true, confirmLabel:'Törlés',
                          message:`Törlöd a ${a.iban} számlát?`,
                          action:()=>delMut.mutate(a.id),
                        })}/>
                    </div>
                  </td>
                </tr>
              )]; })}
            </tbody>
          </table>
        </div>
      </QueryState>

      <ConfirmDialog open={!!confirm} title={confirm?.title} message={confirm?.message}
        danger={confirm?.danger} confirmLabel={confirm?.confirmLabel}
        onCancel={()=>setConfirm(null)}
        onConfirm={()=>{ const a=confirm.action; setConfirm(null); a(); }}/>
    </Card>
  );
}

function CategoryListPanel() {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(null); // {title, message, danger, confirmLabel, action}
  const [newCat, setNewCat] = useState('');
  const [saved, setSaved] = useState(false);

  const catsQ = useQuery({
    queryKey: ['categories'], queryFn: categoriesApi.list,
  });
  const cats = catsQ.data ?? [];

  const saveMut = useMutation({
    mutationFn: categoriesApi.save,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['categories'] }); setSaved(true); setTimeout(()=>setSaved(false),2000); },
  });

  const addCat = () => {
    const v = newCat.trim();
    if (!v || cats.includes(v)) return;
    saveMut.mutate([...cats, v]);
    setNewCat('');
  };
  const removeCat = (cat) => setConfirm({
    title:'Kategória törlése', danger:true, confirmLabel:'Törlés',
    message:`Törlöd a(z) "${cat}" kategóriát?`,
    action:()=>saveMut.mutate(cats.filter(c => c !== cat)),
  });

  return (
    <Card title="Számlakategóriák" subtitle="Ezek jelennek meg a bizonylat kategória mezőjében és az AI is ebből választ">
      {saved && <Alert type="success">Mentve!</Alert>}
      <QueryState query={catsQ} empty={cats.length === 0}
        emptyText="Nincs kategória. Vegyél fel egyet alul."
        errorText="A kategóriák betöltése sikertelen.">
        <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:14}}>
          {cats.map((c,i) => (
            <div key={i} style={{display:'inline-flex',alignItems:'center',gap:4,
              background:'#1e293b',border:'1px solid var(--border)',borderRadius:99,
              padding:'0.2rem 0.5rem 0.2rem 0.75rem',fontSize:'0.78rem'}}>
              {c}
              <button onClick={()=>removeCat(c)} style={{background:'none',border:'none',
                cursor:'pointer',color:'var(--text-muted)',lineHeight:1,padding:'0 2px',
                fontSize:'0.9rem'}} title="Törlés">✕</button>
            </div>
          ))}
        </div>
      </QueryState>
      <div style={{display:'flex',gap:8,alignItems:'flex-end'}}>
        <Input label="Új kategória neve" placeholder="pl. Szoftver előfizetés"
          value={newCat} onChange={e=>setNewCat(e.target.value)}
          onKeyDown={e=>e.key==='Enter'&&addCat()}
          style={{flex:1}}/>
        <Btn icon={Plus} variant="secondary" size="sm"
          loading={saveMut.isPending} onClick={addCat}>Hozzáad</Btn>
      </div>

      <ConfirmDialog open={!!confirm} title={confirm?.title} message={confirm?.message}
        danger={confirm?.danger} confirmLabel={confirm?.confirmLabel}
        onCancel={()=>setConfirm(null)}
        onConfirm={()=>{ const a=confirm.action; setConfirm(null); a(); }}/>
    </Card>
  );
}

function CategoryRulesPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirm, setConfirm] = useState(null); // {title, message, danger, confirmLabel, action}
  const [newRule, setNewRule] = useState({ pattern:'', category:'', match_type:'contains', priority:100 });

  const { data: cats=[] } = useQuery({ queryKey:['categories'], queryFn:categoriesApi.list });
  const rulesQ = useQuery({ queryKey:['category-rules'], queryFn:categoriesApi.listRules });
  const rules = rulesQ.data ?? [];

  const addMut = useMutation({
    mutationFn: categoriesApi.createRule,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['category-rules'] }); setNewRule({pattern:'',category:'',match_type:'contains',priority:100}); toast.success('Szabály hozzáadva'); },
    onError: (err) => toast.error('Mentés sikertelen: ' + (err?.response?.data?.detail || err.message)),
  });
  const delMut = useMutation({
    mutationFn: categoriesApi.deleteRule,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['category-rules'] }),
    onError: (err) => toast.error('Törlés sikertelen: ' + (err?.response?.data?.detail || err.message)),
  });
  const toggleMut = useMutation({
    mutationFn: ({id,data}) => categoriesApi.updateRule(id,data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['category-rules'] }); toast.success('Mentve'); },
    onError: (err) => toast.error('Mentés sikertelen: ' + (err?.response?.data?.detail || err.message)),
  });

  const thS = { padding:'0.45rem 0.75rem', fontSize:'0.65rem', fontWeight:600,
    color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em',
    borderBottom:'1px solid var(--border)', textAlign:'left', whiteSpace:'nowrap' };
  const tdS = { padding:'0.5rem 0.75rem', fontSize:'0.8rem', verticalAlign:'middle',
    borderBottom:'1px solid var(--border-dim)' };

  const MATCH_LABELS = { contains:'tartalmaz', startswith:'kezdődik', exact:'pontos' };

  return (
    <Card title="Kategorizálási szabályok"
      subtitle="Kiállító neve alapján automatikus kategória – az AI-t megelőzi, kisebb szám = nagyobb prioritás">
      <div style={{overflowX:'auto',marginBottom:16}}>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead>
            <tr>
              <th style={thS}>Minta (kiállító)</th>
              <th style={thS}>Típus</th>
              <th style={thS}>Kategória</th>
              <th style={{...thS,textAlign:'center'}}>Prioritás</th>
              <th style={{...thS,textAlign:'center'}}>Aktív</th>
              <th style={thS}></th>
            </tr>
          </thead>
          <tbody>
            <QueryState query={rulesQ} colSpan={6} empty={rules.length === 0}
              emptyText="Még nincs szabály. Az AI a kategória lista alapján dolgozik."
              errorText="A kategória-szabályok betöltése sikertelen.">
              {rules.map(r => (
              <tr key={r.id} style={{transition:'background 0.1s'}}
                onMouseEnter={e=>e.currentTarget.style.background='var(--bg-hover)'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <td style={{...tdS,fontFamily:'var(--mono)'}}>{r.pattern}</td>
                <td style={{...tdS,color:'var(--text-muted)',fontSize:'0.75rem'}}>
                  {MATCH_LABELS[r.match_type]||r.match_type}
                </td>
                <td style={tdS}><Badge>{r.category}</Badge></td>
                <td style={{...tdS,textAlign:'center',fontFamily:'var(--mono)'}}>{r.priority}</td>
                <td style={{...tdS,textAlign:'center'}}>
                  <input type="checkbox" checked={r.is_active}
                    onChange={e=>toggleMut.mutate({id:r.id,data:{...r,is_active:e.target.checked}})}
                    style={{accentColor:'var(--primary)',width:14,height:14,cursor:'pointer'}}/>
                </td>
                <td style={tdS}>
                  <Btn variant="ghost" size="sm" icon={Trash2}
                    onClick={()=>setConfirm({
                      title:'Szabály törlése', danger:true, confirmLabel:'Törlés',
                      message:`Törlöd a(z) "${r.pattern}" → ${r.category} szabályt?`,
                      action:()=>delMut.mutate(r.id),
                    })}/>
                </td>
              </tr>
            ))}
            </QueryState>
          </tbody>
        </table>
      </div>

      {/* Új szabály sor */}
      <div style={{background:'rgba(255,255,255,0.02)',border:'1px solid var(--border-dim)',
        borderRadius:'var(--radius)',padding:'0.75rem',marginTop:8}}>
        <p style={{fontSize:'0.7rem',color:'var(--text-muted)',marginBottom:8,textTransform:'uppercase',
          letterSpacing:'0.06em',fontWeight:600}}>Új szabály</p>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 80px auto',gap:8,alignItems:'flex-end'}}>
          <Input label="Kiállító minta" placeholder="pl. microsoft, szakértő"
            value={newRule.pattern} onChange={e=>setNewRule(r=>({...r,pattern:e.target.value}))}
            onKeyDown={e=>e.key==='Enter'&&addMut.mutate(newRule)}/>
          <Select label="Egyezés típusa" value={newRule.match_type}
            onChange={e=>setNewRule(r=>({...r,match_type:e.target.value}))}
            options={[
              {value:'contains',label:'Tartalmaz'},
              {value:'startswith',label:'Kezdődik'},
              {value:'exact',label:'Pontos egyezés'},
            ]}/>
          <Select label="Kategória" value={newRule.category}
            onChange={e=>setNewRule(r=>({...r,category:e.target.value}))}
            options={[{value:'',label:'– válassz –'},...cats.map(c=>({value:c,label:c}))]}/>
          <Input label="Prio" type="number" value={newRule.priority}
            onChange={e=>setNewRule(r=>({...r,priority:parseInt(e.target.value)||100}))}/>
          <Btn icon={Plus} variant="secondary" size="sm"
            loading={addMut.isPending}
            onClick={()=>{
              if (!newRule.pattern||!newRule.category) return;
              addMut.mutate(newRule);
            }}>Hozzáad</Btn>
        </div>
      </div>

      <ConfirmDialog open={!!confirm} title={confirm?.title} message={confirm?.message}
        danger={confirm?.danger} confirmLabel={confirm?.confirmLabel}
        onCancel={()=>setConfirm(null)}
        onConfirm={()=>{ const a=confirm.action; setConfirm(null); a(); }}/>
    </Card>
  );
}

// ── Projects Settings Panel ────────────────────────────────────────────────
function ProjectsPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirm, setConfirm] = useState(null); // {title, message, danger, confirmLabel, action}
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({name:'', client_name:'', description:''});
  const [err, setErr] = useState('');

  const projectsQ = useQuery({
    queryKey: ['projects'], queryFn: projectsApi.list,
  });
  const projects = projectsQ.data ?? [];
  const saveMut = useMutation({
    mutationFn: d => editing?.id ? projectsApi.update(editing.id, d) : projectsApi.create(d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['projects'] }); setEditing(null); setErr(''); toast.success('Mentve'); },
    onError: e => setErr(e?.response?.data?.detail || 'Hiba'),
  });
  const delMut = useMutation({
    mutationFn: projectsApi.delete,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
    onError: e => toast.error(e?.response?.data?.detail || 'Nem törölhető'),
  });

  const startNew = () => { setEditing({}); setForm({name:'',client_name:'',description:''}); setErr(''); };
  const startEdit = p => { setEditing(p); setForm({name:p.name,client_name:p.client_name||'',description:p.description||''}); setErr(''); };

  const thS = { padding:'0.45rem 0.75rem', fontSize:'0.68rem', fontWeight:600,
    color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em',
    borderBottom:'1px solid var(--border)', textAlign:'left' };
  const tdS = { padding:'0.5rem 0.75rem', fontSize:'0.82rem',
    borderBottom:'1px solid var(--border-dim)', verticalAlign:'middle' };

  return (
    <Card title="Projektek"
      subtitle="A projektek eredménye: Információk › Statisztikák › Projektek"
      actions={<Btn icon={Plus} size="sm" onClick={startNew}>Új projekt</Btn>}>
      {editing !== null && (
        <div style={{background:'rgba(255,255,255,0.03)',border:'1px solid var(--border-dim)',
          borderRadius:'var(--radius)',padding:'0.85rem',marginBottom:14}}>
          <p style={{fontSize:'0.7rem',color:'var(--text-muted)',textTransform:'uppercase',
            letterSpacing:'0.06em',fontWeight:600,marginBottom:8}}>
            {editing?.id ? 'Szerkesztés' : 'Új projekt'}
          </p>
          {err && <Alert type="danger" style={{marginBottom:8}}>{err}</Alert>}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 2fr auto',gap:8,alignItems:'flex-end'}}>
            <Input label="Projekt neve *" value={form.name}
              onChange={e=>setForm(p=>({...p,name:e.target.value}))}
              placeholder="pl. DFP"/>
            <Input label="Ügyfél" value={form.client_name}
              onChange={e=>setForm(p=>({...p,client_name:e.target.value}))}
              placeholder="pl. Visa"/>
            <Input label="Leírás" value={form.description}
              onChange={e=>setForm(p=>({...p,description:e.target.value}))}
              placeholder="Opcionális"/>
            <div style={{display:'flex',gap:6}}>
              <Btn variant="ghost" size="sm" onClick={()=>setEditing(null)}>Mégsem</Btn>
              <Btn size="sm" loading={saveMut.isPending}
                onClick={()=>{ if (!form.name.trim()) { setErr('A projekt neve kötelező'); return; } saveMut.mutate(form); }}>
                Mentés
              </Btn>
            </div>
          </div>
        </div>
      )}

      <div style={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead>
            <tr>
              <th style={thS}>Projekt neve</th>
              <th style={thS}>Ügyfél</th>
              <th style={thS}>Leírás</th>
              <th style={thS}></th>
            </tr>
          </thead>
          <tbody>
            <QueryState query={projectsQ} colSpan={4} empty={projects.length === 0}
              emptyText={'Még nincs projekt. Kattints az „Új projekt" gombra.'}
              errorText="A projektek betöltése sikertelen.">
              {projects.map(p => (
              <tr key={p.id}
                onMouseEnter={e=>e.currentTarget.style.background='var(--bg-hover)'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <td style={{...tdS,fontWeight:600}}>{p.name}</td>
                <td style={{...tdS,color:'var(--text-muted)'}}>{p.client_name||'–'}</td>
                <td style={{...tdS,color:'var(--text-muted)',fontSize:'0.78rem'}}>{p.description||'–'}</td>
                <td style={{...tdS,whiteSpace:'nowrap'}}>
                  <div style={{display:'flex',gap:4}}>
                    <Btn variant="ghost" size="sm" icon={Edit2} onClick={()=>startEdit(p)}/>
                    <Btn variant="ghost" size="sm" icon={Trash2}
                      onClick={()=>setConfirm({
                        title:'Projekt törlése', danger:true, confirmLabel:'Törlés',
                        message:`Törlöd a(z) "${p.name}" projektet?`,
                        action:()=>delMut.mutate(p.id),
                      })}/>
                  </div>
                </td>
              </tr>
            ))}
            </QueryState>
          </tbody>
        </table>
      </div>

      <ConfirmDialog open={!!confirm} title={confirm?.title} message={confirm?.message}
        danger={confirm?.danger} confirmLabel={confirm?.confirmLabel}
        onCancel={()=>setConfirm(null)}
        onConfirm={()=>{ const a=confirm.action; setConfirm(null); a(); }}/>
    </Card>
  );
}

// ── Domains ────────────────────────────────────────────────────────────────
export function Domains() {
  return (
    <div>
      <h1 style={{fontSize:'1.2rem',fontWeight:700,marginBottom:'1.1rem'}}>Domének</h1>
      <Card>
        <p style={{color:'var(--text-muted)',fontSize:'0.85rem'}}>Domain lista funkció – hamarosan.</p>
      </Card>
    </div>
  );
}
